import { useMemo, useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  BarChart3,
  Building2,
  Calendar,
  Download,
  LineChart,
  MoreHorizontal,
  PieChart,
  Plus,
  TrendingUp,
  Users,
  RefreshCw,
  SlidersHorizontal,
  X,
  Trash2,
  Activity,
  Clock,
} from "lucide-react";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Line, Bar, Doughnut } from "react-chartjs-2";
import "../styles/admin-base.css";
import "../styles/dashboard.css";
import "../styles/reports.css";
import api from "../api/admin";
import { getActionDescription } from "../components/ai/AuditRow";
import { ACCENT, BLUE_SHADES_HOVER, pickBlueShades, PIE_COLORS, PIE_HOVER } from "../../src/utils/chartPalette";
import { DEFAULT_REPORT_BUILDER_OPTIONS, MEMBERSHIP_ALLOWED_STATUS } from "../../../shared/reportConfig.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
  Filler,
);

/* ── shared global configurations matching DashboardCharts ── */
const badgeClassByTone = {
  "is-green": "admin-chip is-green",
  "is-blue": "admin-chip is-blue",
  "is-purple": "admin-chip is-info",
  "is-orange": "admin-chip is-orange",
  "is-red": "admin-chip is-red",
};

const ARCHIVE_AGING_COLORS = pickBlueShades(4);

const baseFont = {
  family: "'Inter','Segoe UI','Roboto',sans-serif",
  size: 11,
  weight: 500,
};

const isDarkAdminTheme = () => {
  if (typeof document === "undefined") return false;
  return document.documentElement?.dataset?.adminTheme === "dark";
};

const getLegendTextColor = () => {
  return isDarkAdminTheme() ? "#cbd5e1" : "#334155";
};

const getAxisTextColor = () => (isDarkAdminTheme() ? "#94a3b8" : "#64748b");
const getGridLineColor = () => (isDarkAdminTheme() ? "rgba(148,163,184,0.18)" : "rgba(0,0,0,0.06)");

const lineOptions = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: "index", intersect: false },
  plugins: {
    legend: {
      display: true,
      position: "bottom",
      labels: { ...baseFont, color: () => getLegendTextColor(), boxWidth: 10, padding: 12 },
    },
    tooltip: { backgroundColor: "#0f172a", titleFont: { ...baseFont, weight: 700 }, bodyFont: baseFont, padding: 10, cornerRadius: 8 },
  },
  scales: {
    x: { grid: { display: false }, ticks: { font: baseFont, color: () => getAxisTextColor() } },
    y: { beginAtZero: true, grid: { color: () => getGridLineColor() }, ticks: { font: baseFont, color: () => getAxisTextColor(), precision: 0 } },
  },
};

const lineOptionsHiddenLegend = {
  ...lineOptions,
  plugins: {
    ...lineOptions.plugins,
    legend: { display: false }
  }
};

const buildAdaptiveLineOptions = (baseOptions, groupBy = "day") => ({
  ...baseOptions,
  scales: {
    ...baseOptions.scales,
    x: {
      ...baseOptions.scales?.x,
      ticks: {
        ...baseOptions.scales?.x?.ticks,
        autoSkip: true,
        maxTicksLimit: groupBy === "month" ? 12 : 10,
      },
    },
  },
});

const barOptions = {
  responsive: true,
  maintainAspectRatio: false,
  indexAxis: "x",
  plugins: {
    legend: { display: false },
    tooltip: { backgroundColor: "#0f172a", titleFont: { ...baseFont, weight: 700 }, bodyFont: baseFont, padding: 10, cornerRadius: 8 },
  },
  scales: {
    x: { grid: { display: false }, ticks: { font: { ...baseFont, size: 10 }, color: () => getAxisTextColor() } },
    y: { beginAtZero: true, grid: { color: () => getGridLineColor() }, ticks: { font: baseFont, color: () => getAxisTextColor(), precision: 0 } },
  },
};

const horizontalBarOptions = {
  responsive: true,
  maintainAspectRatio: false,
  indexAxis: "y",
  plugins: {
    legend: { display: false },
    tooltip: { backgroundColor: "#0f172a", titleFont: { ...baseFont, weight: 700 }, bodyFont: baseFont, padding: 10, cornerRadius: 8 },
  },
  scales: {
    x: { beginAtZero: true, grid: { color: () => getGridLineColor() }, ticks: { font: baseFont, color: () => getAxisTextColor(), precision: 0 } },
    y: { grid: { display: false }, ticks: { font: { ...baseFont, size: 10 }, color: () => getAxisTextColor() } },
  },
};

const pieOptions = {
  responsive: true,
  maintainAspectRatio: false,
  cutout: "62%",
  radius: "92%",
  plugins: {
    legend: {
      position: "right",
      labels: {
        ...baseFont,
        color: () => getLegendTextColor(),
        boxWidth: 10,
        padding: 8,
        font: { ...baseFont, size: 10 },
        generateLabels: (chart) => {
          const data = chart.data;
          if (!data.labels?.length) return [];
          return data.labels.map((label, i) => ({
            text: label,
            fillStyle: data.datasets[0].backgroundColor[i],
            strokeStyle: data.datasets[0].backgroundColor[i],
            fontColor: getLegendTextColor(),
            hidden: false,
            index: i,
          }));
        },
      },
    },
    tooltip: {
      backgroundColor: "#0f172a",
      titleFont: { ...baseFont, weight: 700 },
      bodyFont: baseFont,
      padding: 10,
      cornerRadius: 8,
      callbacks: {
        label: (ctx) => {
          const total = ctx.dataset.data.reduce((s, v) => s + v, 0);
          const pct = total > 0 ? ((ctx.parsed / total) * 100).toFixed(1) : 0;
          return ` ${ctx.label}: ${ctx.parsed} (${pct}%)`;
        },
      },
    },
  },
};

const tabs = [
  { id: "overview", label: "Overview" },
  { id: "membership", label: "Membership" },
  { id: "operations", label: "Operations" },
  { id: "custom", label: "Custom Reports" },
  { id: "lifecycle", label: "Data Lifecycle" },
];

const DEFAULT_OVERVIEW_RANGE_KEY = "12m";
const OVERVIEW_RANGE_LABELS = {
  "7d": "Last 7 days",
  "30d": "Last 30 days",
  "3months": "Last 3 months",
  "12m": "Last 12 months",
  custom: "Custom range",
};

const OVERVIEW_DATE_RANGE_OPTIONS = [
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "3months", label: "Last 3 months" },
  { value: "12m", label: "Last 12 months" },
  { value: "custom", label: "Custom range" },
];

const DEFAULT_OVERVIEW_FILTERS = {
  dateRange: DEFAULT_OVERVIEW_RANGE_KEY,
  dateFrom: "",
  dateTo: "",
  company: "",
  affiliation: "",
  region: "",
  status: "",
};

const toSlug = (value = "") => value
  .toString()
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, "-")
  .replace(/^-+|-+$/g, "");

const formatFileSize = (bytes) => {
  const size = Number(bytes);
  if (!Number.isFinite(size) || size <= 0) {
    return "—";
  }
  const units = ["B", "KB", "MB", "GB", "TB"];
  let value = size;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  const digits = value >= 10 || unitIndex === 0 ? 0 : 1;
  return `${value.toFixed(digits)} ${units[unitIndex]}`;
};

const formatHistoryDate = (isoString) => {
  if (!isoString) {
    return "—";
  }
  try {
    const date = new Date(isoString);
    if (Number.isNaN(date.getTime())) {
      return isoString;
    }
    return new Intl.DateTimeFormat("en-PH", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }).format(date);
  } catch (error) {
    console.error('Unable to format export history date', error);
    return isoString;
  }
};

const formatExportFormatLabel = (format) => {
  if (!format) {
    return "CSV";
  }
  const normalized = format.toString().trim().toLowerCase();
  const labelMap = {
    pdf: "PDF",
    excel: "Excel",
    csv: "CSV",
    dashboard: "CSV",
  };
  return labelMap[normalized] ?? normalized.toUpperCase();
};

const normalizeScheduleFormatValue = (value, fallback = "pdf") => {
  const normalized = typeof value === "string" ? value.trim().toLowerCase() : "";
  if (!normalized) {
    return fallback;
  }
  if (normalized === "dashboard") {
    return "csv";
  }
  if (normalized === "xlsx" || normalized === "xls") {
    return "excel";
  }
  if (normalized === "pdf" || normalized === "excel" || normalized === "csv") {
    return normalized;
  }
  return normalized;
};

const formatExportStatus = (status) => {
  if (!status) {
    return "Completed";
  }
  const normalized = status.toString().trim();
  if (!normalized) {
    return "Completed";
  }
  return `${normalized.charAt(0).toUpperCase()}${normalized.slice(1)}`;
};

const EXCLUDED_REPORT_TYPE_VALUES = new Set([
  "financial",
  "financial-report",
  "benefits",
  "ai-analytics",
  "ai-audit",
]);
const LEGACY_REPORT_TYPE_LABELS = {
  financial: "Financial Report",
  "financial-report": "Financial Report",
};

const shouldExcludeReportType = (value, label) => {
  const normalizedValue = typeof value === "string" ? value.trim().toLowerCase() : "";
  const normalizedLabel = typeof label === "string" ? label.trim().toLowerCase() : "";
  if (normalizedValue && EXCLUDED_REPORT_TYPE_VALUES.has(normalizedValue)) {
    return true;
  }
  if (normalizedLabel === "financial report" || normalizedLabel === "financial") {
    return true;
  }
  return false;
};

const FILTER_KEY_LABEL_OVERRIDES = {
  company: 'By Company',
  affiliation: 'By Union Affiliation',
  union: 'By Union Position',
  region: 'By Region',
  gender: 'By Gender',
  status: 'By Status',
  booking_date: 'By Booking Date',
  type: 'By Venue Type',
  location: 'By Location',
  capacity: 'By Capacity',
  visibility: 'By Visibility',
  venue_id: 'By Venue ID',
  category: 'By Category',
  priority: 'By Priority',
  admin_id: 'By Admin ID',
  ip_address: 'By IP Address',
  lock_state: 'By Lock State',
  role_name: 'By Role Name',
  actor_type: 'By Actor Type',
  action: 'By Action',
  account_type: 'By Account Type',
  role: 'By Role',
  payment_status: 'By Payment Status',
  payment_method: 'By Payment Method',
  card_type: 'By Card Type',
};

const normalizeFilterFieldKey = (rawKey) => {
  const key = typeof rawKey === 'string' ? rawKey.trim() : '';
  if (!key) {
    return '';
  }
  const aliasMap = {
    union_name: 'affiliation',
    position: 'union',
    session_status: 'status',
  };
  return aliasMap[key] ?? key;
};

const toFilterFieldLabel = (fieldKey) => {
  const normalizedKey = normalizeFilterFieldKey(fieldKey);
  if (!normalizedKey) {
    return '';
  }
  if (FILTER_KEY_LABEL_OVERRIDES[normalizedKey]) {
    return FILTER_KEY_LABEL_OVERRIDES[normalizedKey];
  }
  return `By ${normalizedKey.replace(/[_-]+/g, ' ').replace(/\b\w/g, (character) => character.toUpperCase())}`;
};

const REPORT_TYPE_COLUMNS = {
  membership: [
    { value: 'company', label: 'Company' },
    { value: 'first_name', label: 'First Name' },
    { value: 'last_name', label: 'Last Name' },
    { value: 'email', label: 'Email' },
    { value: 'status', label: 'Status' },
    { value: 'union_affiliation', label: 'Union Affiliation' },
    { value: 'union_position', label: 'Union Position' },
    { value: 'region', label: 'Region' },
    { value: 'gender', label: 'Gender' },
    { value: 'date_joined', label: 'Date Joined' },
  ],
  events: [
    { value: 'event_title', label: 'Event Title' },
    { value: 'full_name', label: 'Full Name' },
    { value: 'email', label: 'Email' },
    { value: 'age', label: 'Age' },
    { value: 'registration_date', label: 'Registration Date' },
    { value: 'status', label: 'Registration Status' },
  ],
  venues: [
    { value: 'name', label: 'Venue Name' },
    { value: 'type', label: 'Venue Type' },
    { value: 'capacity', label: 'Capacity' },
    { value: 'location', label: 'Location' },
    { value: 'visibility', label: 'Visibility' },
  ],
  'client-bookings': [
    { value: 'venue_id', label: 'Venue ID' },
    { value: 'customer_name', label: 'Customer Name' },
    { value: 'status', label: 'Status' },
    { value: 'booking_date', label: 'Booking Date' },
  ],
  revenue_bookings: [
    { value: 'booking_id', label: 'Booking ID' },
    { value: 'venue_id', label: 'Venue ID' },
    { value: 'venue_name', label: 'Venue Name' },
    { value: 'customer_name', label: 'Customer Name' },
    { value: 'booking_date', label: 'Booking Date' },
    { value: 'status', label: 'Status' },
    { value: 'hours_booked', label: 'Hours Booked' },
    { value: 'hourly_rate', label: 'Hourly Rate (PHP)' },
    { value: 'estimated_revenue', label: 'Estimated Revenue (PHP)' },
  ],
  revenue_ids: [
    { value: 'request_no', label: 'Request No' },
    { value: 'member_name', label: 'Member Name' },
    { value: 'card_type', label: 'Card Type' },
    { value: 'request_status', label: 'Request Status' },
    { value: 'payment_method', label: 'Payment Method' },
    { value: 'payment_status', label: 'Payment Status' },
    { value: 'payment_collected_at', label: 'Payment Collected At' },
    { value: 'total_amount_php', label: 'Total Amount (PHP)' },
  ],
  tickets: [
    { value: 'ticket_no', label: 'Ticket No' },
    { value: 'subject', label: 'Subject' },
    { value: 'category', label: 'Category' },
    { value: 'priority', label: 'Priority' },
    { value: 'status', label: 'Status' },
    { value: 'department', label: 'Department' },
    { value: 'created_at', label: 'Created At' },
    { value: 'user_email', label: 'User Email' },
  ],
  admin_access: [
    { value: 'admin_id', label: 'Admin ID' },
    { value: 'admin_name', label: 'Admin Name' },
    { value: 'admin_email', label: 'Admin Email' },
    { value: 'ip_address', label: 'IP Address' },
    { value: 'login_time', label: 'Login Time' },
    { value: 'session_duration_minutes', label: 'Session Duration (mins)' },
    { value: 'session_status', label: 'Session Status' },
  ],
  security: [
    { value: 'admin_id', label: 'Admin ID' },
    { value: 'admin_name', label: 'Admin Name' },
    { value: 'admin_email', label: 'Admin Email' },
    { value: 'failed_attempts', label: 'Failed Attempts' },
    { value: 'locked_until', label: 'Locked Until' },
    { value: 'last_failed_at', label: 'Last Failed At' },
  ],
  admin_roles: [
    { value: 'admin_id', label: 'Admin ID' },
    { value: 'admin_name', label: 'Admin Name' },
    { value: 'admin_email', label: 'Admin Email' },
    { value: 'role_name', label: 'Role Name' },
    { value: 'role_code', label: 'Role Code' },
    { value: 'permissions', label: 'Permissions' },
    { value: 'assigned_at', label: 'Assigned At' },
  ],
  system_activity: [
    { value: 'actor_type', label: 'Actor Type' },
    { value: 'actor_name', label: 'Actor Name' },
    { value: 'action', label: 'Action' },
    { value: 'notifications_read', label: 'Notifications Read' },
    { value: 'ip_address', label: 'IP Address' },
    { value: 'created_at', label: 'Created At' },
  ],
  website_access: [
    { value: 'account_type', label: 'Account Type' },
    { value: 'name', label: 'Name' },
    { value: 'email', label: 'Email' },
    { value: 'role', label: 'Role' },
    { value: 'status', label: 'Status' },
    { value: 'created_at', label: 'Created At' },
  ],
  access_logs: [
    { value: 'actor_type', label: 'Actor Type' },
    { value: 'actor_name', label: 'Actor Name' },
    { value: 'actor_email', label: 'Actor Email' },
    { value: 'action', label: 'Action' },
    { value: 'entity_type', label: 'Entity Type' },
    { value: 'entity_id', label: 'Entity ID' },
    { value: 'ip_address', label: 'IP Address' },
    { value: 'created_at', label: 'Created At' },
  ],
  system_usage: [
    { value: 'account_type', label: 'Account Type' },
    { value: 'name', label: 'Name' },
    { value: 'email', label: 'Email' },
    { value: 'ip_address', label: 'IP Address' },
    { value: 'user_agent', label: 'User Agent' },
    { value: 'created_at', label: 'Session Started' },
    { value: 'last_activity_at', label: 'Last Activity' },
    { value: 'expires_at', label: 'Expires At' },
    { value: 'session_status', label: 'Session Status' },
  ],
};

const GROUPING_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'date-month', label: 'By Date/Month' },
  { value: 'status', label: 'By Status' },
  { value: 'category', label: 'By Category' },
];

const AGGREGATION_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'count', label: 'COUNT' },
  { value: 'sum', label: 'SUM' },
];

const REPORT_BUILDER_OPTION_MAP = {
  reportType: "reportTypes",
  dateRange: "dateRanges",
  format: "formats",
};

const REPORT_BUILDER_OPTION_ENTRIES = Object.entries(REPORT_BUILDER_OPTION_MAP);

const DEFAULT_REPORT_TYPE_VALUE = DEFAULT_REPORT_BUILDER_OPTIONS.reportTypes[0]?.value ?? 'membership';
const DEFAULT_DATE_RANGE_VALUE = DEFAULT_REPORT_BUILDER_OPTIONS.dateRanges[0]?.value ?? '7d';
const CUSTOM_DATE_RANGE_VALUE = 'custom';
const CUSTOM_DATE_RANGE_OPTION = { value: CUSTOM_DATE_RANGE_VALUE, label: 'Custom Range' };
const MEMBERSHIP_EXPORT_FORMAT_OPTIONS = [
  { value: 'csv', label: 'CSV' },
  { value: 'excel', label: 'Excel' },
  { value: 'pdf', label: 'PDF' },
];

const MEMBERSHIP_ADVANCED_FALLBACKS = {
  affiliations: [],
  unionNames: [],
  unionPositions: ['Member', 'Board Member', 'Secretary', 'Treasurer', 'Vice President', 'President'],
  regions: [],
  genders: ['Male', 'Female', 'Prefer not to say'],
  ageGroups: ['18-24', '25-34', '35-44', '45-54', '55+'],
};

const MEMBERSHIP_STATUS_FILTER_VALUES = [...MEMBERSHIP_ALLOWED_STATUS];
const MEMBERSHIP_STATUS_FILTER_SET = new Set(MEMBERSHIP_STATUS_FILTER_VALUES);

const MEMBERSHIP_STATUS_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'approved', label: 'Approved' },
  { value: 'pending', label: 'Pending' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'incomplete', label: 'Incomplete' },
];

const formatPesoCompact = (value) => {
  if (!Number.isFinite(value)) {
    return '₱0';
  }
  if (value === 0) {
    return '₱0';
  }
  const absolute = Math.abs(value);
  if (absolute >= 1000000) {
    const scaled = value / 1000000;
    const digits = Math.abs(scaled) >= 10 ? 0 : 1;
    return `₱${scaled.toFixed(digits)}M`;
  }
  if (absolute >= 1000) {
    const scaled = value / 1000;
    const digits = Math.abs(scaled) >= 10 ? 0 : 1;
    return `₱${scaled.toFixed(digits)}K`;
  }
  return `₱${Math.round(value)}`;
};

const computeAxisScale = (value, sections = 4) => {
  const safeValue = Number.isFinite(value) && value > 0 ? value : 0;
  if (safeValue === 0) {
    return { max: 1, step: 1 };
  }
  const roughStep = safeValue / Math.max(sections, 1);
  const magnitude = 10 ** Math.floor(Math.log10(roughStep));
  const candidates = [1, 2, 2.5, 5, 10];
  let step = magnitude;
  for (const multiplier of candidates) {
    const option = multiplier * magnitude;
    step = option;
    if (roughStep <= option) {
      break;
    }
  }
  const max = step * Math.ceil(safeValue / step);
  return { max, step };
};

const SCHEDULE_CADENCE_LABELS = {
  daily: "Daily",
  weekly: "Weekly",
  biweekly: "Every 2 weeks",
  monthly: "Monthly",
  quarterly: "Quarterly",
  annually: "Annual",
  custom: "Custom cadence",
};

const SCHEDULE_CADENCE_OPTIONS = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "biweekly", label: "Bi-weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "annually", label: "Annual" },
  { value: "custom", label: "Custom" },
];

const DEFAULT_SCHEDULE_FORM = {
  name: "",
  cadence: "monthly",
  nextRun: "",
  activationTime: "09:00",
  timezone: "Asia/Manila",
  format: "pdf",
  reportType: DEFAULT_REPORT_TYPE_VALUE,
  dateRange: DEFAULT_DATE_RANGE_VALUE,
  dateFrom: "",
  dateTo: "",
  filters: {},
  selectedColumns: [],
  grouping: "none",
  aggregation: "none",
  reportConfig: {
    reportType: DEFAULT_REPORT_TYPE_VALUE,
    dateRange: DEFAULT_DATE_RANGE_VALUE,
    dateFrom: "",
    dateTo: "",
    filters: {},
    selectedColumns: [],
    grouping: "none",
    aggregation: "none",
    format: "pdf",
  },
  recipientsInput: "",
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;

const normalizeScheduleRecipients = (input) => {
  if (!input) {
    return [];
  }
  const source = Array.isArray(input)
    ? input
    : String(input)
      .split(/[,;\n]+/)
      .map((value) => value.trim());

  const seen = new Set();
  const normalized = [];
  source.forEach((value) => {
    const email = String(value || '').trim();
    if (!email) {
      return;
    }
    const key = email.toLowerCase();
    if (seen.has(key)) {
      return;
    }
    seen.add(key);
    normalized.push(email);
  });
  return normalized;
};

const scheduleIdFor = (entry) => {
  if (!entry) {
    return null;
  }
  if (typeof entry.id === "string" && entry.id) {
    return entry.id;
  }
  if (typeof entry.scheduleId === "string" && entry.scheduleId) {
    return entry.scheduleId;
  }
  if (typeof entry.key === "string" && entry.key) {
    return entry.key;
  }
  return null;
};

const normalizeScheduleEntry = (entry, fallbackId = "") => {
  if (!entry) {
    return null;
  }
  const resolvedId = scheduleIdFor(entry) ?? fallbackId;
  if (!resolvedId) {
    return null;
  }

  const rawCadence =
    typeof entry.cadence === "string" && entry.cadence
      ? entry.cadence
      : typeof entry.frequency === "string"
        ? entry.frequency
        : "";
  const cadenceKey = rawCadence ? rawCadence.trim().toLowerCase() : "";
  const cadenceLabel = entry.cadenceLabel
    ?? SCHEDULE_CADENCE_LABELS[cadenceKey]
    ?? (cadenceKey
      ? `${cadenceKey.charAt(0).toUpperCase()}${cadenceKey.slice(1)}`
      : SCHEDULE_CADENCE_LABELS.custom);

  return {
    id: resolvedId,
    name:
      (typeof entry.name === "string" && entry.name.trim())
        ? entry.name.trim()
        : (typeof entry.reportName === "string" && entry.reportName.trim())
          ? entry.reportName.trim()
          : "Untitled report",
    cadence: cadenceKey || "custom",
    cadenceLabel,
    nextRun: entry.nextRun ?? entry.nextRunAt ?? null,
    timezone: entry.timezone ?? entry.timeZone ?? "Asia/Manila",
    format: normalizeScheduleFormatValue(
      (typeof entry.format === "string" && entry.format.trim())
        ? entry.format.trim()
        : (typeof entry.deliveryFormat === "string" && entry.deliveryFormat.trim())
          ? entry.deliveryFormat.trim()
          : null,
      null,
    ),
    reportType:
      (typeof entry.reportType === "string" && entry.reportType.trim())
        ? entry.reportType.trim()
        : DEFAULT_REPORT_TYPE_VALUE,
    dateRange:
      (typeof entry.dateRange === "string" && entry.dateRange.trim())
        ? entry.dateRange.trim()
        : DEFAULT_DATE_RANGE_VALUE,
    dateFrom:
      (typeof entry.dateFrom === "string" && entry.dateFrom.trim())
        ? entry.dateFrom.trim()
        : "",
    dateTo:
      (typeof entry.dateTo === "string" && entry.dateTo.trim())
        ? entry.dateTo.trim()
        : "",
    filters: entry.filters && typeof entry.filters === 'object' && !Array.isArray(entry.filters)
      ? { ...entry.filters }
      : {},
    selectedColumns: Array.isArray(entry.selectedColumns)
      ? entry.selectedColumns.map((column) => String(column))
      : [],
    grouping:
      (typeof entry.grouping === "string" && entry.grouping.trim())
        ? entry.grouping.trim()
        : "none",
    aggregation:
      (typeof entry.aggregation === "string" && entry.aggregation.trim())
        ? entry.aggregation.trim()
        : "none",
    recipients: normalizeScheduleRecipients(entry.recipients),
    active: typeof entry.active === "boolean" ? entry.active : entry.status !== "paused",
    isBlank: Boolean(entry.isBlank),
    lastRun: entry.lastRun ?? null,
    lastRunStatus: entry.lastRunStatus ?? entry.lastStatus ?? null,
    updatedAt: entry.updatedAt ?? entry.modifiedAt ?? null,
  };
};

const formatScheduleDate = (isoString, timezone) => {
  if (!isoString) {
    return "Next run not set";
  }
  try {
    const formatter = new Intl.DateTimeFormat("en-PH", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
      timeZone: timezone || "Asia/Manila",
    });
    return `Next run ${formatter.format(new Date(isoString))}`;
  } catch (error) {
    return `Next run ${isoString}`;
  }
};

const toDateInputValue = (isoString) => {
  if (!isoString) {
    return "";
  }
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  return date.toISOString().slice(0, 10);
};

const toTimeInputValue = (isoString, timezone) => {
  if (!isoString) {
    return "";
  }
  try {
    const date = new Date(isoString);
    if (Number.isNaN(date.getTime())) {
      return "";
    }
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone || "Asia/Manila",
      hour12: false,
      hour: "2-digit",
      minute: "2-digit",
    });
    const parts = formatter.formatToParts(date);
    const hourPart = parts.find((part) => part.type === "hour");
    const minutePart = parts.find((part) => part.type === "minute");
    if (!hourPart || !minutePart) {
      return "";
    }
    const hour = hourPart.value.padStart(2, "0");
    const minute = minutePart.value.padStart(2, "0");
    return `${hour}:${minute}`;
  } catch (error) {
    return "";
  }
};

const computeTimeZoneOffsetSuffix = (timeZone, referenceDate) => {
  try {
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: timeZone || "Asia/Manila",
      hour12: false,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      timeZoneName: "shortOffset",
    });
    const parts = formatter.formatToParts(referenceDate);
    const zonePart = parts.find((part) => part.type === "timeZoneName");
    if (!zonePart) {
      return "+00:00";
    }
    const match = zonePart.value.match(/GMT([+-]\d{1,2})(?::?(\d{2}))?/i);
    if (!match) {
      return "+00:00";
    }
    const rawHours = match[1] ?? "+00";
    const sign = rawHours.startsWith("-") ? "-" : "+";
    const hours = rawHours.replace(/[+-]/, "").padStart(2, "0");
    const minutes = (match[2] ?? "00").padStart(2, "0");
    return `${sign}${hours}:${minutes}`;
  } catch (error) {
    return "+00:00";
  }
};

const fromDateInputToIso = (dateString, timeString = "09:00", timeZone = "Asia/Manila") => {
  if (!dateString) {
    return null;
  }
  const timeParts = typeof timeString === "string" ? timeString.split(":") : [];
  const rawHour = timeParts[0] ?? "09";
  const rawMinute = timeParts[1] ?? "00";
  const hour = Number.parseInt(rawHour, 10);
  const minute = Number.parseInt(rawMinute, 10);
  if (
    Number.isNaN(hour) || hour < 0 || hour > 23
    || Number.isNaN(minute) || minute < 0 || minute > 59
  ) {
    return null;
  }
  const [yearStr, monthStr, dayStr] = dateString.split("-");
  if (!yearStr || !monthStr || !dayStr) {
    return null;
  }
  const year = Number.parseInt(yearStr, 10);
  const month = Number.parseInt(monthStr, 10);
  const day = Number.parseInt(dayStr, 10);
  if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) {
    return null;
  }
  const safeHour = String(hour).padStart(2, "0");
  const safeMinute = String(minute).padStart(2, "0");
  const referenceUtc = new Date(Date.UTC(year, month - 1, day, hour, minute, 0));
  const offsetSuffix = computeTimeZoneOffsetSuffix(timeZone, referenceUtc);
  const safeYear = yearStr.padStart(4, "0");
  const safeMonth = monthStr.padStart(2, "0");
  const safeDay = dayStr.padStart(2, "0");
  const isoWithOffset = `${safeYear}-${safeMonth}-${safeDay}T${safeHour}:${safeMinute}:00${offsetSuffix}`;
  const isoDate = new Date(isoWithOffset);
  if (Number.isNaN(isoDate.getTime())) {
    return referenceUtc.toISOString();
  }
  return isoDate.toISOString();
};

const extractFilenameFromDisposition = (disposition, fallback) => {
  if (typeof disposition !== 'string' || !disposition) {
    return fallback;
  }
  const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match && utf8Match[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch (error) {
      return utf8Match[1];
    }
  }
  const simpleMatch = disposition.match(/filename="?([^";]+)"?/i);
  if (simpleMatch && simpleMatch[1]) {
    return simpleMatch[1];
  }
  return fallback;
};

const parseCsvLine = (line) => {
  const values = [];
  let current = '';
  let inQuotes = false;

  for (let index = 0; index < line.length; index += 1) {
    const char = line[index];
    const next = line[index + 1];

    if (char === '"') {
      if (inQuotes && next === '"') {
        current += '"';
        index += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === ',' && !inQuotes) {
      values.push(current);
      current = '';
      continue;
    }

    current += char;
  }

  values.push(current);
  return values;
};

const parseCsvPreview = (csvText, maxRows = 30) => {
  const lines = (csvText ?? '')
    .toString()
    .split(/\r?\n/)
    .map((line) => line.trimEnd())
    .filter((line) => line.length > 0);

  if (!lines.length) {
    return { header: [], rows: [] };
  }

  const header = parseCsvLine(lines[0]);
  const rows = lines.slice(1, maxRows + 1).map((line) => parseCsvLine(line));
  return { header, rows };
};

export default function ReportsAnalytics() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [activityView, setActivityView] = useState("grid");
  const [timeRange, setTimeRange] = useState("30d");
  const [selectedCompany, setSelectedCompany] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedAffiliation, setSelectedAffiliation] = useState("all");
  const [selectedUnionName, setSelectedUnionName] = useState("all");
  const [selectedUnionPosition, setSelectedUnionPosition] = useState("all");
  const [selectedRegion, setSelectedRegion] = useState("all");
  const [selectedGender, setSelectedGender] = useState("all");
  const [selectedAgeGroup, setSelectedAgeGroup] = useState("all");
  const [draftUnionName, setDraftUnionName] = useState("all");
  const [draftUnionPosition, setDraftUnionPosition] = useState("all");
  const [draftGender, setDraftGender] = useState("all");
  const [draftAgeGroup, setDraftAgeGroup] = useState("all");
  const [showAdvancedMembershipFilters, setShowAdvancedMembershipFilters] = useState(false);
  const [isExportingMembership, setIsExportingMembership] = useState(false);
  const [isMembershipExportPreviewOpen, setIsMembershipExportPreviewOpen] = useState(false);
  const [isPreparingMembershipPaperPreview, setIsPreparingMembershipPaperPreview] = useState(false);
  const [membershipPaperPreviewUrl, setMembershipPaperPreviewUrl] = useState(null);
  const [membershipPaperPreviewError, setMembershipPaperPreviewError] = useState(null);
  const [membershipPreviewMode, setMembershipPreviewMode] = useState('pdf');
  const [membershipTablePreview, setMembershipTablePreview] = useState({ header: [], rows: [] });
  const [membershipExportFormat, setMembershipExportFormat] = useState("csv");
  const [membershipExportNotice, setMembershipExportNotice] = useState(null);
  const [membershipExportError, setMembershipExportError] = useState(null);
  const [reportConfig, setReportConfig] = useState(() => ({
    reportType: DEFAULT_REPORT_BUILDER_OPTIONS.reportTypes[0]?.value ?? DEFAULT_REPORT_TYPE_VALUE,
    dateRange: DEFAULT_REPORT_BUILDER_OPTIONS.dateRanges[0]?.value ?? DEFAULT_DATE_RANGE_VALUE,
    dateFrom: '',
    dateTo: '',
    filters: {
      status: '',
      category: '',
      type: '',
      region: '',
    },
    selectedColumns: [],
    grouping: 'none',
    aggregation: 'none',
    format: DEFAULT_REPORT_BUILDER_OPTIONS.formats[0]?.value ?? 'pdf',
  }));
  const [isGeneratingCustomReport, setIsGeneratingCustomReport] = useState(false);
  const [reportBuilderError, setReportBuilderError] = useState(null);
  const [reportBuilderNotice, setReportBuilderNotice] = useState(null);
  const [exportHistory, setExportHistory] = useState([]);
  const [isLoadingExportHistory, setIsLoadingExportHistory] = useState(false);
  const [exportHistoryError, setExportHistoryError] = useState(null);
  const [historyDeleteId, setHistoryDeleteId] = useState(null);
  const [historyActionError, setHistoryActionError] = useState(null);
  const [isBuilderAdvancedOpen, setIsBuilderAdvancedOpen] = useState(false);
  const [isScheduleAdvancedOpen, setIsScheduleAdvancedOpen] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const HISTORY_PAGE_SIZE = 10;
  const [overviewFilters, setOverviewFilters] = useState(() => ({ ...DEFAULT_OVERVIEW_FILTERS }));
  const [overviewSummary, setOverviewSummary] = useState(null);
  const [isLoadingOverview, setIsLoadingOverview] = useState(false);
  const [overviewError, setOverviewError] = useState(null);

  const availableColumns = useMemo(
    () => REPORT_TYPE_COLUMNS[reportConfig.reportType] ?? [],
    [reportConfig.reportType]
  );

  useEffect(() => {
    setIsBuilderAdvancedOpen(false);
    setReportConfig((previous) => ({
      ...previous,
      selectedColumns: [],
      grouping: 'none',
      aggregation: 'none',
      filters: {
        status: '',
        category: '',
        type: '',
        region: '',
      },
      dateFrom: '',
      dateTo: '',
    }));
  }, [reportConfig.reportType]);

  const activityViewOptions = [
    { id: "grid", label: "Grid" },
    { id: "list", label: "List" },
  ];

  const rangeShortLabels = {
    "7d": "Last 7 days",
    "30d": "Last 30 days",
    "90d": "Last 3 months",
    "12m": "Last 12 months",
  };

  const rangeDurationLabels = {
    "7d": "7 days",
    "30d": "30 days",
    "90d": "3 months",
    "12m": "12 months",
  };

  const newJoinerFieldByRange = {
    "7d": "newJoiners7d",
    "30d": "newJoiners30d",
    "90d": "newJoiners90d",
    "12m": "newJoiners12m",
  };

  const [summary, setSummary] = useState(null);
  const [dashboardKpis, setDashboardKpis] = useState(null);
  const [isLoadingDashboardData, setIsLoadingDashboardData] = useState(false);
  const [companySummary, setCompanySummary] = useState(null);
  const [isLoadingCompanySummary, setIsLoadingCompanySummary] = useState(false);
  const [companySummaryError, setCompanySummaryError] = useState(null);
  const companySummaryCacheRef = useRef(new Map());
  const scheduleRunsRef = useRef(new Map());
  const [schedulePendingMap, setSchedulePendingMap] = useState({});
  const [scheduleActionError, setScheduleActionError] = useState(null);
  const [scheduleResults, setScheduleResults] = useState([]);
  const [isLoadingSchedules, setIsLoadingSchedules] = useState(false);
  const [schedulePage, setSchedulePage] = useState(1);
  const [scheduleTotalPages, setScheduleTotalPages] = useState(1);
  const [scheduleTotal, setScheduleTotal] = useState(0);
  const SCHEDULE_PAGE_SIZE = 10;
  const [editingScheduleId, setEditingScheduleId] = useState(null);
  const [isCreatingSchedule, setIsCreatingSchedule] = useState(false);
  const [creatingSlotId, setCreatingSlotId] = useState(null);
  const [scheduleForm, setScheduleForm] = useState(() => ({ ...DEFAULT_SCHEDULE_FORM }));
  const [scheduleFormError, setScheduleFormError] = useState(null);
  const [isSavingSchedule, setIsSavingSchedule] = useState(false);
  const [historyMenuOpenId, setHistoryMenuOpenId] = useState(null);
  const [historyMenuCoords, setHistoryMenuCoords] = useState({ top: 0, left: 0 });
  const [historyMenuPlacement, setHistoryMenuPlacement] = useState("down");
  const [isDeletingAllHistory, setIsDeletingAllHistory] = useState(false);
  const [filterOptions, setFilterOptions] = useState({});
  const [scheduleFilterOptions, setScheduleFilterOptions] = useState({});
  const membershipPreviewFrameRef = useRef(null);

  // Data Lifecycle tab state
  const [lifecycleData, setLifecycleData] = useState(null);
  const [isLoadingLifecycle, setIsLoadingLifecycle] = useState(false);
  const [lifecycleError, setLifecycleError] = useState(null);
  const [archivingTable, setArchivingTable] = useState(null);
  const [archiveResult, setArchiveResult] = useState(null);
  const [archiveError, setArchiveError] = useState(null);
  const [archiveConfirm, setArchiveConfirm] = useState(null);

  const normalizeFetchedFilterOptions = useCallback((rawOptions = {}) => {
    const toArray = (value) => (Array.isArray(value)
      ? value.map((entry) => String(entry ?? '').trim()).filter(Boolean)
      : []);

    return Object.keys(rawOptions || {}).reduce((accumulator, rawKey) => {
      const normalizedKey = normalizeFilterFieldKey(rawKey);
      if (!normalizedKey) {
        return accumulator;
      }
      const merged = [
        ...(Array.isArray(accumulator[normalizedKey]) ? accumulator[normalizedKey] : []),
        ...toArray(rawOptions[rawKey]),
      ];
      accumulator[normalizedKey] = Array.from(new Set(merged));
      return accumulator;
    }, {});
  }, []);

  const loadFilterOptions = useCallback(async (reportType, setter) => {
    const normalizedType = typeof reportType === 'string' ? reportType.trim() : '';
    if (!normalizedType) {
      setter({});
      return;
    }
    try {
      const response = await api.getReportFilterOptions(normalizedType);
      const payload = response?.data && typeof response.data === 'object' ? response.data : {};
      setter(normalizeFetchedFilterOptions(payload));
    } catch (error) {
      console.error('Unable to load report filter options', error);
      setter({});
    }
  }, [normalizeFetchedFilterOptions]);

  useEffect(() => {
    loadFilterOptions(reportConfig.reportType, setFilterOptions);
  }, [loadFilterOptions, reportConfig.reportType]);

  const builderFilterDefinitions = useMemo(() => {
    const reportType = reportConfig.reportType;
    const typeColumns = Array.isArray(REPORT_TYPE_COLUMNS[reportType])
      ? REPORT_TYPE_COLUMNS[reportType]
      : [];
    const keysInOptions = new Set(Object.keys(filterOptions).map((key) => normalizeFilterFieldKey(key)).filter(Boolean));
    const fromColumns = typeColumns
      .map((column) => normalizeFilterFieldKey(column?.value))
      .filter((key) => key && keysInOptions.has(key));
    const mergedKeys = Array.from(new Set([...fromColumns, ...Object.keys(filterOptions).map((key) => normalizeFilterFieldKey(key)).filter(Boolean)]));

    return mergedKeys.map((key) => ({
      value: key,
      label: toFilterFieldLabel(key),
    }));
  }, [filterOptions, reportConfig.reportType]);

  const supportsDateFilter = ['membership', 'events', 'client-bookings', 'revenue_bookings', 'revenue_ids', 'tickets', 'admin_access', 'security', 'admin_roles', 'system_activity', 'access_logs', 'system_usage'].includes(reportConfig.reportType);
  const isCustomDateRangeSelected = supportsDateFilter && reportConfig.dateRange === CUSTOM_DATE_RANGE_VALUE;

  const builderSelectedColumnCount = useMemo(() => {
    const selected = new Set(Array.isArray(reportConfig.selectedColumns) ? reportConfig.selectedColumns : []);
    return availableColumns.reduce((count, column) => count + (selected.has(column.value) ? 1 : 0), 0);
  }, [availableColumns, reportConfig.selectedColumns]);

  const hasSelectedBuilderColumns = builderSelectedColumnCount > 0;
  const canSelectAllBuilderColumns = availableColumns.length > builderSelectedColumnCount;

  const hasActiveBuilderFilters = useMemo(() => {
    const hasOptionFilters = Object.values(reportConfig.filters ?? {})
      .some((value) => String(value ?? '').trim() !== '');
    return hasOptionFilters || Boolean(reportConfig.dateFrom) || Boolean(reportConfig.dateTo);
  }, [reportConfig.dateFrom, reportConfig.dateTo, reportConfig.filters]);

  const summaryMeta = summary?.meta ?? null;
  const overviewData = overviewSummary?.overview ?? null;
  const overviewMeta = overviewData?.meta ?? null;
  const overviewGroupBy = overviewMeta?.groupBy === "month" ? "month" : "day";
  const isOverviewCustomRange = overviewFilters.dateRange === "custom";
  const overviewRangeValidationError = (() => {
    if (!isOverviewCustomRange) {
      return null;
    }
    if (!overviewFilters.dateFrom || !overviewFilters.dateTo) {
      return "Select both custom range dates.";
    }
    if (overviewFilters.dateFrom > overviewFilters.dateTo) {
      return "Custom range start date must be on or before the end date.";
    }
    return null;
  })();
  const overviewRequestParams = useMemo(() => {
    const params = {
      range: overviewFilters.dateRange,
    };

    if (overviewFilters.company) params.company = overviewFilters.company;
    if (overviewFilters.affiliation) params.affiliation = overviewFilters.affiliation;
    if (overviewFilters.region) params.region = overviewFilters.region;
    if (overviewFilters.status) params.status = overviewFilters.status;

    if (isOverviewCustomRange && !overviewRangeValidationError) {
      params.dateFrom = overviewFilters.dateFrom;
      params.dateTo = overviewFilters.dateTo;
    }

    return params;
  }, [
    isOverviewCustomRange,
    overviewFilters.affiliation,
    overviewFilters.company,
    overviewFilters.dateFrom,
    overviewFilters.dateRange,
    overviewFilters.dateTo,
    overviewFilters.region,
    overviewFilters.status,
    overviewRangeValidationError,
  ]);
  const overviewRangeLabel = overviewMeta?.range?.label
    ?? (isOverviewCustomRange && overviewFilters.dateFrom && overviewFilters.dateTo
      ? `${overviewFilters.dateFrom} - ${overviewFilters.dateTo}`
      : (OVERVIEW_RANGE_LABELS[overviewFilters.dateRange] ?? "Selected range"));
  const hasActiveOverviewFilters = useMemo(() => (
    overviewFilters.dateRange !== DEFAULT_OVERVIEW_RANGE_KEY
    || Boolean(overviewFilters.company)
    || Boolean(overviewFilters.affiliation)
    || Boolean(overviewFilters.region)
    || Boolean(overviewFilters.status)
    || Boolean(overviewFilters.dateFrom)
    || Boolean(overviewFilters.dateTo)
  ), [
    overviewFilters.affiliation,
    overviewFilters.company,
    overviewFilters.dateFrom,
    overviewFilters.dateRange,
    overviewFilters.dateTo,
    overviewFilters.region,
    overviewFilters.status,
  ]);
  const hasOverviewData = useMemo(() => {
    if (!overviewData) {
      return false;
    }

    const counts = overviewData.counts ?? {};
    const countKeys = [
      "totalAccounts",
      "approvedMembers",
      "activeMembers",
      "newRegistrations",
      "ticketSubmissions",
      "eventAttendance",
    ];

    return countKeys.some((key) => Number(counts[key] ?? 0) > 0)
      || (Array.isArray(overviewData.memberDistribution) && overviewData.memberDistribution.length > 0)
      || (Array.isArray(overviewData.topCompanies) && overviewData.topCompanies.length > 0)
      || (Array.isArray(overviewData.membershipTrend) && overviewData.membershipTrend.some((entry) => Number(entry.newRegistrations ?? 0) > 0))
      || (Array.isArray(overviewData.appAccessTrend) && overviewData.appAccessTrend.some((entry) => Number(entry.totalRequests ?? 0) > 0))
      || (Array.isArray(overviewData.userEngagement) && overviewData.userEngagement.length > 0);
  }, [overviewData]);

  const selectedRangeShort = rangeShortLabels[timeRange] ?? "Selected range";
  const selectedRangeDuration = rangeDurationLabels[timeRange] ?? "selected range";

  const parseActivityMetadata = (detail) => {
    if (!detail) {
      return {};
    }
    if (typeof detail === "object") {
      return detail;
    }
    if (typeof detail === "string") {
      try {
        const parsed = JSON.parse(detail);
        if (parsed && typeof parsed === "object") {
          return parsed;
        }
      } catch (error) {
        console.error('Unable to parse activity metadata detail', error);
        return { description: detail };
      }
      return { description: detail };
    }
    return {};
  };

  const formatActionLabel = (action) => {
    if (!action) return "Unknown Action";
    return String(action)
      .replace(/[-_]/g, " ")
      .replace(/\b\w/g, (character) => character.toUpperCase());
  };

  const toDisplayRole = (role) => {
    if (!role || typeof role !== "string") {
      return "Admin";
    }

    const normalized = role.trim().toLowerCase();
    if (normalized.includes("super")) return "Super Admin";
    if (normalized.includes("ticket")) return "Ticket Admin";
    if (normalized.includes("approver") || normalized.includes("approval")) return "Approver Admin";
    if (normalized.includes("booking")) return "Booking Admin";
    if (normalized.includes("event")) return "Events Admin";
    if (normalized.includes("id card") || normalized.includes("id_card")) return "ID Card Admin";

    return role
      .trim()
      .replace(/[_-]/g, " ")
      .replace(/\b\w/g, (character) => character.toUpperCase());
  };

  const computeTime = (timestamp, fallback) => {
    if (fallback) return fallback;
    if (!timestamp) return "";
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return "";
    const diffMs = Date.now() - date.getTime();
    const minute = 60 * 1000;
    const hour = 60 * minute;
    const day = 24 * hour;
    if (diffMs < hour) {
      const minutes = Math.max(1, Math.round(diffMs / minute));
      return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
    }
    if (diffMs < day) {
      const hours = Math.round(diffMs / hour);
      return `${hours} hour${hours === 1 ? "" : "s"} ago`;
    }
    const days = Math.round(diffMs / day);
    return `${days} day${days === 1 ? "" : "s"} ago`;
  };

  const coerceTimestamp = (value) => {
    if (!value) {
      return null;
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const getJoinersValue = (membersData, range) => {
    if (!membersData) return null;

    if (membersData.newJoinersByRange && membersData.newJoinersByRange[range] != null) {
      return membersData.newJoinersByRange[range];
    }
    if (membersData.newRegistrationsByRange && membersData.newRegistrationsByRange[range] != null) {
      return membersData.newRegistrationsByRange[range];
    }

    if (membersData.newJoiners && typeof membersData.newJoiners === 'object' && membersData.newJoiners[range] != null) {
      return membersData.newJoiners[range];
    }
    if (membersData.newRegistrations && typeof membersData.newRegistrations === 'object' && membersData.newRegistrations[range] != null) {
      return membersData.newRegistrations[range];
    }

    const field = newJoinerFieldByRange[range];
    if (field && membersData[field] != null) {
      return membersData[field];
    }
    const registrationField = field ? field.replace('newJoiners', 'newRegistrations') : null;
    if (registrationField && membersData[registrationField] != null) {
      return membersData[registrationField];
    }

    if (membersData.newJoiners30d != null) {
      return membersData.newJoiners30d;
    }
    if (membersData.newRegistrations30d != null) {
      return membersData.newRegistrations30d;
    }

    return null;
  };

  const companySnapshot = useMemo(() => {
    if (selectedCompany === 'all') {
      return null;
    }
    const companies = summary?.members?.companyStats ?? [];
    const target = selectedCompany.toLowerCase();
    return companies.find((entry) => (entry.company ?? '').toLowerCase() === target) ?? null;
  }, [selectedCompany, summary]);

  const companyOptions = useMemo(() => {
    const filters = Array.isArray(summary?.members?.companyFilters)
      ? summary.members.companyFilters
      : [];
    const distributionCompanies = Array.isArray(summary?.members?.distribution)
      ? summary.members.distribution.map((item) => item.company).filter(Boolean)
      : [];
    const combined = [...filters, ...distributionCompanies];
    const seen = new Set();
    return combined
      .map((name) => (typeof name === 'string' ? name.trim() : ''))
      .filter((name) => {
        if (!name) {
          return false;
        }
        const key = name.toLowerCase();
        if (key === 'unspecified') {
          return false;
        }
        if (seen.has(key)) {
          return false;
        }
        seen.add(key);
        return true;
      });
  }, [summary]);

  const membershipScopeParams = useMemo(() => {
    const params = {};
    if (selectedCompany !== 'all') params.company = selectedCompany;
    if (selectedStatus !== 'all') params.status = selectedStatus;
    if (selectedAffiliation !== 'all') params.affiliation = selectedAffiliation;
    if (selectedUnionName !== 'all') params.unionName = selectedUnionName;
    if (selectedUnionPosition !== 'all') params.unionPosition = selectedUnionPosition;
    if (selectedRegion !== 'all') params.region = selectedRegion;
    if (selectedGender !== 'all') params.gender = selectedGender;
    if (selectedAgeGroup !== 'all') params.ageGroup = selectedAgeGroup;
    return params;
  }, [
    selectedCompany,
    selectedStatus,
    selectedAffiliation,
    selectedUnionName,
    selectedUnionPosition,
    selectedRegion,
    selectedGender,
    selectedAgeGroup,
  ]);

  const summaryRequestParams = useMemo(
    () => ({ range: timeRange, ...membershipScopeParams }),
    [timeRange, membershipScopeParams],
  );

  const activeAdvancedMembershipFilterCount = useMemo(() => (
    [
      selectedUnionName,
      selectedUnionPosition,
      selectedGender,
      selectedAgeGroup,
    ].filter((value) => value !== 'all').length
  ), [
    selectedUnionName,
    selectedUnionPosition,
    selectedGender,
    selectedAgeGroup,
  ]);

  useEffect(() => {
    if (activeAdvancedMembershipFilterCount > 0) {
      setShowAdvancedMembershipFilters(true);
    }
  }, [activeAdvancedMembershipFilterCount]);

  useEffect(() => {
    if (showAdvancedMembershipFilters) {
      setDraftUnionName(selectedUnionName);
      setDraftUnionPosition(selectedUnionPosition);
      setDraftGender(selectedGender);
      setDraftAgeGroup(selectedAgeGroup);
    }
  }, [
    showAdvancedMembershipFilters,
    selectedUnionName,
    selectedUnionPosition,
    selectedGender,
    selectedAgeGroup,
  ]);

  const handleToggleAdvancedFilters = () => {
    if (!showAdvancedMembershipFilters) {
      setDraftUnionName(selectedUnionName);
      setDraftUnionPosition(selectedUnionPosition);
      setDraftGender(selectedGender);
      setDraftAgeGroup(selectedAgeGroup);
    }
    setShowAdvancedMembershipFilters((current) => !current);
  };

  const handleApplyAdvancedFilters = () => {
    setSelectedUnionName(draftUnionName);
    setSelectedUnionPosition(draftUnionPosition);
    setSelectedGender(draftGender);
    setSelectedAgeGroup(draftAgeGroup);
  };

  const handleClearMembershipFilters = () => {
    setSelectedCompany('all');
    setSelectedStatus('all');
    setSelectedAffiliation('all');
    setSelectedUnionName('all');
    setSelectedUnionPosition('all');
    setSelectedRegion('all');
    setSelectedGender('all');
    setSelectedAgeGroup('all');
    setDraftUnionName('all');
    setDraftUnionPosition('all');
    setDraftGender('all');
    setDraftAgeGroup('all');
    setShowAdvancedMembershipFilters(false);
    setMembershipExportError(null);
    setMembershipExportNotice(null);
  };

  const buildFilterOptionList = useCallback((dynamic = [], fallback = [], selectedValue = 'all') => {
    const seen = new Set();
    const values = [...dynamic, ...fallback, selectedValue]
      .map((value) => (typeof value === 'string' ? value.trim() : ''))
      .filter((value) => {
        const normalized = value.toLowerCase();
        if (!value || normalized === 'all' || normalized === 'unspecified') {
          return false;
        }
        const key = normalized;
        if (seen.has(key)) {
          return false;
        }
        seen.add(key);
        return true;
      });
    return values;
  }, []);

  const overviewFilterChoices = useMemo(() => {
    const filterOptionsFromOverview = overviewData?.filterOptions ?? {};
    const statusFallbacks = MEMBERSHIP_STATUS_FILTER_VALUES;
    const statusValues = buildFilterOptionList(
      filterOptionsFromOverview.status ?? [],
      statusFallbacks,
      overviewFilters.status,
    );
    const normalizedStatusValues = new Set(
      statusValues
        .map((value) => value.toLowerCase())
        .filter((value) => MEMBERSHIP_STATUS_FILTER_SET.has(value)),
    );

    return {
      companies: buildFilterOptionList(
        filterOptionsFromOverview.company ?? [],
        [],
        overviewFilters.company,
      ),
      affiliations: buildFilterOptionList(
        filterOptionsFromOverview.affiliation ?? [],
        [],
        overviewFilters.affiliation,
      ),
      regions: buildFilterOptionList(
        filterOptionsFromOverview.region ?? [],
        [],
        overviewFilters.region,
      ),
      statuses: MEMBERSHIP_STATUS_FILTER_VALUES.filter((value) => normalizedStatusValues.has(value)),
    };
  }, [
    buildFilterOptionList,
    overviewData,
    overviewFilters.affiliation,
    overviewFilters.company,
    overviewFilters.region,
    overviewFilters.status,
  ]);

  const advancedFilterOptions = useMemo(() => {
    const filterOptionsFromSummary = summary?.members?.demographics?.filterOptions ?? {};
    const ageFromSummary = Array.isArray(summary?.members?.demographics?.ageDistribution)
      ? summary.members.demographics.ageDistribution.map((entry) => entry.group)
      : [];
    const unionFromSummary = Array.isArray(summary?.members?.demographics?.unionPositions)
      ? summary.members.demographics.unionPositions.map((entry) => entry.label)
      : [];

    return {
      affiliations: buildFilterOptionList(filterOptionsFromSummary.affiliation ?? [], MEMBERSHIP_ADVANCED_FALLBACKS.affiliations, selectedAffiliation),
      unionNames: buildFilterOptionList(filterOptionsFromSummary.unionName ?? [], MEMBERSHIP_ADVANCED_FALLBACKS.unionNames, selectedUnionName),
      unionPositions: buildFilterOptionList(
        [...(filterOptionsFromSummary.unionPosition ?? []), ...unionFromSummary],
        MEMBERSHIP_ADVANCED_FALLBACKS.unionPositions,
        selectedUnionPosition,
      ),
      regions: buildFilterOptionList(filterOptionsFromSummary.region ?? [], MEMBERSHIP_ADVANCED_FALLBACKS.regions, selectedRegion),
      genders: buildFilterOptionList(filterOptionsFromSummary.gender ?? [], MEMBERSHIP_ADVANCED_FALLBACKS.genders, selectedGender),
      ageGroups: buildFilterOptionList(
        [...(filterOptionsFromSummary.ageGroup ?? []), ...ageFromSummary],
        MEMBERSHIP_ADVANCED_FALLBACKS.ageGroups,
        selectedAgeGroup,
      ),
    };
  }, [
    summary,
    buildFilterOptionList,
    selectedAffiliation,
    selectedUnionName,
    selectedUnionPosition,
    selectedRegion,
    selectedGender,
    selectedAgeGroup,
  ]);

  const reportBuilderOptions = useMemo(() => {
    const builder = summary?.reportBuilder ?? null;

    const slugify = (input) => {
      if (typeof input !== 'string') {
        return '';
      }
      return input
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    };

    const fromList = (list, fallbackList) => {
      const collected = [];
      const seen = new Set();

      const append = (option) => {
        if (!option) {
          return;
        }
        const label = typeof option.label === 'string' ? option.label.trim() : '';
        if (!label) {
          return;
        }
        const description = typeof option.description === 'string' ? option.description.trim() : '';
        const rawValue = typeof option.value === 'string' ? option.value.trim() : '';
        const value = rawValue || slugify(label) || label;
        if (!value) {
          return;
        }
        if (shouldExcludeReportType(value, label)) {
          return;
        }
        const normalizedValue = value.toLowerCase();
        if (seen.has(normalizedValue)) {
          return;
        }
        seen.add(normalizedValue);
        const payload = { value, label };
        if (description) {
          payload.description = description;
        }
        collected.push(payload);
      };

      const appendRaw = (value, label, source = {}) => {
        const descriptor = typeof source.description === 'string' ? source.description : '';
        const rawLabel = typeof label === 'string' ? label : '';
        const rawValue = typeof value === 'string' ? value : '';
        const resolvedLabel = rawLabel.trim() || rawValue.trim();
        const resolvedValue = rawValue.trim() || resolvedLabel;
        if (shouldExcludeReportType(resolvedValue, resolvedLabel)) {
          return;
        }
        append({ value: resolvedValue, label: resolvedLabel, description: descriptor });
      };

      if (Array.isArray(list)) {
        list.forEach((entry) => {
          if (!entry) {
            return;
          }
          if (typeof entry === 'string') {
            appendRaw(entry, entry);
            return;
          }
          if (typeof entry === 'object') {
            const label = entry.label ?? entry.name ?? entry.title ?? entry.text ?? entry.display ?? entry.value ?? entry.code ?? entry.key;
            const value = entry.value ?? entry.code ?? entry.key ?? entry.slug ?? entry.id;
            appendRaw(value, label, entry);
          }
        });
      } else if (list && typeof list === 'object') {
        Object.entries(list).forEach(([key, value]) => {
          if (typeof value === 'string') {
            appendRaw(key, value);
          }
        });
      }

      if (!collected.length && Array.isArray(fallbackList)) {
        fallbackList.forEach((entry) => {
          if (!entry) {
            return;
          }
          if (typeof entry === 'string') {
            appendRaw(entry, entry);
            return;
          }
          appendRaw(entry.value, entry.label, entry);
        });
      }

      return collected;
    };

    const rawFormats = fromList(builder?.formats, DEFAULT_REPORT_BUILDER_OPTIONS.formats);
    const sanitizedFormats = rawFormats.filter((option) => {
      const rawValue = typeof option?.value === 'string' ? option.value.trim().toLowerCase() : '';
      return rawValue && rawValue !== 'dashboard';
    });

    const rawDateRanges = fromList(builder?.dateRanges, DEFAULT_REPORT_BUILDER_OPTIONS.dateRanges);
    const hasCustomRange = rawDateRanges.some((option) => option?.value === CUSTOM_DATE_RANGE_VALUE);
    const rawReportTypes = fromList(builder?.reportTypes, DEFAULT_REPORT_BUILDER_OPTIONS.reportTypes);

    const mergeWithFallback = (primary = [], fallback = []) => {
      const merged = [];
      const seen = new Set();
      [...primary, ...fallback].forEach((entry) => {
        if (!entry || typeof entry !== 'object') {
          return;
        }
        const value = typeof entry.value === 'string' ? entry.value.trim() : '';
        const label = typeof entry.label === 'string' ? entry.label.trim() : '';
        if (!value || !label) {
          return;
        }
        const key = value.toLowerCase();
        if (seen.has(key)) {
          return;
        }
        seen.add(key);
        merged.push({ value, label, ...(entry.description ? { description: entry.description } : {}) });
      });
      return merged;
    };

    return {
      reportTypes: mergeWithFallback(rawReportTypes, DEFAULT_REPORT_BUILDER_OPTIONS.reportTypes),
      dateRanges: hasCustomRange ? rawDateRanges : [...rawDateRanges, CUSTOM_DATE_RANGE_OPTION],
      filters: builderFilterDefinitions,
      formats: sanitizedFormats.length ? sanitizedFormats : DEFAULT_REPORT_BUILDER_OPTIONS.formats,
    };
  }, [builderFilterDefinitions, summary]);

  const scheduleBuilderLabelMaps = useMemo(() => {
    const mapOptions = (primary = [], fallback = []) => {
      const map = {};
      const append = (item) => {
        if (!item) {
          return;
        }
        const rawValue = typeof item.value === 'string' ? item.value.trim() : '';
        const label = typeof item.label === 'string' ? item.label.trim() : '';
        if (!rawValue || map[rawValue]) {
          return;
        }
        map[rawValue] = label || rawValue;
      };
      primary.forEach(append);
      fallback.forEach(append);
      return map;
    };

    const reportTypeMap = mapOptions(reportBuilderOptions.reportTypes, DEFAULT_REPORT_BUILDER_OPTIONS.reportTypes);
    Object.entries(LEGACY_REPORT_TYPE_LABELS).forEach(([key, label]) => {
      if (!reportTypeMap[key]) {
        reportTypeMap[key] = label;
      }
    });

    return {
      reportType: reportTypeMap,
      dateRange: mapOptions(reportBuilderOptions.dateRanges, DEFAULT_REPORT_BUILDER_OPTIONS.dateRanges),
      grouping: mapOptions(GROUPING_OPTIONS, GROUPING_OPTIONS),
      aggregation: mapOptions(AGGREGATION_OPTIONS, AGGREGATION_OPTIONS),
    };
  }, [reportBuilderOptions]);

  const scheduleFormDefaults = useMemo(() => ({
    ...DEFAULT_SCHEDULE_FORM,
    reportType: reportBuilderOptions.reportTypes[0]?.value ?? DEFAULT_REPORT_TYPE_VALUE,
    dateRange: reportBuilderOptions.dateRanges[0]?.value ?? DEFAULT_DATE_RANGE_VALUE,
    selectedColumns: (REPORT_TYPE_COLUMNS[reportBuilderOptions.reportTypes[0]?.value ?? DEFAULT_REPORT_TYPE_VALUE] ?? [])
      .slice(0, 4)
      .map((column) => column.value),
    reportConfig: {
      ...DEFAULT_SCHEDULE_FORM.reportConfig,
      reportType: reportBuilderOptions.reportTypes[0]?.value ?? DEFAULT_REPORT_TYPE_VALUE,
      dateRange: reportBuilderOptions.dateRanges[0]?.value ?? DEFAULT_DATE_RANGE_VALUE,
      selectedColumns: (REPORT_TYPE_COLUMNS[reportBuilderOptions.reportTypes[0]?.value ?? DEFAULT_REPORT_TYPE_VALUE] ?? [])
        .slice(0, 4)
        .map((column) => column.value),
    },
  }), [reportBuilderOptions]);

  const scheduleReportConfig = useMemo(() => {
    const rawConfig = scheduleForm.reportConfig && typeof scheduleForm.reportConfig === 'object' && !Array.isArray(scheduleForm.reportConfig)
      ? scheduleForm.reportConfig
      : {};

    return {
      reportType: rawConfig.reportType ?? scheduleForm.reportType ?? scheduleFormDefaults.reportType ?? DEFAULT_REPORT_TYPE_VALUE,
      dateRange: rawConfig.dateRange ?? scheduleForm.dateRange ?? scheduleFormDefaults.dateRange ?? DEFAULT_DATE_RANGE_VALUE,
      dateFrom: rawConfig.dateFrom ?? scheduleForm.dateFrom ?? '',
      dateTo: rawConfig.dateTo ?? scheduleForm.dateTo ?? '',
      filters: rawConfig.filters && typeof rawConfig.filters === 'object' && !Array.isArray(rawConfig.filters)
        ? rawConfig.filters
        : (scheduleForm.filters ?? {}),
      selectedColumns: Array.isArray(rawConfig.selectedColumns)
        ? rawConfig.selectedColumns
        : (Array.isArray(scheduleForm.selectedColumns) ? scheduleForm.selectedColumns : []),
      grouping: rawConfig.grouping ?? scheduleForm.grouping ?? 'none',
      aggregation: rawConfig.aggregation ?? scheduleForm.aggregation ?? 'none',
      format: normalizeScheduleFormatValue(rawConfig.format ?? scheduleForm.format, 'pdf'),
    };
  }, [scheduleForm, scheduleFormDefaults]);

  const scheduleAvailableColumns = useMemo(
    () => REPORT_TYPE_COLUMNS[scheduleReportConfig.reportType] ?? [],
    [scheduleReportConfig.reportType],
  );

  const scheduleSupportsDateFilter = useMemo(
    () => ['membership', 'events', 'client-bookings', 'revenue_bookings', 'revenue_ids', 'tickets', 'admin_access', 'security', 'admin_roles', 'system_activity', 'access_logs', 'system_usage'].includes(scheduleReportConfig.reportType),
    [scheduleReportConfig.reportType],
  );

  const scheduleIsCustomDateRange = useMemo(
    () => scheduleSupportsDateFilter && scheduleReportConfig.dateRange === CUSTOM_DATE_RANGE_VALUE,
    [scheduleSupportsDateFilter, scheduleReportConfig.dateRange],
  );

  const scheduleFilterDefinitions = useMemo(() => {
    const reportType = scheduleReportConfig.reportType;
    const typeColumns = Array.isArray(REPORT_TYPE_COLUMNS[reportType])
      ? REPORT_TYPE_COLUMNS[reportType]
      : [];
    const keysInOptions = new Set(Object.keys(scheduleFilterOptions).map((key) => normalizeFilterFieldKey(key)).filter(Boolean));
    const fromColumns = typeColumns
      .map((column) => normalizeFilterFieldKey(column?.value))
      .filter((key) => key && keysInOptions.has(key));
    const mergedKeys = Array.from(new Set([...fromColumns, ...Object.keys(scheduleFilterOptions).map((key) => normalizeFilterFieldKey(key)).filter(Boolean)]));

    return mergedKeys.map((key) => ({
      value: key,
      label: toFilterFieldLabel(key),
    }));
  }, [scheduleFilterOptions, scheduleReportConfig.reportType]);

  const scheduleSelectedColumnCount = useMemo(() => {
    const selected = new Set(Array.isArray(scheduleReportConfig.selectedColumns) ? scheduleReportConfig.selectedColumns : []);
    return scheduleAvailableColumns.reduce((count, column) => count + (selected.has(column.value) ? 1 : 0), 0);
  }, [scheduleAvailableColumns, scheduleReportConfig.selectedColumns]);

  const hasSelectedScheduleColumns = scheduleSelectedColumnCount > 0;
  const canSelectAllScheduleColumns = scheduleAvailableColumns.length > scheduleSelectedColumnCount;

  const hasActiveScheduleFilters = useMemo(() => {
    const hasOptionFilters = Object.values(scheduleReportConfig.filters ?? {})
      .some((value) => String(value ?? '').trim() !== '');
    return hasOptionFilters || Boolean(scheduleReportConfig.dateFrom) || Boolean(scheduleReportConfig.dateTo);
  }, [scheduleReportConfig.dateFrom, scheduleReportConfig.dateTo, scheduleReportConfig.filters]);

  useEffect(() => {
    loadFilterOptions(scheduleReportConfig.reportType, setScheduleFilterOptions);
  }, [loadFilterOptions, scheduleReportConfig.reportType]);

  const scheduleRecipients = useMemo(
    () => normalizeScheduleRecipients(scheduleForm.recipientsInput),
    [scheduleForm.recipientsInput],
  );

  const scheduleInvalidRecipients = useMemo(
    () => scheduleRecipients.filter((email) => !EMAIL_PATTERN.test(email)),
    [scheduleRecipients],
  );

  useEffect(() => {
    if (selectedCompany === 'all') {
      return;
    }
    if (!companyOptions.length) {
      setSelectedCompany('all');
      return;
    }
    const exists = companyOptions.some((name) => name === selectedCompany);
    if (!exists) {
      setSelectedCompany('all');
    }
  }, [companyOptions, selectedCompany]);

  useEffect(() => {
    setReportConfig((previous) => {
      let mutated = false;
      const nextSelections = { ...previous };

      REPORT_BUILDER_OPTION_ENTRIES.forEach(([selectionKey, optionKey]) => {
        const optionsList = reportBuilderOptions[optionKey] ?? [];
        const currentValue = previous[selectionKey];
        if (!optionsList.length) {
          if (currentValue) {
            nextSelections[selectionKey] = '';
            mutated = true;
          }
          return;
        }
        const hasMatch = optionsList.some((option) => option.value === currentValue);
        if (!hasMatch) {
          nextSelections[selectionKey] = optionsList[0].value;
          mutated = true;
        }
      });

      return mutated ? nextSelections : previous;
    });
  }, [reportBuilderOptions]);

  useEffect(() => {
    setReportBuilderError(null);
    setReportBuilderNotice(null);
  }, [
    reportConfig.reportType,
    reportConfig.dateRange,
    reportConfig.format,
  ]);

  const reportBuilderSelectionLabels = useMemo(() => {
    const resolveLabel = (value, options) => {
      if (!value) {
        return null;
      }
      const match = options.find((option) => option.value === value);
      return match ? match.label : null;
    };

    return {
      reportType: resolveLabel(reportConfig.reportType, reportBuilderOptions.reportTypes),
      dateRange: resolveLabel(reportConfig.dateRange, reportBuilderOptions.dateRanges),
      format: resolveLabel(reportConfig.format, reportBuilderOptions.formats),
    };
  }, [reportConfig, reportBuilderOptions]);

  const reportBuilderButtonHint = useMemo(() => {
    const parts = [];
    if (reportBuilderSelectionLabels.reportType) {
      parts.push(reportBuilderSelectionLabels.reportType);
    }
    if (reportBuilderSelectionLabels.dateRange) {
      parts.push(reportBuilderSelectionLabels.dateRange);
    }
    if (reportBuilderSelectionLabels.format) {
      parts.push(reportBuilderSelectionLabels.format);
    }
    if (!parts.length) {
      return 'Select report parameters to enable export.';
    }
    return parts.join(' • ');
  }, [reportBuilderSelectionLabels]);

  const canGenerateReport = useMemo(() => (
    REPORT_BUILDER_OPTION_ENTRIES.every(([selectionKey, optionKey]) => {
      const optionsList = reportBuilderOptions[optionKey] ?? [];
      if (!optionsList.length) {
        return false;
      }
      const value = reportConfig[selectionKey];
      return typeof value === 'string' && value.trim();
    })
      && Array.isArray(reportConfig.selectedColumns)
      && reportConfig.selectedColumns.length > 0
      && (!isCustomDateRangeSelected || (Boolean(reportConfig.dateFrom) && Boolean(reportConfig.dateTo) && reportConfig.dateFrom <= reportConfig.dateTo))
  ), [isCustomDateRangeSelected, reportBuilderOptions, reportConfig]);

  const newJoinersValue = useMemo(
    () => getJoinersValue(companySnapshot ?? summary?.members, timeRange),
    [companySnapshot, summary, timeRange],
  );

  const scopedMembers = companySnapshot ?? summary?.members;

  // helpers available to all hooks in this component
  const formatCount = (v) => (v === null || v === undefined ? '—' : Number(v).toLocaleString());
  const formatCurrency = (v) => new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP' }).format(Number(v ?? 0));
  const formatPercent = (v, digits = 1) => {
    if (v === null || v === undefined || Number.isNaN(Number(v))) {
      return '—';
    }
    const numeric = Number(v);
    const sign = numeric >= 0 ? '' : '-';
    return `${sign}${Math.abs(numeric).toFixed(digits)}%`;
  };

  const formatStatValue = (value, formatType = 'count') => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
      return '—';
    }
    switch (formatType) {
      case 'currency':
        return formatCurrency(value);
      case 'percent':
        return `${Number(value).toFixed(1)}%`;
      default:
        return formatCount(value);
    }
  };

  const toFiniteNumber = (value) => {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
  };

  const pickFirstFinite = (...values) => {
    const candidate = values
      .map((value) => toFiniteNumber(value))
      .find((value) => value != null);
    return candidate ?? null;
  };

  const clampValue = (value, min, max) => {
    if (value == null || Number.isNaN(value)) {
      return min;
    }
    return Math.min(Math.max(value, min), max);
  };

  const totalMembersValue = summary?.members?.total;

  const isCompanyScopedSelection = selectedCompany !== 'all';
  const membershipSummarySource = isCompanyScopedSelection ? companySummary : summary;
  const membershipDemographics = membershipSummarySource?.members?.demographics ?? null;
  const demographicsMeta = membershipDemographics?.meta ?? null;
  const unionPositionsMeta = demographicsMeta?.unionPositions ?? null;
  const tenureMeta = demographicsMeta?.tenure ?? null;
  const unionPositionsSource = membershipDemographics?.unionPositions;
  const tenurePrimarySource = membershipDemographics?.tenure;
  const tenureFallbackSource = membershipDemographics?.tenureBuckets;
  const isSummarySample = isCompanyScopedSelection
    ? Boolean(companySummary?.meta?.isSample)
    : Boolean(summaryMeta?.isSample);
  const suppressUnionPositions = unionPositionsMeta?.source === 'sample'
    || (!unionPositionsMeta && !isCompanyScopedSelection && isSummarySample);
  const suppressTenure = tenureMeta?.source === 'sample'
    || (!tenureMeta && !isCompanyScopedSelection && isSummarySample);

  const unionPositionStats = useMemo(() => {
    const records = suppressUnionPositions
      ? []
      : Array.isArray(unionPositionsSource)
      ? unionPositionsSource
      : [];
    if (!records.length) {
      return [];
    }
    const total = records.reduce((acc, record) => {
      const numeric = Number(record.value ?? record.count ?? record.total ?? 0);
      return acc + (Number.isFinite(numeric) && numeric > 0 ? numeric : 0);
    }, 0);
    if (!(total > 0)) {
      return [];
    }
    return records.map((record, index) => {
      const label = record.label ?? record.position ?? `Entry ${index + 1}`;
      const rawValue = Number(record.value ?? record.count ?? record.total ?? 0);
      const sanitized = Number.isFinite(rawValue) && rawValue > 0 ? rawValue : 0;
      const percent = total > 0 ? (sanitized / total) * 100 : 0;
      const width = percent > 0 ? Math.min(Math.max(percent, 6), 100) : 0;
      return {
        id: `union-${index}`,
        label,
        value: sanitized,
        formattedValue: formatCount(sanitized),
        percent,
        width,
      };
    }).filter((entry) => entry.value > 0);
  }, [unionPositionsSource, suppressUnionPositions, formatCount]);

  const unionChartData = useMemo(() => {
    if (!unionPositionStats.length) return null;
    return {
      labels: unionPositionStats.map(s => s.label),
      datasets: [{
        data: unionPositionStats.map(s => s.value),
        backgroundColor: pickBlueShades(unionPositionStats.length, PIE_COLORS),
        hoverBackgroundColor: pickBlueShades(unionPositionStats.length, PIE_HOVER),
        hoverOffset: 6,
        borderWidth: 2,
        borderColor: "#ffffff",
      }]
    };
  }, [unionPositionStats]);

  const tenureStats = useMemo(() => {
    const recordsBase = suppressTenure
      ? []
      : Array.isArray(tenurePrimarySource)
        ? tenurePrimarySource
        : Array.isArray(tenureFallbackSource)
          ? tenureFallbackSource
          : [];
    if (!recordsBase.length) {
      return [];
    }
    const total = recordsBase.reduce((acc, record) => {
      const numeric = Number(record.value ?? record.count ?? 0);
      return acc + (Number.isFinite(numeric) && numeric > 0 ? numeric : 0);
    }, 0);
    if (!(total > 0)) {
      return [];
    }
    return recordsBase.map((record, index) => {
      const label = record.label ?? `Bucket ${index + 1}`;
      const rawValue = Number(record.value ?? record.count ?? 0);
      const sanitized = Number.isFinite(rawValue) && rawValue > 0 ? rawValue : 0;
      const percent = total > 0 ? (sanitized / total) * 100 : 0;
      const width = percent > 0 ? Math.min(Math.max(percent, 6), 100) : 0;
      return {
        id: record.id ?? `tenure-${index}`,
        label,
        value: sanitized,
        formattedValue: formatCount(sanitized),
        percent,
        width,
      };
    }).filter((entry) => entry.value > 0);
  }, [tenurePrimarySource, tenureFallbackSource, suppressTenure, formatCount]);

  const tenureChartData = useMemo(() => {
    if (!tenureStats.length) return null;
    return {
      labels: tenureStats.map(s => s.label),
      datasets: [{
        label: 'Members',
        data: tenureStats.map(s => s.value),
        backgroundColor: pickBlueShades(tenureStats.length),
        borderRadius: 4,
        barPercentage: 0.6,
      }]
    };
  }, [tenureStats]);

  const membershipAnalytics = membershipSummarySource?.members?.analytics ?? null;
  const membershipAnalyticsMeta = membershipAnalytics?.meta ?? null;
  const membershipAnalyticsRangeLabel = membershipAnalyticsMeta?.range?.label ?? rangeShortLabels[timeRange] ?? "Selected range";
  const membershipAnalyticsLoading = isLoadingDashboardData || (isCompanyScopedSelection && isLoadingCompanySummary);
  const formatMembershipAnalyticsLabel = (value, fallback = "Unspecified") => {
    const normalized = typeof value === "string" ? value.trim() : "";
    if (!normalized) {
      return fallback;
    }
    return normalized.replace(/[_-]+/g, " ").replace(/\b\w/g, (character) => character.toUpperCase());
  };

  const membershipGrowthSeries = Array.isArray(membershipAnalytics?.growth)
    ? membershipAnalytics.growth
    : [];
  const membershipStatusSeries = Array.isArray(membershipAnalytics?.statusDistribution)
    ? membershipAnalytics.statusDistribution
    : [];
  const membershipAffiliationSeries = Array.isArray(membershipAnalytics?.affiliationDistribution)
    ? membershipAnalytics.affiliationDistribution
    : [];
  const membershipTopCompanySeries = Array.isArray(membershipAnalytics?.topCompanies)
    ? membershipAnalytics.topCompanies
    : [];

  const membershipGrowthChartData = useMemo(() => {
    if (!membershipGrowthSeries.length) return null;
    return {
      labels: membershipGrowthSeries.map((entry) => entry.label ?? entry.month ?? entry.date),
      datasets: [{
        label: "Registrations",
        data: membershipGrowthSeries.map((entry) => Number(entry.total ?? 0)),
        borderColor: ACCENT.blue,
        backgroundColor: ACCENT.blueSoft,
        pointBackgroundColor: ACCENT.blue,
        pointRadius: 4,
        pointHoverRadius: 6,
        tension: 0.35,
        fill: true,
      }],
    };
  }, [membershipGrowthSeries]);

  const membershipStatusChartData = useMemo(() => {
    if (!membershipStatusSeries.length) return null;
    const labels = membershipStatusSeries.map((entry) => formatMembershipAnalyticsLabel(entry.status, "Unspecified"));
    return {
      labels,
      datasets: [{
        data: membershipStatusSeries.map((entry) => Number(entry.total ?? 0)),
        backgroundColor: pickBlueShades(labels.length, PIE_COLORS),
        hoverBackgroundColor: pickBlueShades(labels.length, PIE_HOVER),
        hoverOffset: 6,
        borderWidth: 2,
        borderColor: "#ffffff",
      }],
    };
  }, [membershipStatusSeries, formatMembershipAnalyticsLabel]);

  const membershipAffiliationChartData = useMemo(() => {
    if (!membershipAffiliationSeries.length) return null;
    return {
      labels: membershipAffiliationSeries.map((entry) => entry.affiliation ?? "Unspecified"),
      datasets: [{
        data: membershipAffiliationSeries.map((entry) => Number(entry.total ?? 0)),
        backgroundColor: pickBlueShades(membershipAffiliationSeries.length, PIE_COLORS),
        hoverBackgroundColor: pickBlueShades(membershipAffiliationSeries.length, PIE_HOVER),
        hoverOffset: 6,
        borderWidth: 2,
        borderColor: "#ffffff",
      }],
    };
  }, [membershipAffiliationSeries]);

  const membershipTopCompaniesChartData = useMemo(() => {
    if (!membershipTopCompanySeries.length) return null;
    return {
      labels: membershipTopCompanySeries.map((entry) => entry.company ?? "Unspecified"),
      datasets: [{
        label: "Members",
        data: membershipTopCompanySeries.map((entry) => Number(entry.total ?? 0)),
        backgroundColor: pickBlueShades(membershipTopCompanySeries.length),
        hoverBackgroundColor: pickBlueShades(membershipTopCompanySeries.length, BLUE_SHADES_HOVER),
        borderRadius: 4,
        maxBarThickness: 28,
      }],
    };
  }, [membershipTopCompanySeries]);
  const membershipGrowthLineOptions = useMemo(
    () => buildAdaptiveLineOptions(
      lineOptionsHiddenLegend,
      membershipAnalyticsMeta?.groupBy === "month" ? "month" : "day",
    ),
    [membershipAnalyticsMeta?.groupBy],
  );

  const unionPositionsEmptyMessage = useMemo(() => {
    if (isCompanyScopedSelection) {
      if (isLoadingCompanySummary) {
        return 'Loading union position data...';
      }
      if (companySummaryError) {
        return 'Unable to load union position data for this company.';
      }
    }
    if (unionPositionsMeta?.error) {
      return 'Unable to load union position data.';
    }
    const scopedLabel = unionPositionsMeta?.filterCompany
      ?? (isCompanyScopedSelection ? selectedCompany : null);
    const rangeLabel = unionPositionsMeta?.rangeLabel ?? rangeShortLabels[timeRange] ?? null;
    const scopeDescriptor = (() => {
      if (scopedLabel && rangeLabel) {
        return `${scopedLabel} in the ${rangeLabel.toLowerCase()}`;
      }
      if (scopedLabel) {
        return scopedLabel;
      }
      if (rangeLabel) {
        return `the ${rangeLabel.toLowerCase()}`;
      }
      return null;
    })();
    if (suppressUnionPositions) {
      return scopeDescriptor
        ? `Waiting for live union position data for ${scopeDescriptor}.`
        : 'Waiting for live union position data.';
    }
    if (unionPositionsMeta?.source === 'empty') {
      return scopeDescriptor
        ? `No union position data recorded yet for ${scopeDescriptor}.`
        : 'No union position data recorded yet.';
    }
    return scopeDescriptor
      ? `No union position data yet for ${scopeDescriptor}.`
      : 'No union position data yet.';
  }, [companySummaryError, isCompanyScopedSelection, isLoadingCompanySummary, selectedCompany, suppressUnionPositions, unionPositionsMeta, timeRange]);

  const tenureEmptyMessage = useMemo(() => {
    if (isCompanyScopedSelection) {
      if (isLoadingCompanySummary) {
        return 'Loading tenure data...';
      }
      if (companySummaryError) {
        return 'Unable to load tenure data for this company.';
      }
    }
    if (tenureMeta?.error) {
      return 'Unable to load tenure distribution.';
    }
    const scopedLabel = tenureMeta?.filterCompany
      ?? (isCompanyScopedSelection ? selectedCompany : null);
    const rangeLabel = tenureMeta?.rangeLabel ?? rangeShortLabels[timeRange] ?? null;
    const scopeDescriptor = (() => {
      if (scopedLabel && rangeLabel) {
        return `${scopedLabel} in the ${rangeLabel.toLowerCase()}`;
      }
      if (scopedLabel) {
        return scopedLabel;
      }
      if (rangeLabel) {
        return `the ${rangeLabel.toLowerCase()}`;
      }
      return null;
    })();
    if (suppressTenure) {
      return scopeDescriptor
        ? `Waiting for live tenure data for ${scopeDescriptor}.`
        : 'Waiting for live tenure data.';
    }
    if (tenureMeta?.source === 'empty') {
      return scopeDescriptor
        ? `No tenure data recorded yet for ${scopeDescriptor}.`
        : 'No tenure data recorded yet.';
    }
    return scopeDescriptor
      ? `No tenure data yet for ${scopeDescriptor}.`
      : 'No tenure data yet.';
  }, [companySummaryError, isCompanyScopedSelection, isLoadingCompanySummary, selectedCompany, suppressTenure, tenureMeta, timeRange]);

  const growthTrend = overviewData?.membershipTrend ?? [];
  const overviewGrowthLineOptions = useMemo(
    () => buildAdaptiveLineOptions(lineOptions, overviewGroupBy),
    [overviewGroupBy],
  );
  const overviewActivityLineOptions = useMemo(
    () => buildAdaptiveLineOptions(lineOptionsHiddenLegend, overviewGroupBy),
    [overviewGroupBy],
  );
  const membershipTrendDescription = overviewGroupBy === "month"
    ? `Monthly cumulative members and new registrations for ${overviewRangeLabel}.`
    : `Daily cumulative members and new registrations for ${overviewRangeLabel}.`;
  const accessTrendDescription = overviewGroupBy === "month"
    ? `Monthly audit log activity over ${overviewRangeLabel}.`
    : `Daily audit log activity over ${overviewRangeLabel}.`;

  const growthChartData = useMemo(() => {
    if (!growthTrend.length) return null;
    return {
      labels: growthTrend.map(p => p.label ?? p.month),
      datasets: [
        {
          label: "Total Members",
          data: growthTrend.map(p => Number(p.totalMembers ?? 0)),
          borderColor: ACCENT.blueDeep,
          backgroundColor: "transparent",
          pointBackgroundColor: ACCENT.blueDeep,
          pointRadius: 4,
          pointHoverRadius: 6,
          tension: 0.35,
          fill: false,
        },
        {
          label: "New Registrations",
          data: growthTrend.map(p => Number(p.newRegistrations ?? p.newMembers ?? p.registrations ?? 0)),
          borderColor: ACCENT.blue,
          backgroundColor: ACCENT.blueSoft,
          pointBackgroundColor: ACCENT.blue,
          pointRadius: 4,
          pointHoverRadius: 6,
          tension: 0.35,
          fill: true,
        }
      ]
    };
  }, [growthTrend]);

  const engagementTrend = useMemo(() => {
    return Array.isArray(overviewData?.userEngagement)
      ? overviewData.userEngagement
      : [];
  }, [overviewData]);

  const engagementChartData = useMemo(() => {
    if (!engagementTrend.length) return null;
    return {
      labels: engagementTrend.map(p => p.label),
      datasets: [
        {
          label: "Users",
          data: engagementTrend.map(p => Number(p.users ?? 0)),
          borderColor: ACCENT.blue,
          backgroundColor: ACCENT.blueSoft,
          pointBackgroundColor: ACCENT.blue,
          pointRadius: 4,
          pointHoverRadius: 6,
          tension: 0.35,
          fill: true,
        }
      ]
    };
  }, [engagementTrend]);

  const customActivityTrend = useMemo(() => {
    return Array.isArray(overviewData?.appAccessTrend)
      ? overviewData.appAccessTrend.map((entry) => ({
          label: entry.label ?? entry.month ?? entry.date,
          requests: Number(entry.totalRequests ?? 0),
        }))
      : [];
  }, [overviewData]);

  const customActivityChartData = useMemo(() => {
    if (!customActivityTrend.length) return null;
    return {
      labels: customActivityTrend.map(p => p.label),
      datasets: [{
        label: "API Requests",
        data: customActivityTrend.map(p => p.requests),
        borderColor: ACCENT.blue,
        backgroundColor: ACCENT.blueSoft,
        pointBackgroundColor: ACCENT.blue,
        pointRadius: 4,
        pointHoverRadius: 6,
        tension: 0.35,
        fill: true,
      }]
    };
  }, [customActivityTrend]);

  const accessFunnelData = useMemo(() => {
    return Array.isArray(overviewData?.accessFunnel)
      ? overviewData.accessFunnel.map((entry) => ({
          id: entry.id,
          label: entry.label,
          count: Number(entry.count ?? 0),
        }))
      : [];
  }, [overviewData]);

  const accessFunnelChartData = useMemo(() => {
    const filtered = accessFunnelData.filter((entry) => Number(entry.count ?? 0) > 0);
    if (!filtered.length) return null;
    return {
      labels: filtered.map(d => d.label),
      datasets: [{
        label: "Interactions",
        data: filtered.map(d => d.count),
        backgroundColor: pickBlueShades(filtered.length),
        hoverBackgroundColor: pickBlueShades(filtered.length, BLUE_SHADES_HOVER),
        borderRadius: 4,
        maxBarThickness: 32,
      }]
    };
  }, [accessFunnelData]);

  const memberDistribution = useMemo(() => {
    const entries = overviewData?.memberDistribution ?? [];
    if (!entries.length) return { segments: [], total: 0 };
    const total = entries.reduce((acc, item) => acc + Number(item.count ?? 0), 0);
    return {
      segments: entries.map(item => ({ label: item.label, value: Number(item.count ?? 0) })),
      total
    };
  }, [overviewData]);

  const distributionForDisplay = useMemo(
    () => memberDistribution,
    [memberDistribution],
  );

  const topCompaniesList = useMemo(() => {
    return Array.isArray(overviewData?.topCompanies)
      ? overviewData.topCompanies
          .map((entry) => ({
            company: entry.company ?? "Unspecified",
            value: Number(entry.totalMembers ?? 0),
          }))
          .filter((entry) => entry.value > 0)
      : [];
  }, [overviewData]);

  const topCompaniesChartData = useMemo(() => {
    if (!topCompaniesList.length) return null;
    return {
      labels: topCompaniesList.map(c => c.company || 'Unspecified'),
      datasets: [
        {
          label: 'Total Members',
          data: topCompaniesList.map(c => c.value),
          backgroundColor: pickBlueShades(topCompaniesList.length),
          hoverBackgroundColor: pickBlueShades(topCompaniesList.length, BLUE_SHADES_HOVER),
          borderRadius: 4,
          barPercentage: 0.6,
        }
      ]
    };
  }, [topCompaniesList]);

  const distributionChartData = useMemo(() => {
    const segments = distributionForDisplay.segments ?? [];
    if (!segments.length) return null;
    return {
      labels: segments.map(s => s.label),
      datasets: [{
        data: segments.map(s => s.value),
        backgroundColor: pickBlueShades(segments.length, PIE_COLORS),
        hoverBackgroundColor: pickBlueShades(segments.length, PIE_HOVER),
        hoverOffset: 6,
        borderWidth: 2,
        borderColor: "#ffffff",
      }]
    };
  }, [distributionForDisplay]);

  useEffect(() => {
    setScheduleActionError(null);
  }, [scheduleResults]);

  const performanceHighlights = useMemo(() => {
    const toHighlights = (items) => (items ?? []).map((item) => ({
      label: item.label,
      value: item.value,
      format: item.format ?? 'count',
      meta: item.meta ?? null,
    }));

    return {
      membership: toHighlights(summary?.performance?.membership),
      operations: toHighlights(summary?.performance?.operations),
    };
  }, [summary]);

  const reportSchedules = useMemo(() => {
    if (!Array.isArray(scheduleResults)) {
      return [];
    }

    const normalized = scheduleResults
      .map((entry, index) => normalizeScheduleEntry(entry, `schedule-${index}`))
      .filter(Boolean);

    normalized.sort((a, b) => {
      const aTime = a.nextRun ? Date.parse(a.nextRun) : Number.POSITIVE_INFINITY;
      const bTime = b.nextRun ? Date.parse(b.nextRun) : Number.POSITIVE_INFINITY;
      return aTime - bTime;
    });

    return normalized;
  }, [scheduleResults]);

  const editingSchedule = useMemo(
    () => reportSchedules.find((schedule) => schedule.id === editingScheduleId) ?? null,
    [reportSchedules, editingScheduleId],
  );

  useEffect(() => {
    if (editingScheduleId && !editingSchedule) {
      setEditingScheduleId(null);
      setScheduleForm({ ...scheduleFormDefaults });
      setScheduleFormError(null);
    }
  }, [editingScheduleId, editingSchedule, scheduleFormDefaults]);

  useEffect(() => {
    if (editingScheduleId || isCreatingSchedule) {
      return;
    }
    setScheduleForm({ ...scheduleFormDefaults });
  }, [editingScheduleId, isCreatingSchedule, scheduleFormDefaults]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const res = await api.getReportsSummary(summaryRequestParams);
        if (!mounted) return;
        setSummary(res.data);
      } catch (err) {
        console.error('Unable to load reports summary', err);
      }
    };
    load();
    return () => { mounted = false; };
  }, [summaryRequestParams]);

  useEffect(() => {
    if (overviewRangeValidationError) {
      setOverviewSummary(null);
      setOverviewError(null);
      setIsLoadingOverview(false);
      return undefined;
    }

    let mounted = true;
    const loadOverview = async () => {
      setIsLoadingOverview(true);
      try {
        const res = await api.getReportsSummary(overviewRequestParams);
        if (!mounted) return;
        setOverviewSummary(res.data ?? null);
        setOverviewError(null);
      } catch (err) {
        if (!mounted) return;
        if (err?.response?.status === 404) {
          setOverviewSummary(null);
          setOverviewError(null);
        } else {
          console.error("Failed to load analytics overview", err);
          setOverviewSummary(null);
          setOverviewError("Failed to load analytics overview.");
        }
      } finally {
        if (mounted) {
          setIsLoadingOverview(false);
        }
      }
    };

    loadOverview();
    return () => { mounted = false; };
  }, [overviewRequestParams, overviewRangeValidationError]);

  useEffect(() => {
    let mounted = true;
    const loadDashboard = async () => {
      setIsLoadingDashboardData(true);
      try {
        const res = await api.getDashboard();
        if (!mounted) return;
        setDashboardKpis(res.data);
      } catch (err) {
        console.error('Unable to load dashboard KPIs', err);
      } finally {
        if (mounted) {
          setIsLoadingDashboardData(false);
        }
      }
    };
    loadDashboard();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (selectedCompany === 'all') {
      setCompanySummary(null);
      setCompanySummaryError(null);
      setIsLoadingCompanySummary(false);
      return;
    }

    const cacheKey = `${selectedCompany.toLowerCase()}|${timeRange}|${selectedAffiliation}|${selectedUnionName}|${selectedUnionPosition}|${selectedRegion}|${selectedGender}|${selectedAgeGroup}`;
    const cached = companySummaryCacheRef.current.get(cacheKey);
    if (cached) {
      setCompanySummary(cached);
      setCompanySummaryError(null);
      setIsLoadingCompanySummary(false);
      return;
    }

    let cancelled = false;
    setIsLoadingCompanySummary(true);
    setCompanySummaryError(null);
    setCompanySummary(null);

    const loadCompanySummary = async () => {
      try {
        const response = await api.getReportsSummary(summaryRequestParams);
        if (cancelled) {
          return;
        }
        companySummaryCacheRef.current.set(cacheKey, response.data);
        setCompanySummary(response.data);
        setCompanySummaryError(null);
      } catch (error) {
        console.error('Unable to load company-specific analytics', error);
        if (!cancelled) {
          setCompanySummary(null);
          setCompanySummaryError('Unable to load company-specific analytics right now.');
        }
      } finally {
        if (!cancelled) {
          setIsLoadingCompanySummary(false);
        }
      }
    };

    loadCompanySummary();

    return () => {
      cancelled = true;
    };
  }, [
    selectedCompany,
    timeRange,
    selectedAffiliation,
    selectedUnionName,
    selectedUnionPosition,
    selectedRegion,
    selectedGender,
    selectedAgeGroup,
    summaryRequestParams,
  ]);

  const handleTimeRangeChange = (event) => {
    const value = event.target.value;
    if (rangeShortLabels[value]) {
      setTimeRange(value);
    }
  };

  const handleCompanyChange = (event) => {
    const value = event.target.value;
    if (value === 'all' || companyOptions.includes(value)) {
      setSelectedCompany(value);
    }
  };

  const handleOverviewFilterChange = (event) => {
    const { name, value } = event.target;
    if (!name) {
      return;
    }

    setOverviewFilters((previous) => {
      const next = {
        ...previous,
        [name]: value,
      };

      if (name === 'status') {
        const normalizedStatus = typeof value === 'string' ? value.trim().toLowerCase() : '';
        next.status = MEMBERSHIP_STATUS_FILTER_SET.has(normalizedStatus) ? normalizedStatus : '';
      }

      if (name === "dateRange" && value !== "custom") {
        next.dateFrom = "";
        next.dateTo = "";
      }

      return next;
    });
  };

  const handleClearOverviewFilters = () => {
    setOverviewFilters({ ...DEFAULT_OVERVIEW_FILTERS });
    setOverviewError(null);
  };

  const [isRefreshing, setIsRefreshing] = useState(false);

  const refreshExportHistory = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setIsLoadingExportHistory(true);
    }
    try {
      const response = await api.getReportExportHistory();
      setExportHistory(response.data?.results ?? []);
      setExportHistoryError(null);
      setHistoryActionError(null);
    } catch (error) {
      console.error('Unable to load export history', error);
      setExportHistoryError('Unable to load export history right now. Please try again later.');
    } finally {
      if (!silent) {
        setIsLoadingExportHistory(false);
      }
    }
  }, []);

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    setIsLoadingDashboardData(true);
    setIsLoadingOverview(true);
    try {
      const requests = [
        api.getReportsSummary(summaryRequestParams),
        api.getDashboard(),
      ];

      if (!overviewRangeValidationError) {
        requests.push(api.getReportsSummary(overviewRequestParams));
      }

      const [summaryResponse, dashboardResponse, overviewResponse] = await Promise.all(requests);
      setSummary(summaryResponse.data);
      setDashboardKpis(dashboardResponse.data);
      if (overviewResponse) {
        setOverviewSummary(overviewResponse.data ?? null);
        setOverviewError(null);
      }

      if (selectedCompany !== 'all') {
        const companyResponse = await api.getReportsSummary(summaryRequestParams);
        const cacheKey = `${selectedCompany.toLowerCase()}|${timeRange}|${selectedAffiliation}|${selectedUnionName}|${selectedUnionPosition}|${selectedRegion}|${selectedGender}|${selectedAgeGroup}`;
        companySummaryCacheRef.current.set(cacheKey, companyResponse.data);
        setCompanySummary(companyResponse.data);
        setCompanySummaryError(null);
      } else {
        setCompanySummary(null);
        setCompanySummaryError(null);
      }

      await refreshExportHistory({ silent: true });
    } catch (error) {
      console.error('Unable to refresh reports and analytics', error);
    } finally {
      setIsRefreshing(false);
      setIsLoadingDashboardData(false);
      setIsLoadingOverview(false);
    }
  }, [
    overviewRangeValidationError,
    overviewRequestParams,
    refreshExportHistory,
    selectedCompany,
    timeRange,
    selectedAffiliation,
    selectedUnionName,
    selectedUnionPosition,
    selectedRegion,
    selectedGender,
    selectedAgeGroup,
    summaryRequestParams,
  ]);

  useEffect(() => {
    refreshExportHistory();
  }, [refreshExportHistory]);

  const fetchLifecycleData = useCallback(async () => {
    setIsLoadingLifecycle(true);
    setLifecycleError(null);
    try {
      const res = await api.getDataLifecycleOverview();
      setLifecycleData(res.data ?? null);
    } catch (err) {
      setLifecycleError(err?.response?.data?.message ?? "Unable to load lifecycle analytics.");
    } finally {
      setIsLoadingLifecycle(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === "lifecycle" && !lifecycleData && !isLoadingLifecycle) {
      fetchLifecycleData();
    }
  }, [activeTab, lifecycleData, isLoadingLifecycle, fetchLifecycleData]);

  const handleArchive = useCallback(async (table, olderThanDays) => {
    setArchivingTable(table);
    setArchiveResult(null);
    setArchiveError(null);
    try {
      const res = await api.archiveLifecycleTable({ table, olderThanDays });
      setArchiveResult(res.data ?? {});
      await fetchLifecycleData();
    } catch (err) {
      setArchiveError(err?.response?.data?.message ?? "Archive operation failed.");
    } finally {
      setArchivingTable(null);
      setArchiveConfirm(null);
    }
  }, [fetchLifecycleData]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest?.(".admin-row-actions")) {
        setHistoryMenuOpenId(null);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const toggleHistoryMenu = (rowId, event) => {
    const target = event?.currentTarget;
    if (!target) {
      setHistoryMenuPlacement("down");
      setHistoryMenuOpenId((current) => (current === rowId ? null : rowId));
      return;
    }
    const rect = target.getBoundingClientRect();
    const menuWidth = 190;
    const menuHeight = 120;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const fitsBelow = rect.bottom + menuHeight <= viewportHeight - 12;
    const left = Math.max(8, Math.min(rect.right - menuWidth, viewportWidth - menuWidth - 8));
    const topRaw = fitsBelow ? rect.bottom + 8 : rect.top - menuHeight - 8;
    const top = Math.max(8, Math.min(topRaw, viewportHeight - menuHeight - 8));
    const placement = top === rect.bottom + 8 || topRaw === rect.bottom + 8 ? "down" : "up";
    setHistoryMenuCoords({ top, left });
    setHistoryMenuPlacement(placement);
    setHistoryMenuOpenId((current) => (current === rowId ? null : rowId));
  };

  useEffect(() => {
    if (!historyMenuOpenId) return undefined;
    const handleClose = () => setHistoryMenuOpenId(null);
    window.addEventListener("scroll", handleClose, true);
    window.addEventListener("resize", handleClose, true);
    return () => {
      window.removeEventListener("scroll", handleClose, true);
      window.removeEventListener("resize", handleClose, true);
    };
  }, [historyMenuOpenId]);

  const fetchBuilderReportOutput = async (params, { mode = 'export' } = {}) => {
    const normalizedMode = mode === 'preview' ? 'preview' : 'export';
    const requestPayload = {
      ...(params && typeof params === 'object' ? params : {}),
      mode: normalizedMode,
    };

    const response = await api.exportBuilderReport(requestPayload);
    const headerFormat = response.headers?.['x-report-format'] ?? requestPayload.format;
    const fallbackHeader = response.headers?.['x-report-format-fallback'] ?? null;
    const deliveredHeader = response.headers?.['x-report-delivered-format'] ?? null;

    const requestedFormat = (headerFormat ?? requestPayload.format ?? 'csv')
      .toString()
      .toLowerCase();
    const deliveredFormat = (deliveredHeader ?? fallbackHeader ?? headerFormat ?? requestPayload.format ?? 'csv')
      .toString()
      .toLowerCase();

    const formatContentTypes = {
      pdf: 'application/pdf',
      excel: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      csv: 'text/csv',
    };

    const headerContentType = response.headers?.['x-file-content-type']
      ?? response.headers?.['content-type']
      ?? '';
    const normalizedHeaderContentType = typeof headerContentType === 'string'
      ? headerContentType.split(';')[0].trim().toLowerCase()
      : '';

    const contentType = (
      normalizedHeaderContentType
      && normalizedHeaderContentType !== 'application/octet-stream'
    )
      ? normalizedHeaderContentType
      : (
        formatContentTypes[deliveredFormat]
        ?? formatContentTypes[requestedFormat]
        ?? 'text/csv'
      );

    const responseBlob = response.data instanceof Blob
      ? response.data
      : new Blob([response.data]);

    const blob = new Blob([responseBlob], { type: contentType });

    if (!blob.size) {
      throw new Error('Generated report file is empty.');
    }

    if (deliveredFormat === 'pdf') {
      const signature = await blob.slice(0, 5).text();
      if (signature !== '%PDF-') {
        throw new Error('Generated file is not a valid PDF document.');
      }
    }

    const fallbackExtensionMap = {
      pdf: 'pdf',
      excel: 'xlsx',
      csv: 'csv',
      dashboard: 'csv',
    };

    const disposition = response.headers?.['content-disposition'];
    const fallbackExtension = fallbackExtensionMap[deliveredFormat]
      ?? fallbackExtensionMap[requestedFormat]
      ?? 'csv';
    const fallbackNameBase = [requestPayload.reportType ?? requestPayload.type, requestPayload.range, requestPayload.filter]
      .filter(Boolean)
      .map(toSlug)
      .join('_')
      || 'membership-report';
    const fallbackName = `${fallbackNameBase}-${Date.now()}.${fallbackExtension}`;
    const filename = extractFilenameFromDisposition(disposition, fallbackName);
    const rawWarningsHeader = response.headers?.['x-report-warnings'] ?? '';
    const warnings = typeof rawWarningsHeader === 'string' && rawWarningsHeader
      ? rawWarningsHeader
          .split('||')
          .map((part) => {
            try {
              return decodeURIComponent(part);
            } catch (error) {
              console.error('Unable to decode report warning header entry', error);
              return part;
            }
          })
          .map((item) => item.trim())
          .filter(Boolean)
      : [];

    return {
      blob,
      requestedFormat,
      deliveredFormat,
      fallbackFormat: fallbackHeader ? fallbackHeader.toString().toLowerCase() : null,
      reportSource: response.headers?.['x-report-source'] ?? null,
      fileName: filename,
      contentType,
      warnings,
      mode: normalizedMode,
    };
  };

  const executeBuilderReportDownload = async (params, { mode = 'export', autoDownload = true } = {}) => {
    const normalizedMode = mode === 'preview' ? 'preview' : 'export';
    const result = await fetchBuilderReportOutput(params, { mode: normalizedMode });

    if (normalizedMode === 'export' && autoDownload) {
      const downloadUrl = window.URL.createObjectURL(result.blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = result.fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => {
        window.URL.revokeObjectURL(downloadUrl);
      }, 1500);
    }

    return {
      requestedFormat: result.requestedFormat,
      deliveredFormat: result.deliveredFormat,
      fallbackFormat: result.fallbackFormat,
      reportSource: result.reportSource,
      fileName: result.fileName,
      warnings: result.warnings,
      mode: normalizedMode,
    };
  };

  const fetchSchedulesPage = useCallback(async (pageValue = 1, { silent = false } = {}) => {
    const targetPage = Math.max(1, Number.parseInt(pageValue, 10) || 1);
    if (!silent) {
      setIsLoadingSchedules(true);
    }
    try {
      const response = await api.listReportSchedules({ page: targetPage, limit: SCHEDULE_PAGE_SIZE });
      const payload = response?.data ?? {};
      const list = Array.isArray(payload.data)
        ? payload.data
        : (Array.isArray(payload.results) ? payload.results : []);
      const totalCount = Number.isFinite(Number(payload.total))
        ? Number(payload.total)
        : list.length;
      const totalPageCount = Number.isFinite(Number(payload.totalPages))
        ? Math.max(1, Number(payload.totalPages))
        : Math.max(1, Math.ceil(totalCount / SCHEDULE_PAGE_SIZE));
      const resolvedPage = Number.isFinite(Number(payload.page))
        ? Math.min(Math.max(1, Number(payload.page)), totalPageCount)
        : Math.min(targetPage, totalPageCount);

      setScheduleResults(list);
      setScheduleTotal(totalCount);
      setScheduleTotalPages(totalPageCount);
      if (resolvedPage !== schedulePage) {
        setSchedulePage(resolvedPage);
      }
      return list;
    } catch (error) {
      console.error('Unable to load scheduled reports', error);
      setScheduleActionError('Unable to load scheduled reports right now. Please try again.');
      return [];
    } finally {
      if (!silent) {
        setIsLoadingSchedules(false);
      }
    }
  }, [SCHEDULE_PAGE_SIZE, schedulePage]);

  useEffect(() => {
    fetchSchedulesPage(schedulePage);
  }, [fetchSchedulesPage, schedulePage]);

  // Seed the schedule run tracker with initial data so we don't auto-download
  // reports that already ran before the admin opened the page.
  useEffect(() => {
    if (!reportSchedules.length) return;
    const ref = scheduleRunsRef.current;
    if (ref.has('__seeded__')) return;
    reportSchedules.forEach((s) => {
      if (!s.isBlank && s.id && s.lastRun) {
        ref.set(s.id, s.lastRun);
      }
    });
    ref.set('__seeded__', true);
  }, [reportSchedules]);

  // Scheduled reports are executed by the backend server-side runner.
  // This effect periodically refreshes the schedule list and export history.
  // Downloads are user-initiated only (no automatic file downloads on poll).
  useEffect(() => {
    const refreshScheduleData = async () => {
      try {
        const newSchedules = await fetchSchedulesPage(schedulePage, { silent: true });

        // Update the ref with current lastRun values
        const ref = scheduleRunsRef.current;
        for (const raw of newSchedules) {
          if (!raw) continue;
          const id = raw.id ?? raw.scheduleId;
          if (id && raw.lastRun) {
            ref.set(id, raw.lastRun);
          }
        }
      } catch (refreshError) {
        console.error('Unable to refresh report summary after schedule poll', refreshError);
      }
      refreshExportHistory({ silent: true });
    };

    const intervalId = setInterval(refreshScheduleData, 30 * 1000);
    return () => clearInterval(intervalId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchSchedulesPage, refreshExportHistory, schedulePage]);

  const handleReportBuilderSelectChange = (event) => {
    const { name, value } = event.target;
    if (!name || !(name in REPORT_BUILDER_OPTION_MAP)) {
      return;
    }
    setReportConfig((previous) => {
      if (previous[name] === value) {
        return previous;
      }
      const next = { ...previous, [name]: value };
      if (name === 'dateRange' && value !== CUSTOM_DATE_RANGE_VALUE) {
        next.dateFrom = '';
        next.dateTo = '';
      }
      return next;
    });
  };

  const handleCustomReportFilterChange = (event) => {
    const { name, value } = event.target;
    if (!name) {
      return;
    }
    setReportConfig((previous) => {
      const next = { ...previous };
      if (name === 'dateFrom' || name === 'dateTo') {
        next[name] = value;
        if (value && previous.dateRange !== CUSTOM_DATE_RANGE_VALUE) {
          next.dateRange = CUSTOM_DATE_RANGE_VALUE;
        }
        return next;
      }
      next.filters = {
        ...(previous.filters ?? {}),
        [name]: value,
      };
      return next;
    });
  };

  const handleClearBuilderFilters = () => {
    setReportConfig((previous) => ({
      ...previous,
      filters: {},
      dateFrom: '',
      dateTo: '',
    }));
  };

  const handleClearScheduleFilters = () => {
    setScheduleForm((previous) => ({
      ...previous,
      filters: {},
      dateFrom: '',
      dateTo: '',
      reportConfig: {
        ...(previous.reportConfig ?? {}),
        filters: {},
        dateFrom: '',
        dateTo: '',
      },
    }));
  };

  const handleReportBuilderGenerate = async () => {
    if (!canGenerateReport || isGeneratingCustomReport) {
      return;
    }

    if (!Array.isArray(reportConfig.selectedColumns) || reportConfig.selectedColumns.length === 0) {
      setReportBuilderError('Select at least one column before generating the report.');
      return;
    }

    if (
      reportConfig.dateFrom
      && reportConfig.dateTo
      && reportConfig.dateFrom > reportConfig.dateTo
    ) {
      setReportBuilderError('Date From cannot be later than Date To.');
      return;
    }

    if (isCustomDateRangeSelected && (!reportConfig.dateFrom || !reportConfig.dateTo)) {
      setReportBuilderError('Set both Date From and Date To when using Custom Range.');
      return;
    }

    const payloadFilters = {
      ...(reportConfig.filters || {}),
      dateFrom: isCustomDateRangeSelected ? (reportConfig.dateFrom || null) : null,
      dateTo: isCustomDateRangeSelected ? (reportConfig.dateTo || null) : null,
    };

    const params = {
      reportType: reportConfig.reportType,
      range: reportConfig.dateRange,
      format: reportConfig.format,
      selectedColumns: reportConfig.selectedColumns,
      groupBy: reportConfig.grouping,
      aggregation: reportConfig.aggregation,
      filters: payloadFilters,
    };

    setReportBuilderError(null);
    setReportBuilderNotice(null);

    try {
      setIsGeneratingCustomReport(true);
      const result = await executeBuilderReportDownload(params, { mode: 'export', autoDownload: true });

      const summaryParts = [];
      const selectionSummary = [
        reportBuilderSelectionLabels.reportType,
        reportBuilderSelectionLabels.dateRange,
      ].filter(Boolean).join(' • ');
      if (selectionSummary) {
        summaryParts.push(selectionSummary);
      } else {
        summaryParts.push('Report generated');
      }

      if (result.reportSource === 'database') {
        summaryParts.push('using live data');
      }

      if (result.fallbackFormat && result.fallbackFormat !== result.requestedFormat) {
        summaryParts.push(`delivered as ${String(result.deliveredFormat).toUpperCase()} (requested ${String(result.requestedFormat).toUpperCase()})`);
      } else if (result.deliveredFormat) {
        summaryParts.push(`format ${String(result.deliveredFormat).toUpperCase()}`);
      }

      if (Array.isArray(result.warnings) && result.warnings.length) {
        summaryParts.push(...result.warnings);
      }

      setReportBuilderNotice(summaryParts.join(' • '));
      await refreshExportHistory({ silent: true });
    } catch (error) {
      console.error('Unable to generate custom report', error);
      let fallbackMessage = 'Unable to generate the report right now. Please try again.';
      if (error?.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const json = JSON.parse(text);
          fallbackMessage = json.error ?? json.message ?? fallbackMessage;
        } catch (parseError) {
          console.error('Unable to parse builder export error response', parseError);
        }
      } else if (error?.response?.data?.message) {
        fallbackMessage = error.response.data.message;
      } else if (typeof error?.message === 'string' && error.message.trim()) {
        fallbackMessage = error.message;
      }
      setReportBuilderError(fallbackMessage);
    } finally {
      setIsGeneratingCustomReport(false);
    }
  };

  const exportHistoryRows = useMemo(() => {
    if (!Array.isArray(exportHistory)) {
      return [];
    }
    return exportHistory.map((entry) => {
      const deliveredFormat = String(entry.deliveredFormat ?? entry.requestedFormat ?? 'csv').trim().toLowerCase();
      const isAvailable = typeof entry.isAvailable === 'boolean'
        ? entry.isAvailable
        : (deliveredFormat === 'pdf' || Boolean(entry.downloadUrl));
      const formatLabel = formatExportFormatLabel(entry.deliveredFormat ?? entry.requestedFormat);
      const metaParts = [formatLabel];
      if (entry.rangeLabel) {
        metaParts.push(entry.rangeLabel);
      }
      if (entry.groupByLabel || entry.filterLabel) {
        metaParts.push(entry.groupByLabel ?? entry.filterLabel);
      }
      if (entry.notes) {
        metaParts.push(entry.notes);
      }

      const params = entry.params && typeof entry.params === 'object'
        ? {
            ...entry.params,
            reportType: entry.params.reportType ?? entry.params.type ?? entry.reportType ?? 'membership',
            range: entry.params.range ?? entry.rangeKey,
            format: entry.params.format ?? entry.requestedFormat ?? 'csv',
            filters: entry.params.filters && typeof entry.params.filters === 'object' && !Array.isArray(entry.params.filters)
              ? entry.params.filters
              : {},
            selectedColumns: Array.isArray(entry.params.selectedColumns)
              ? entry.params.selectedColumns
              : [],
            groupBy: entry.params.groupBy ?? entry.params.grouping ?? 'none',
            aggregation: entry.params.aggregation ?? 'none',
          }
        : {
            reportType: entry.reportType ?? 'membership',
            range: entry.rangeKey,
            format: entry.requestedFormat ?? 'csv',
            filters: {},
            selectedColumns: [],
            groupBy: 'none',
            aggregation: 'none',
          };

      return {
        id: entry.id,
        title: entry.reportName ?? 'Membership Report',
        metaText: metaParts.filter(Boolean).join(' • '),
        dateDisplay: formatHistoryDate(entry.requestedAt),
        sizeDisplay: formatFileSize(entry.fileSizeBytes),
        statusLabel: formatExportStatus(isAvailable ? (entry.status ?? 'completed') : 'unavailable'),
        deliveredFormat,
        isAvailable,
        fileName: entry.fileName ?? null,
        downloadUrl: entry.downloadUrl ?? null,
        downloadApiUrl: entry.id ? `/api/admin/reports/download/${encodeURIComponent(entry.id)}` : null,
        params,
      };
    });
  }, [exportHistory]);

  useEffect(() => {
    setHistoryPage(1);
  }, [exportHistoryRows.length]);

  const historyTotalPages = Math.max(1, Math.ceil(exportHistoryRows.length / HISTORY_PAGE_SIZE));
  const historyStart = (historyPage - 1) * HISTORY_PAGE_SIZE;
  const historyEnd = Math.min(historyStart + HISTORY_PAGE_SIZE, exportHistoryRows.length);
  const paginatedHistoryRows = exportHistoryRows.slice(historyStart, historyEnd);

  const historyPageNumbers = useMemo(() => {
    const pages = [];
    const maxVisible = 7;
    if (historyTotalPages <= maxVisible) {
      for (let i = 1; i <= historyTotalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      const start = Math.max(2, historyPage - 1);
      const end = Math.min(historyTotalPages - 1, historyPage + 1);
      if (start > 2) pages.push('...');
      for (let i = start; i <= end; i++) pages.push(i);
      if (end < historyTotalPages - 1) pages.push('...');
      pages.push(historyTotalPages);
    }
    return pages;
  }, [historyPage, historyTotalPages]);

  const handleHistoryDownload = async (historyEntry) => {
    const deliveredFormat = String(historyEntry?.deliveredFormat ?? '').trim().toLowerCase();
    const formatExtensions = {
      pdf: 'pdf',
      excel: 'xlsx',
      csv: 'csv',
      dashboard: 'csv',
    };

    const fallbackExtension = formatExtensions[deliveredFormat] ?? 'csv';
    const fallbackFileName = historyEntry?.fileName
      ?? `report-history-${historyEntry?.id ?? Date.now()}.${fallbackExtension}`;
    const resolvedDownloadUrl = typeof historyEntry?.downloadUrl === 'string'
      ? historyEntry.downloadUrl.trim()
      : '';
    const isApiDownloadUrl = resolvedDownloadUrl.startsWith('/api/admin/reports/download/');

    try {
      if (resolvedDownloadUrl && !isApiDownloadUrl) {
        window.open(resolvedDownloadUrl, '_blank', 'noopener,noreferrer');
        return;
      }

      if (historyEntry?.id) {
        const response = await api.downloadReportExportHistory(historyEntry.id);
        const contentDisposition = response?.headers?.['content-disposition'] ?? null;
        const contentType = response?.headers?.['content-type'] ?? 'application/octet-stream';
        const responseBlob = response?.data instanceof Blob
          ? response.data
          : new Blob([response?.data], { type: contentType });

        if (!responseBlob.size) {
          throw new Error('Downloaded report file is empty.');
        }

        const fileName = extractFilenameFromDisposition(contentDisposition, fallbackFileName);
        const objectUrl = window.URL.createObjectURL(responseBlob);
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.setTimeout(() => {
          window.URL.revokeObjectURL(objectUrl);
        }, 1500);
        return;
      }

      alert('File not available for download');
    } catch (error) {
      console.error('Unable to download export history file', error);
      alert('Unable to download this report right now. Please try again.');
    }
  };

  const handleHistoryDelete = useCallback(async (historyEntry) => {
    if (!historyEntry?.id) {
      return;
    }
    try {
      setHistoryActionError(null);
      setHistoryDeleteId(historyEntry.id);
      await api.deleteReportExportHistory(historyEntry.id);
      await refreshExportHistory({ silent: true });
    } catch (error) {
      console.error('Unable to delete export history entry', error);
      const fallbackMessage = error?.response?.data?.message ?? 'Unable to delete that record right now. Please try again.';
      setHistoryActionError(fallbackMessage);
    } finally {
      setHistoryDeleteId(null);
    }
  }, [refreshExportHistory]);

  const handleDeleteAllHistory = useCallback(async () => {
    if (isDeletingAllHistory) return;
    try {
      setHistoryActionError(null);
      setIsDeletingAllHistory(true);
      await api.deleteAllReportExportHistory();
      await refreshExportHistory({ silent: true });
    } catch (error) {
      console.error('Unable to clear export history', error);
      const fallbackMessage = error?.response?.data?.message ?? 'Unable to clear export history right now. Please try again.';
      setHistoryActionError(fallbackMessage);
    } finally {
      setIsDeletingAllHistory(false);
    }
  }, [isDeletingAllHistory, refreshExportHistory]);

  const handleMembershipExport = async () => {
    try {
      setMembershipExportError(null);
      setMembershipExportNotice(null);
      setIsExportingMembership(true);

      const membershipFilter = selectedCompany !== 'all'
        ? 'company'
        : 'membership-breakdown';
      const params = buildMembershipExportParams();

      const result = await executeBuilderReportDownload(params, { mode: 'export', autoDownload: true });
      await refreshExportHistory({ silent: true });

      const formatLabel = (result?.deliveredFormat ?? membershipExportFormat).toUpperCase();
      const filterLabel = membershipFilter === 'company'
        ? (selectedCompany !== 'all' ? `${selectedCompany} (Company)` : 'All Companies')
        : 'Membership Breakdown';
      const rangeLabel = rangeShortLabels[timeRange] ?? selectedRangeShort;
      setMembershipExportNotice(`${filterLabel} • ${rangeLabel} exported as ${formatLabel}.`);
    } catch (error) {
      console.error('Unable to export membership report', error);
      let message = 'Unable to export membership report right now. Please try again.';
      if (error?.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const json = JSON.parse(text);
          message = json.error ?? json.message ?? message;
        } catch (parseError) {
          console.error('Unable to parse membership export error response', parseError);
        }
      } else if (error?.response?.data?.message) {
        message = error.response.data.message;
      } else if (typeof error?.message === 'string' && error.message.trim()) {
        message = error.message;
      }
      setMembershipExportError(message);
    } finally {
      setIsExportingMembership(false);
    }
  };

  const buildMembershipExportParams = () => {
    const membershipFilters = {};
    if (membershipScopeParams.company) {
      membershipFilters.company = membershipScopeParams.company;
    }
    if (membershipScopeParams.status) {
      membershipFilters.status = membershipScopeParams.status;
    }
    if (membershipScopeParams.affiliation) {
      membershipFilters.affiliation = membershipScopeParams.affiliation;
    }
    if (membershipScopeParams.unionName) {
      membershipFilters['union-name'] = membershipScopeParams.unionName;
    }
    if (membershipScopeParams.unionPosition) {
      membershipFilters.union = membershipScopeParams.unionPosition;
    }
    if (membershipScopeParams.region) {
      membershipFilters.region = membershipScopeParams.region;
    }
    if (membershipScopeParams.gender) {
      membershipFilters.gender = membershipScopeParams.gender;
    }

    return {
      reportType: 'membership',
      range: timeRange,
      format: membershipExportFormat,
      selectedColumns: (REPORT_TYPE_COLUMNS.membership ?? []).map((column) => column.value),
      groupBy: 'none',
      aggregation: 'none',
      filters: membershipFilters,
    };
  };

  const clearMembershipPaperPreview = () => {
    setMembershipPaperPreviewUrl((previous) => {
      if (previous) {
        window.URL.revokeObjectURL(previous);
      }
      return null;
    });
    setMembershipPreviewMode('pdf');
    setMembershipTablePreview({ header: [], rows: [] });
    setMembershipPaperPreviewError(null);
  };

  const handleOpenMembershipExportPreview = async () => {
    setMembershipExportError(null);
    setMembershipExportNotice(null);
    clearMembershipPaperPreview();
    setIsMembershipExportPreviewOpen(true);

    try {
      setIsPreparingMembershipPaperPreview(true);
      const targetFormat = (membershipExportFormat ?? 'pdf').toLowerCase();

      if (targetFormat === 'pdf') {
        const previewParams = {
          ...buildMembershipExportParams(),
          format: 'pdf',
        };
        const previewResult = await fetchBuilderReportOutput(previewParams, { mode: 'preview' });
        const previewUrl = window.URL.createObjectURL(previewResult.blob);
        setMembershipPreviewMode('pdf');
        setMembershipPaperPreviewUrl(previewUrl);
      } else {
        const previewParams = {
          ...buildMembershipExportParams(),
          format: 'csv',
        };
        const previewResult = await fetchBuilderReportOutput(previewParams, { mode: 'preview' });
        const csvText = await previewResult.blob.text();
        setMembershipPreviewMode(targetFormat === 'excel' ? 'excel' : 'csv');
        setMembershipTablePreview(parseCsvPreview(csvText));
      }
    } catch (error) {
      console.error('Unable to prepare print preview', error);
      let message = 'Unable to load print preview right now. You can still continue exporting.';
      if (error?.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const json = JSON.parse(text);
          message = json.error ?? json.message ?? message;
        } catch (parseError) {
          console.error('Unable to parse membership preview error response', parseError);
        }
      } else if (error?.response?.data?.message) {
        message = error.response.data.message;
      } else if (typeof error?.message === 'string' && error.message.trim()) {
        message = error.message;
      }
      setMembershipPaperPreviewError(message);
    } finally {
      setIsPreparingMembershipPaperPreview(false);
    }
  };

  const handleCloseMembershipExportPreview = () => {
    if (isExportingMembership || isPreparingMembershipPaperPreview) {
      return;
    }
    clearMembershipPaperPreview();
    setIsMembershipExportPreviewOpen(false);
  };

  const handleConfirmMembershipExport = async () => {
    await handleMembershipExport();
    clearMembershipPaperPreview();
    setIsMembershipExportPreviewOpen(false);
  };

  const handlePrintMembershipPreview = () => {
    if (membershipPreviewMode !== 'pdf') {
      const previewWindow = window.open('', '_blank', 'noopener,noreferrer');
      if (!previewWindow) {
        return;
      }
      const { header, rows } = membershipTablePreview;
      const escapeHtml = (value) => String(value ?? '')
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#39;');
      const headerHtml = (header ?? []).map((cell) => `<th>${escapeHtml(cell)}</th>`).join('');
      const rowsHtml = (rows ?? []).map((row) => `<tr>${row.map((cell) => `<td>${escapeHtml(cell)}</td>`).join('')}</tr>`).join('');
      previewWindow.document.write(`
        <html>
          <head>
            <title>Membership Export Preview</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 24px; }
              h1 { font-size: 18px; margin: 0 0 12px; }
              table { border-collapse: collapse; width: 100%; font-size: 12px; }
              th, td { border: 1px solid #d1d5db; padding: 8px; text-align: left; vertical-align: top; }
              th { background: #f3f4f6; }
            </style>
          </head>
          <body>
            <h1>Membership Export Preview (${formatExportFormatLabel(membershipExportFormat)})</h1>
            <table>
              <thead><tr>${headerHtml}</tr></thead>
              <tbody>${rowsHtml}</tbody>
            </table>
          </body>
        </html>
      `);
      previewWindow.document.close();
      previewWindow.focus();
      previewWindow.print();
      return;
    }

    if (!membershipPaperPreviewUrl) {
      return;
    }
    const frame = membershipPreviewFrameRef.current;
    if (frame?.contentWindow) {
      frame.contentWindow.focus();
      frame.contentWindow.print();
      return;
    }
    const fallbackWindow = window.open(membershipPaperPreviewUrl, '_blank', 'noopener,noreferrer');
    if (fallbackWindow) {
      fallbackWindow.addEventListener('load', () => {
        fallbackWindow.focus();
        fallbackWindow.print();
      });
    }
  };

  useEffect(() => {
    return () => {
      if (membershipPaperPreviewUrl) {
        window.URL.revokeObjectURL(membershipPaperPreviewUrl);
      }
    };
  }, [membershipPaperPreviewUrl]);

  const setSchedulePending = (scheduleId, flag) => {
    if (!scheduleId) {
      return;
    }
    setSchedulePendingMap((previous) => {
      const next = { ...previous };
      if (!flag) {
        if (next[scheduleId]) {
          delete next[scheduleId];
          return next;
        }
        return previous;
      }
      if (next[scheduleId]) {
        return previous;
      }
      next[scheduleId] = true;
      return next;
    });
  };

  const handleScheduleToggle = async (schedule) => {
    if (!schedule?.id) {
      return;
    }
    setScheduleActionError(null);
    setSchedulePending(schedule.id, true);
    try {
      const response = await api.updateReportSchedule(schedule.id, { active: !schedule.active });
      const updatedSchedule = response?.data?.schedule ?? null;
      setSummary((previous) => {
        if (!previous) {
          return previous;
        }
        const baseList = Array.isArray(previous.reportSchedules)
          ? previous.reportSchedules
          : [];
        let updated = false;
        const nextList = baseList.map((item, index) => {
          const itemId = scheduleIdFor(item) ?? `schedule-${index}`;
          if (itemId !== schedule.id) {
            return item;
          }
          updated = true;
          if (updatedSchedule) {
            return { ...item, ...updatedSchedule };
          }
          return { ...item, active: !schedule.active };
        });
        if (!updated) {
          return updatedSchedule
            ? { ...previous, reportSchedules: [...baseList, updatedSchedule] }
            : previous;
        }
        return { ...previous, reportSchedules: nextList };
      });
      setScheduleActionError(null);
      await fetchSchedulesPage(schedulePage, { silent: true });
    } catch (error) {
      console.error('Unable to update schedule', error);
      setScheduleActionError('Unable to update the schedule right now. Please try again.');
    } finally {
      setSchedulePending(schedule.id, false);
    }
  };

  const openScheduleDialog = (schedule) => {
    if (!schedule?.id) {
      return;
    }
    const timezone = schedule.timezone ?? 'Asia/Manila';
    const fallbackReportType = scheduleFormDefaults.reportType ?? DEFAULT_REPORT_TYPE_VALUE;
    const fallbackDateRange = scheduleFormDefaults.dateRange ?? DEFAULT_DATE_RANGE_VALUE;
    const targetReportType = schedule.reportType ?? fallbackReportType;
    const availableColumnsForType = REPORT_TYPE_COLUMNS[targetReportType] ?? [];
    const allowedColumnSet = new Set(availableColumnsForType.map((column) => column.value));
    const selectedColumns = (Array.isArray(schedule.selectedColumns) ? schedule.selectedColumns : [])
      .filter((column) => allowedColumnSet.has(column));
    const resolvedColumns = selectedColumns.length
      ? selectedColumns
      : availableColumnsForType.slice(0, 4).map((column) => column.value);

    const baseFilters = schedule.filters && typeof schedule.filters === 'object' && !Array.isArray(schedule.filters)
      ? schedule.filters
      : {};

    setScheduleForm({
      name: schedule.name ?? '',
      cadence: schedule.cadence ?? 'custom',
      nextRun: toDateInputValue(schedule.nextRun),
      activationTime: toTimeInputValue(schedule.nextRun, timezone) || '09:00',
      timezone,
      format: normalizeScheduleFormatValue(schedule.format, 'pdf'),
      reportType: targetReportType,
      dateRange: schedule.dateRange ?? fallbackDateRange,
      dateFrom: schedule.dateFrom ?? '',
      dateTo: schedule.dateTo ?? '',
      filters: { ...baseFilters },
      selectedColumns: resolvedColumns,
      grouping: schedule.grouping ?? 'none',
      aggregation: schedule.aggregation ?? 'none',
      reportConfig: {
        reportType: targetReportType,
        dateRange: schedule.dateRange ?? fallbackDateRange,
        dateFrom: schedule.dateFrom ?? '',
        dateTo: schedule.dateTo ?? '',
        filters: { ...baseFilters },
        selectedColumns: resolvedColumns,
        grouping: schedule.grouping ?? 'none',
        aggregation: schedule.aggregation ?? 'none',
        format: normalizeScheduleFormatValue(schedule.format, 'pdf'),
      },
      recipientsInput: normalizeScheduleRecipients(schedule.recipients).join(', '),
    });
    setIsScheduleAdvancedOpen(false);
    setScheduleFormError(null);
    setEditingScheduleId(schedule.id);
  };

  const closeScheduleDialog = () => {
    setEditingScheduleId(null);
    setIsCreatingSchedule(false);
    setCreatingSlotId(null);
    setIsScheduleAdvancedOpen(false);
    setScheduleForm({ ...scheduleFormDefaults });
    setScheduleFormError(null);
  };

  const openCreateDialog = (slotId) => {
    setIsCreatingSchedule(true);
    setCreatingSlotId(slotId || null);
    setEditingScheduleId(null);
    setIsScheduleAdvancedOpen(false);
    setScheduleForm({ ...scheduleFormDefaults });
    setScheduleFormError(null);
  };

  const openCreateDialogFromBuilder = () => {
    const reportType = reportConfig.reportType || scheduleFormDefaults.reportType;
    const format = normalizeScheduleFormatValue(reportConfig.format, 'pdf');
    const hasCustomDates = reportConfig.dateRange === CUSTOM_DATE_RANGE_VALUE;
    setIsCreatingSchedule(true);
    setCreatingSlotId(null);
    setEditingScheduleId(null);
    setIsScheduleAdvancedOpen(false);
    const sharedReportConfig = {
      reportType,
      dateRange: hasCustomDates ? CUSTOM_DATE_RANGE_VALUE : (reportConfig.dateRange || scheduleFormDefaults.dateRange),
      dateFrom: hasCustomDates ? (reportConfig.dateFrom || '') : '',
      dateTo: hasCustomDates ? (reportConfig.dateTo || '') : '',
      filters: {
        ...(reportConfig.filters ?? {}),
      },
      selectedColumns: Array.isArray(reportConfig.selectedColumns) && reportConfig.selectedColumns.length
        ? [...reportConfig.selectedColumns]
        : [...(scheduleFormDefaults.selectedColumns ?? [])],
      grouping: reportConfig.grouping || 'none',
      aggregation: reportConfig.aggregation || 'none',
      format,
    };
    setScheduleForm({
      ...scheduleFormDefaults,
      name: '',
      reportType: sharedReportConfig.reportType,
      format: sharedReportConfig.format,
      dateRange: sharedReportConfig.dateRange,
      dateFrom: sharedReportConfig.dateFrom,
      dateTo: sharedReportConfig.dateTo,
      filters: { ...sharedReportConfig.filters },
      selectedColumns: [...sharedReportConfig.selectedColumns],
      grouping: sharedReportConfig.grouping,
      aggregation: sharedReportConfig.aggregation,
      reportConfig: sharedReportConfig,
      recipientsInput: '',
    });
    setScheduleFormError(null);
  };

  const handleScheduleCreate = async () => {
    const trimmedName = scheduleForm.name.trim();
    if (!trimmedName) {
      setScheduleFormError('Please provide a report name.');
      return;
    }
    if (!scheduleForm.nextRun) {
      setScheduleFormError('Please select the next run date.');
      return;
    }
    if (!scheduleForm.activationTime) {
      setScheduleFormError('Please set the activation time.');
      return;
    }
    const reportTypeValue = (scheduleReportConfig.reportType ?? '').trim() || scheduleFormDefaults.reportType;
    if (!reportTypeValue) {
      setScheduleFormError('Please select the report type.');
      return;
    }

    const selectedColumnsValue = Array.isArray(scheduleReportConfig.selectedColumns)
      ? scheduleReportConfig.selectedColumns.filter(Boolean)
      : [];
    if (!selectedColumnsValue.length) {
      setScheduleFormError('Select at least one column for scheduled export.');
      return;
    }

    const isCustomDate = (scheduleReportConfig.dateRange ?? '').trim() === CUSTOM_DATE_RANGE_VALUE;
    const dateRangeValue = (scheduleReportConfig.dateRange ?? '').trim() || scheduleFormDefaults.dateRange;
    if (!dateRangeValue) {
      setScheduleFormError('Please select the date range.');
      return;
    }
    if (isCustomDate && (!scheduleReportConfig.dateFrom || !scheduleReportConfig.dateTo)) {
      setScheduleFormError('Date From and Date To are required for Custom Range.');
      return;
    }

    const recipients = normalizeScheduleRecipients(scheduleForm.recipientsInput);
    const invalidRecipients = recipients.filter((email) => !EMAIL_PATTERN.test(email));
    if (invalidRecipients.length) {
      setScheduleFormError(`Invalid recipient email(s): ${invalidRecipients.join(', ')}`);
      return;
    }

    const timezone = 'Asia/Manila';
    const formatValue = normalizeScheduleFormatValue(scheduleReportConfig.format, 'pdf');
    const nextRunIso = fromDateInputToIso(
      scheduleForm.nextRun,
      scheduleForm.activationTime,
      timezone,
    );
    if (!nextRunIso) {
      setScheduleFormError('Next run date or activation time is invalid.');
      return;
    }
    setIsSavingSchedule(true);
    setScheduleFormError(null);
    try {
      const response = await api.createReportSchedule({
        name: trimmedName,
        cadence: scheduleForm.cadence,
        nextRun: nextRunIso,
        timezone,
        format: formatValue,
        reportType: reportTypeValue,
        dateRange: dateRangeValue,
        dateFrom: isCustomDate ? (scheduleReportConfig.dateFrom || null) : null,
        dateTo: isCustomDate ? (scheduleReportConfig.dateTo || null) : null,
        filters: scheduleReportConfig.filters ?? {},
        selectedColumns: selectedColumnsValue,
        grouping: scheduleReportConfig.grouping ?? 'none',
        aggregation: scheduleReportConfig.aggregation ?? 'none',
        reportConfig: {
          ...(scheduleReportConfig ?? {}),
          reportType: reportTypeValue,
          dateRange: dateRangeValue,
          dateFrom: isCustomDate ? (scheduleReportConfig.dateFrom || '') : '',
          dateTo: isCustomDate ? (scheduleReportConfig.dateTo || '') : '',
          selectedColumns: selectedColumnsValue,
          grouping: scheduleReportConfig.grouping ?? 'none',
          aggregation: scheduleReportConfig.aggregation ?? 'none',
          format: formatValue,
          filters: scheduleReportConfig.filters ?? {},
        },
        recipients,
        active: true,
      });
      const createdSchedule = response?.data?.schedule ?? null;
      if (createdSchedule) {
        setSummary((previous) => {
          if (!previous) return previous;
          const baseList = Array.isArray(previous.reportSchedules)
            ? previous.reportSchedules
            : [];
          return { ...previous, reportSchedules: [...baseList, createdSchedule] };
        });
      }
      setScheduleActionError(null);
      closeScheduleDialog();
      setSchedulePage(1);
      await fetchSchedulesPage(1, { silent: true });
      try {
        const res = await api.getReportsSummary(summaryRequestParams);
        setSummary(res.data);
      } catch (summaryError) {
        console.error('Unable to refresh summary after schedule create', summaryError);
      }
    } catch (error) {
      console.error('Unable to create schedule', error);
      setScheduleFormError('Unable to create schedule. Please try again.');
    } finally {
      setIsSavingSchedule(false);
    }
  };

  const handleScheduleDelete = async (schedule) => {
    if (!schedule?.id) return;
    setScheduleActionError(null);
    setSchedulePending(schedule.id, true);
    try {
      await api.deleteReportSchedule(schedule.id);
      setSummary((previous) => {
        if (!previous) return previous;
        const baseList = Array.isArray(previous.reportSchedules)
          ? previous.reportSchedules
          : [];
        return {
          ...previous,
          reportSchedules: baseList.filter((item) => {
            const itemId = scheduleIdFor(item) ?? '';
            return itemId !== schedule.id;
          }),
        };
      });
      try {
        const res = await api.getReportsSummary(summaryRequestParams);
        setSummary(res.data);
      } catch (summaryError) {
        console.error('Unable to refresh summary after schedule delete', summaryError);
      }
      await fetchSchedulesPage(schedulePage, { silent: true });
    } catch (error) {
      console.error('Unable to delete schedule', error);
      setScheduleActionError('Unable to delete schedule right now. Please try again.');
    } finally {
      setSchedulePending(schedule.id, false);
    }
  };

  const handleScheduleFormChange = (event) => {
    const { name, value } = event.target;
    if (!name) {
      return;
    }
    setScheduleForm((previous) => {
      const next = {
        ...previous,
        [name]: name === 'format' ? normalizeScheduleFormatValue(value, 'pdf') : value,
      };
      if (name === 'reportType') {
        const defaults = REPORT_TYPE_COLUMNS[value] ?? [];
        next.selectedColumns = defaults.slice(0, 4).map((column) => column.value);
        next.filters = {};
      }
      if (name === 'dateRange' && value !== CUSTOM_DATE_RANGE_VALUE) {
        next.dateFrom = '';
        next.dateTo = '';
      }
      if ((name === 'dateFrom' || name === 'dateTo') && value) {
        next.dateRange = CUSTOM_DATE_RANGE_VALUE;
      }

      const config = {
        ...(previous.reportConfig ?? {}),
        reportType: name === 'reportType' ? value : (next.reportType ?? previous.reportConfig?.reportType ?? DEFAULT_REPORT_TYPE_VALUE),
        dateRange: next.dateRange,
        dateFrom: next.dateFrom,
        dateTo: next.dateTo,
        filters: { ...(next.filters ?? {}) },
        selectedColumns: Array.isArray(next.selectedColumns) ? [...next.selectedColumns] : [],
        grouping: next.grouping,
        aggregation: next.aggregation,
        format: next.format,
      };
      next.reportConfig = config;
      return next;
    });
  };

  const handleScheduleRecipientRemove = (emailToRemove) => {
    const nextRecipients = normalizeScheduleRecipients(scheduleForm.recipientsInput)
      .filter((email) => email.toLowerCase() !== String(emailToRemove || '').toLowerCase());
    setScheduleForm((previous) => ({
      ...previous,
      recipientsInput: nextRecipients.join(', '),
    }));
  };

  const handleScheduleFilterChange = (filterKey, value) => {
    setScheduleForm((previous) => ({
      ...previous,
      filters: {
        ...(previous.filters ?? {}),
        [filterKey]: value,
      },
      reportConfig: {
        ...(previous.reportConfig ?? {}),
        filters: {
          ...(previous.filters ?? {}),
          [filterKey]: value,
        },
      },
    }));
  };

  const handleScheduleColumnToggle = (columnKey) => {
    setScheduleForm((previous) => {
      const current = Array.isArray(previous.selectedColumns) ? previous.selectedColumns : [];
      if (current.includes(columnKey)) {
        const nextColumns = current.filter((column) => column !== columnKey);
        return {
          ...previous,
          selectedColumns: nextColumns,
          reportConfig: {
            ...(previous.reportConfig ?? {}),
            selectedColumns: nextColumns,
          },
        };
      }
      const nextColumns = [...current, columnKey];
      return {
        ...previous,
        selectedColumns: nextColumns,
        reportConfig: {
          ...(previous.reportConfig ?? {}),
          selectedColumns: nextColumns,
        },
      };
    });
  };

  const handleScheduleSave = async () => {
    if (!editingScheduleId) {
      return;
    }
    const trimmedName = scheduleForm.name.trim();
    if (!trimmedName) {
      setScheduleFormError('Please provide a report name.');
      return;
    }
    if (!scheduleForm.nextRun) {
      setScheduleFormError('Please select the next run date.');
      return;
    }
    if (!scheduleForm.activationTime) {
      setScheduleFormError('Please set the activation time.');
      return;
    }
    const reportTypeValue = (scheduleReportConfig.reportType ?? '').trim() || scheduleFormDefaults.reportType;
    if (!reportTypeValue) {
      setScheduleFormError('Please select the report type.');
      return;
    }

    const selectedColumnsValue = Array.isArray(scheduleReportConfig.selectedColumns)
      ? scheduleReportConfig.selectedColumns.filter(Boolean)
      : [];
    if (!selectedColumnsValue.length) {
      setScheduleFormError('Select at least one column for scheduled export.');
      return;
    }

    const isCustomDate = (scheduleReportConfig.dateRange ?? '').trim() === CUSTOM_DATE_RANGE_VALUE;
    const dateRangeValue = (scheduleReportConfig.dateRange ?? '').trim() || scheduleFormDefaults.dateRange;
    if (!dateRangeValue) {
      setScheduleFormError('Please select the date range.');
      return;
    }
    if (isCustomDate && (!scheduleReportConfig.dateFrom || !scheduleReportConfig.dateTo)) {
      setScheduleFormError('Date From and Date To are required for Custom Range.');
      return;
    }

    const recipients = normalizeScheduleRecipients(scheduleForm.recipientsInput);
    const invalidRecipients = recipients.filter((email) => !EMAIL_PATTERN.test(email));
    if (invalidRecipients.length) {
      setScheduleFormError(`Invalid recipient email(s): ${invalidRecipients.join(', ')}`);
      return;
    }

    const timezone = 'Asia/Manila';
    const formatValue = normalizeScheduleFormatValue(scheduleReportConfig.format, 'pdf');
    const nextRunIso = fromDateInputToIso(
      scheduleForm.nextRun,
      scheduleForm.activationTime,
      timezone,
    );
    if (!nextRunIso) {
      setScheduleFormError('Next run date or activation time is invalid.');
      return;
    }
    setIsSavingSchedule(true);
    setScheduleFormError(null);
    try {
      const response = await api.updateReportSchedule(editingScheduleId, {
        name: trimmedName,
        cadence: scheduleForm.cadence,
        nextRun: nextRunIso,
        timezone,
        format: formatValue,
        reportType: reportTypeValue,
        dateRange: dateRangeValue,
        dateFrom: isCustomDate ? (scheduleReportConfig.dateFrom || null) : null,
        dateTo: isCustomDate ? (scheduleReportConfig.dateTo || null) : null,
        filters: scheduleReportConfig.filters ?? {},
        selectedColumns: selectedColumnsValue,
        grouping: scheduleReportConfig.grouping ?? 'none',
        aggregation: scheduleReportConfig.aggregation ?? 'none',
        reportConfig: {
          ...(scheduleReportConfig ?? {}),
          reportType: reportTypeValue,
          dateRange: dateRangeValue,
          dateFrom: isCustomDate ? (scheduleReportConfig.dateFrom || '') : '',
          dateTo: isCustomDate ? (scheduleReportConfig.dateTo || '') : '',
          selectedColumns: selectedColumnsValue,
          grouping: scheduleReportConfig.grouping ?? 'none',
          aggregation: scheduleReportConfig.aggregation ?? 'none',
          format: formatValue,
          filters: scheduleReportConfig.filters ?? {},
        },
        recipients,
      });
      const updatedSchedule = response?.data?.schedule ?? null;
      const normalizedSchedule = updatedSchedule
        ? normalizeScheduleEntry(updatedSchedule, editingScheduleId)
        : null;
      setSummary((previous) => {
        if (!previous) {
          return previous;
        }
        const baseList = Array.isArray(previous.reportSchedules)
          ? previous.reportSchedules
          : [];
        let updated = false;
        const nextList = baseList.map((item, index) => {
          const itemId = scheduleIdFor(item) ?? `schedule-${index}`;
          if (itemId !== editingScheduleId) {
            return item;
          }
          updated = true;
          if (normalizedSchedule) {
            return { ...item, ...normalizedSchedule };
          }
          return {
            ...item,
            name: trimmedName,
            cadence: scheduleForm.cadence,
            nextRun: nextRunIso,
            timezone,
            format: formatValue,
            reportType: reportTypeValue,
            dateRange: dateRangeValue,
            dateFrom: isCustomDate ? (scheduleReportConfig.dateFrom || null) : null,
            dateTo: isCustomDate ? (scheduleReportConfig.dateTo || null) : null,
            filters: scheduleReportConfig.filters ?? {},
            selectedColumns: selectedColumnsValue,
            grouping: scheduleReportConfig.grouping ?? 'none',
            aggregation: scheduleReportConfig.aggregation ?? 'none',
            reportConfig: {
              ...(scheduleReportConfig ?? {}),
              reportType: reportTypeValue,
              dateRange: dateRangeValue,
              dateFrom: isCustomDate ? (scheduleReportConfig.dateFrom || '') : '',
              dateTo: isCustomDate ? (scheduleReportConfig.dateTo || '') : '',
              selectedColumns: selectedColumnsValue,
              grouping: scheduleReportConfig.grouping ?? 'none',
              aggregation: scheduleReportConfig.aggregation ?? 'none',
              format: formatValue,
              filters: scheduleReportConfig.filters ?? {},
            },
            recipients,
          };
        });
        const reportSchedulesNext = updated
          ? nextList
          : normalizedSchedule
            ? [...baseList, normalizedSchedule]
            : baseList;
        return { ...previous, reportSchedules: reportSchedulesNext };
      });
      setScheduleActionError(null);
      closeScheduleDialog();
      await fetchSchedulesPage(schedulePage, { silent: true });
    } catch (error) {
      console.error('Unable to update schedule', error);
      setScheduleFormError('Unable to save changes. Please try again.');
    } finally {
      setIsSavingSchedule(false);
    }
  };

  // membership & operations highlights are data-driven where possible; default to empty/placeholder
  const membershipHighlightsRaw = performanceHighlights.membership;
  const operationsHighlights = useMemo(() => {
    const base = performanceHighlights.operations ?? [];
    const ticketsSummary = summary?.tickets ?? {};
    const toNumeric = (value) => {
      const numeric = Number(value);
      return Number.isFinite(numeric) ? numeric : null;
    };
    const closedCount = toNumeric(ticketsSummary.closed);
    const openCount = toNumeric(ticketsSummary.open);
    const totalCount = toNumeric(ticketsSummary.total);

    const resolvedValue = Number.isFinite(closedCount) ? closedCount : 0;
    const resolvedMeta = (() => {
      if (Number.isFinite(openCount) && openCount > 0) {
        return `${openCount} open`;
      }
      if (Number.isFinite(totalCount) && totalCount > 0) {
        return `${totalCount} logged`;
      }
      return 'No ticket activity';
    })();

    const resolvedHighlight = {
      label: 'Tickets Resolved',
      value: resolvedValue,
      format: 'count',
      meta: resolvedMeta,
    };

    let replaced = false;
    const updated = base.map((item) => {
      const label = (item.label ?? '').toLowerCase();
      if (label.includes('assistance') || label.includes('ticket')) {
        replaced = true;
        return { ...item, ...resolvedHighlight };
      }
      return item;
    });

    if (!replaced) {
      return [...updated, resolvedHighlight];
    }

    return updated;
  }, [performanceHighlights.operations, summary?.tickets]);

  const operationsPrimaryStats = useMemo(() => {
    const kpis = Array.isArray(dashboardKpis?.kpis) ? dashboardKpis.kpis : [];
    const findKpi = (id) => kpis.find((k) => k.id === id);

    const totalAccounts = findKpi('total-members');
    const pendingReg = findKpi('pending-registrations');
    const totalMembers = findKpi('active-members');
    const cardRequests = findKpi('card-requests');
    const totalEvents = findKpi('total-events');
    const totalBookings = findKpi('total-bookings');
    const totalTickets = findKpi('total-tickets');
    const overdueTickets = findKpi('overdue-tickets');

    const safeVal = (kpi) => {
      if (!kpi) return null;
      const num = Number(kpi.value);
      return Number.isFinite(num) ? num : null;
    };

    return [
      {
        id: 'total-accounts',
        label: 'Total Accounts',
        value: safeVal(totalAccounts),
        meta: totalAccounts?.change ?? null,
        format: 'count',
        tone: 'is-blue',
      },
      {
        id: 'pending-registrations',
        label: 'Pending Registrations',
        value: safeVal(pendingReg),
        meta: pendingReg?.change ?? null,
        format: 'count',
        tone: 'is-orange',
      },
      {
        id: 'total-members',
        label: 'Total Members',
        value: safeVal(totalMembers),
        meta: totalMembers?.change ?? null,
        format: 'count',
        tone: 'is-green',
      },
      {
        id: 'card-requests',
        label: 'Card Requests',
        value: safeVal(cardRequests),
        meta: cardRequests?.change ?? null,
        format: 'count',
        tone: 'is-purple',
      },
      {
        id: 'total-events',
        label: 'Total Events',
        value: safeVal(totalEvents),
        meta: totalEvents?.change ?? null,
        format: 'count',
        tone: 'is-blue',
      },
      {
        id: 'total-bookings',
        label: 'Total Bookings',
        value: safeVal(totalBookings),
        meta: totalBookings?.change ?? null,
        format: 'count',
        tone: 'is-purple',
      },
      {
        id: 'total-tickets',
        label: 'Total Tickets',
        value: safeVal(totalTickets),
        meta: totalTickets?.change ?? null,
        format: 'count',
        tone: 'is-orange',
      },
      {
        id: 'overdue-tickets',
        label: 'Overdue Tickets',
        value: safeVal(overdueTickets),
        meta: overdueTickets?.change ?? null,
        format: 'count',
        tone: safeVal(overdueTickets) > 0 ? 'is-red' : 'is-green',
      },
    ];
  }, [dashboardKpis]);

  const resolvedMembershipHighlights = useMemo(() => {
    if (!Array.isArray(membershipHighlightsRaw)) {
      return [];
    }

    const rangeLabel = rangeShortLabels[timeRange] ?? rangeShortLabels['30d'] ?? 'Last 30 days';
    const newJoinersLabel = `New Registrations (${rangeLabel})`;
    const scopedMembers = companySnapshot ?? summary?.members ?? {};
    const scopedJoinersMap = scopedMembers?.newJoinersByRange ?? {};
    const scopedChangeMap = scopedMembers && typeof scopedMembers === 'object'
      ? scopedMembers.newJoinersChangeByRange ?? null
      : null;

    const normalizedHighlights = membershipHighlightsRaw.map((item) => {
      if ((item.label ?? '').toLowerCase().includes('new registrations')) {
        const scopedValueRaw = scopedJoinersMap?.[timeRange];
        const resolvedValue = Number.isFinite(Number(scopedValueRaw)) ? Number(scopedValueRaw) : Number(item.value);

        let formattedMeta = item.meta;
        const changeEntry = scopedChangeMap && Object.prototype.hasOwnProperty.call(scopedChangeMap, timeRange)
          ? scopedChangeMap[timeRange]
          : null;
        const changeNumeric = Number(changeEntry);
        if (Number.isFinite(changeNumeric)) {
          const suffixMap = {
            '7d': 'vs prior week',
            '30d': 'vs prior 30d',
            '90d': 'vs prior quarter',
            '12m': 'vs prior year',
          };
          const suffix = suffixMap[timeRange] ?? 'vs previous period';
          formattedMeta = `${changeNumeric >= 0 ? '+' : ''}${changeNumeric.toFixed(1)}% ${suffix}`;
        }

        return {
          ...item,
          label: newJoinersLabel,
          value: Number.isFinite(resolvedValue) ? resolvedValue : item.value,
          meta: formattedMeta,
        };
      }
      return item;
    });

    return normalizedHighlights.filter((item) => {
      const label = (item.label ?? '').toLowerCase();
      return label && !label.includes('retention');
    });
  }, [membershipHighlightsRaw, companySnapshot, summary?.members, timeRange]);

  const performanceSections = [
    {
      id: 'membership',
      title: 'Membership Metrics',
      items: resolvedMembershipHighlights,
      emptyLabel: 'No membership KPIs yet.',
    },
    {
      id: 'operations',
      title: 'Operations',
      items: operationsHighlights,
      emptyLabel: 'No operations KPIs yet.',
    },
  ];


  const topKpis = useMemo(() => {
    const overviewCounts = overviewData?.counts ?? {};
    const totalAccounts = Number(overviewCounts.totalAccounts ?? 0);
    const approvedMembers = Number(overviewCounts.approvedMembers ?? 0);
    const activeMembers = Number(overviewCounts.activeMembers ?? 0);
    const newRegistrations = Number(overviewCounts.newRegistrations ?? 0);
    const eventAttendance = Number(overviewCounts.eventAttendance ?? 0);
    const approvalRate = totalAccounts > 0 ? (approvedMembers / totalAccounts) * 100 : null;
    const formatCountValue = formatCount;

    return [
      {
        id: 'total-accounts',
        label: 'Total Accounts',
        value: formatCount(totalAccounts),
        icon: Users,
        tone: 'is-blue',
        change: '↑ 12.4% vs prev 30 days'
      },
      {
        id: 'active-members',
        label: 'Approved Members',
        value: formatCountValue(activeMembers),
        icon: TrendingUp,
        tone: 'is-green',
        change: '↑ 8.2% vs prev 30 days'
      },
      {
        id: 'new-registrations',
        label: 'New registrations (30D)',
        value: formatCountValue(newRegistrations),
        icon: Plus,
        tone: 'is-purple',
        change: '↑ 15.1% vs prev 30 days'
      },
      {
        id: 'event-attendance',
        label: 'Event Attendance',
        value: formatCountValue(eventAttendance),
        icon: Calendar,
        tone: 'is-orange',
        change: '↑ 5.0% vs prev 30 days'
      },
    ];
  }, [summary, timeRange]);

  const overviewKpis = useMemo(() => {
    const overviewCounts = overviewData?.counts ?? {};
    const totalAccounts = Number(overviewCounts.totalAccounts ?? 0);
    const approvedMembers = Number(overviewCounts.approvedMembers ?? 0);
    const activeMembers = Number(overviewCounts.activeMembers ?? 0);
    const newRegistrations = Number(overviewCounts.newRegistrations ?? 0);
    const eventAttendance = Number(overviewCounts.eventAttendance ?? 0);
    const approvalRate = totalAccounts > 0 ? (approvedMembers / totalAccounts) * 100 : null;

    return [
      {
        id: "total-accounts",
        label: "Total Accounts",
        value: formatCount(totalAccounts),
        icon: Users,
        tone: "is-blue",
        change: `${formatCount(newRegistrations)} new registrations in ${overviewRangeLabel}`,
      },
      {
        id: "approved-members",
        label: "Approved Members",
        value: formatCount(approvedMembers),
        icon: TrendingUp,
        tone: "is-green",
        change: approvalRate != null ? `${approvalRate.toFixed(1)}% of total accounts` : "No approved accounts yet",
      },
      {
        id: "active-members",
        label: "Active Members",
        value: formatCount(activeMembers),
        icon: Activity,
        tone: "is-purple",
        change: `Signed in during ${overviewRangeLabel}`,
      },
      {
        id: "event-attendance",
        label: "Event Attendance",
        value: formatCount(eventAttendance),
        icon: Calendar,
        tone: "is-orange",
        change: `Recorded during ${overviewRangeLabel}`,
      },
    ];
  }, [formatCount, overviewData, overviewRangeLabel]);

  const recentActivity = Array.isArray(dashboardKpis?.recentActivity) ? dashboardKpis.recentActivity : [];
  const loading = isLoadingDashboardData;
  const navigateTo = (target) => {
    if (target === "audit") {
      navigate('/admin/audit-log');
    }
  };

  const activeTabLabel = tabs.find((tab) => tab.id === activeTab)?.label ?? "Overview";
  const reportsDataSourceLabel = isSummarySample ? "Sample dataset" : "Live dataset";

  return (
    <div className="admin-page admin-stack-xl reports-page">
      <header className="admin-surface dashboard-hero">
        <div className="dashboard-hero__intro">
          <span className="reports-hero__eyebrow">Analytics Workspace</span>
          <h1>Reports & Analytics</h1>
          <p className="admin-muted">Comprehensive insights and reporting for LaborConnect operations.</p>
        </div>
        <div className="admin-actions dashboard-hero__actions">
          <button
            type="button"
            className={`admin-icon-button admin-icon-button--refresh ${isRefreshing ? "is-loading" : ""}`}
            onClick={handleRefresh}
            disabled={isRefreshing}
            aria-label="Refresh reports and analytics"
            title="Refresh reports and analytics"
          >
            <RefreshCw size={18} />
          </button>
        </div>
      </header>

        <div className="reports-tab-strip">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`reports-tab ${activeTab === tab.id ? "is-active" : ""}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="reports-tab-content">
        {activeTab === "overview" ? (
            <>
              <section className="reports-filters reports-filters-wrapper reports-overview-filters">
                <div className="reports-overview-filters__header">
                  <div>
                    <h2>Overview Filters</h2>
                    <p className="admin-muted">Showing {overviewRangeLabel} in Asia/Manila.</p>
                  </div>
                  {hasActiveOverviewFilters ? (
                    <div className="reports-filters__actions">
                      <button
                        type="button"
                        className="admin-button is-ghost"
                        onClick={handleClearOverviewFilters}
                      >
                        Clear Filters
                      </button>
                    </div>
                  ) : null}
                </div>

                <div className="reports-filters-grid-primary reports-overview-filters__grid">
                  <div className="reports-filters__field">
                    <label>Date Range</label>
                    <select
                      name="dateRange"
                      value={overviewFilters.dateRange}
                      onChange={handleOverviewFilterChange}
                    >
                      {OVERVIEW_DATE_RANGE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="reports-filters__field">
                    <label>Affiliation</label>
                    <select
                      name="affiliation"
                      value={overviewFilters.affiliation}
                      onChange={handleOverviewFilterChange}
                    >
                      <option value="">All affiliations</option>
                      {overviewFilterChoices.affiliations.map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>

                  <div className="reports-filters__field">
                    <label>Company</label>
                    <select
                      name="company"
                      value={overviewFilters.company}
                      onChange={handleOverviewFilterChange}
                    >
                      <option value="">All companies</option>
                      {overviewFilterChoices.companies.map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>

                  <div className="reports-filters__field">
                    <label>Region</label>
                    <select
                      name="region"
                      value={overviewFilters.region}
                      onChange={handleOverviewFilterChange}
                    >
                      <option value="">All regions</option>
                      {overviewFilterChoices.regions.map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>

                  <div className="reports-filters__field">
                    <label>Status</label>
                    <select
                      name="status"
                      value={overviewFilters.status}
                      onChange={handleOverviewFilterChange}
                    >
                      <option value="">All statuses</option>
                      {overviewFilterChoices.statuses.map((option) => (
                        <option key={option} value={option}>{option.replace(/[_-]+/g, " ")}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {isOverviewCustomRange ? (
                  <div className="reports-filters-grid-secondary reports-overview-filters__grid">
                    <div className="reports-filters__field">
                      <label>Date From</label>
                      <input
                        type="date"
                        name="dateFrom"
                        value={overviewFilters.dateFrom}
                        onChange={handleOverviewFilterChange}
                      />
                    </div>

                    <div className="reports-filters__field">
                      <label>Date To</label>
                      <input
                        type="date"
                        name="dateTo"
                        value={overviewFilters.dateTo}
                        onChange={handleOverviewFilterChange}
                      />
                    </div>
                  </div>
                ) : null}

                {overviewRangeValidationError ? (
                  <p className="reports-inline-feedback is-error">{overviewRangeValidationError}</p>
                ) : null}
                {overviewError ? (
                  <p className="reports-inline-feedback is-error">{overviewError}</p>
                ) : null}
              </section>

              {isLoadingOverview ? (
                <div className="reports-dashboard-stack">
                  <div className="admin-card-grid admin-card-grid--kpi">
                    {Array.from({ length: 4 }, (_unused, index) => (
                      <article key={`overview-kpi-skeleton-${index}`} className="admin-card dashboard-kpi-card reports-card-skeleton">
                        <div className="reports-loading-line reports-loading-line--label" />
                        <div className="reports-loading-line reports-loading-line--value" />
                        <div className="reports-loading-line reports-loading-line--meta" />
                      </article>
                    ))}
                  </div>

                  <div className="admin-grid-one">
                    <div className="reports-loading-block reports-loading-block--chart" />
                  </div>

                  <div className="admin-grid-two reports-section-mt-24">
                    <div className="reports-loading-block reports-loading-block--chart" />
                    <div className="reports-loading-block reports-loading-block--chart" />
                  </div>

                  <div className="admin-grid-two reports-section-mt-24">
                    <div className="reports-loading-block reports-loading-block--chart" />
                    <div className="reports-loading-block reports-loading-block--chart" />
                  </div>

                  <div className="admin-grid-one reports-section-mt-24">
                    <div className="reports-loading-block reports-loading-block--chart" />
                  </div>
                </div>
              ) : !overviewRangeValidationError && !overviewError && !hasOverviewData ? (
                <div className="admin-empty-state is-minimal">No overview data available for the selected filters.</div>
              ) : hasOverviewData ? (
                <div className="reports-dashboard-stack">
                  <div className="admin-card-grid admin-card-grid--kpi">
                    {overviewKpis.map((kpi) => {
                      const IconComponent = kpi.icon || Users;
                      return (
                        <article key={kpi.id} className="admin-card dashboard-kpi-card">
                          <div className="admin-card__label">{kpi.label}</div>
                          <div className="admin-row dashboard-kpi-value-row">
                            <div className="admin-card__value">{kpi.value}</div>
                            <span className={`admin-chip ${kpi.tone || 'is-blue'}`}>
                              <IconComponent size={16} />
                            </span>
                          </div>
                          <div className="admin-card__meta has-positive-trend">{kpi.change}</div>
                        </article>
                      );
                    })}
                  </div>

                  <div className="admin-grid-one">
                    <article className="admin-card admin-stack-md reports-chart-card reports-chart-card--primary">
                      <header className="admin-card__heading">
                        <LineChart size={18} />
                        <div>
                          <h2>Membership Count</h2>
                          <p className="admin-muted">{membershipTrendDescription}</p>
                        </div>
                      </header>
                      {growthChartData ? (
                        <div className="reports-growth-card">
                          <div className="dashboard-chart-card__canvas dashboard-chart-card__canvas--line reports-chart-line-growth reports-chart-card__canvas reports-chart-card__canvas--primary">
                            <Line data={growthChartData} options={overviewGrowthLineOptions} />
                          </div>
                        </div>
                      ) : (
                        <div className="admin-empty-state is-minimal">No data available for selected filters.</div>
                      )}
                    </article>
                  </div>

                  <div className="admin-grid-two reports-section-mt-24">
                    <article className="admin-card admin-stack-md reports-chart-card">
                      <header className="admin-card__heading">
                        <Activity size={18} />
                        <div>
                          <h2>Access Funnel</h2>
                          <p className="admin-muted">Registrations, approvals, active members, and ticket submissions.</p>
                        </div>
                      </header>
                      {accessFunnelChartData ? (
                        <div className="dashboard-chart-card__canvas dashboard-chart-card__canvas--bar reports-chart-bar-funnel reports-chart-card__canvas reports-chart-card__canvas--secondary">
                          <Bar data={accessFunnelChartData} options={horizontalBarOptions} />
                        </div>
                      ) : (
                        <div className="admin-empty-state is-minimal">No data available for selected filters.</div>
                      )}
                    </article>

                    <article className="admin-card admin-stack-md reports-chart-card">
                      <header className="admin-card__heading">
                        <PieChart size={18} />
                        <div>
                          <h2>Member Distribution</h2>
                          <p className="admin-muted">Breakdown of members by affiliation.</p>
                        </div>
                      </header>
                      {distributionChartData ? (
                        <div className="dashboard-chart-card__canvas dashboard-chart-card__canvas--pie reports-chart-pie-dist reports-chart-card__canvas reports-chart-card__canvas--secondary">
                          <Doughnut data={distributionChartData} options={pieOptions} />
                        </div>
                      ) : (
                        <div className="admin-empty-state is-minimal">No data available for selected filters.</div>
                      )}
                    </article>
                  </div>

                  <div className="admin-grid-two reports-section-mt-24">
                    <article className="admin-card admin-stack-md reports-chart-card">
                      <header className="admin-card__heading">
                        <TrendingUp size={18} />
                        <div>
                          <h2>App Access Trend</h2>
                          <p className="admin-muted">{accessTrendDescription}</p>
                        </div>
                      </header>
                      {customActivityChartData ? (
                        <div className="dashboard-chart-card__canvas dashboard-chart-card__canvas--line reports-chart-line-activity reports-chart-card__canvas reports-chart-card__canvas--supporting">
                          <Line data={customActivityChartData} options={overviewActivityLineOptions} />
                        </div>
                      ) : (
                        <div className="admin-empty-state is-minimal">No data available for selected filters.</div>
                      )}
                    </article>

                    <article className="admin-card admin-stack-md reports-chart-card">
                      <header className="admin-card__heading">
                        <Activity size={18} />
                        <div>
                          <h2>User Engagement</h2>
                          <p className="admin-muted">Weekly member activity grouped in Asia/Manila.</p>
                        </div>
                      </header>
                      {engagementChartData ? (
                        <div className="dashboard-chart-card__canvas dashboard-chart-card__canvas--line reports-chart-line-activity reports-chart-card__canvas reports-chart-card__canvas--supporting">
                          <Line data={engagementChartData} options={lineOptionsHiddenLegend} />
                        </div>
                      ) : (
                        <div className="admin-empty-state is-minimal">No data available for selected filters.</div>
                      )}
                    </article>
                  </div>

                  <div className="admin-grid-one reports-section-mt-24">
                    <article className="admin-card admin-stack-md reports-chart-card reports-chart-card--ranking">
                      <header className="admin-card__heading">
                        <Building2 size={18} />
                        <div>
                          <h2>Top 10 Active Companies</h2>
                          <p className="admin-muted">Companies ranked by filtered member count.</p>
                        </div>
                      </header>
                      {topCompaniesChartData ? (
                        <div className="dashboard-chart-card__canvas dashboard-chart-card__canvas--bar reports-chart-bar-top reports-chart-card__canvas reports-chart-card__canvas--ranking">
                          <Bar data={topCompaniesChartData} options={horizontalBarOptions} />
                        </div>
                      ) : (
                        <div className="admin-empty-state is-minimal">No data available for selected filters.</div>
                      )}
                    </article>
                  </div>
                </div>
              ) : null}

              <section className="admin-surface dashboard-section dashboard-section--activity">
                <div className="dashboard-section__head dashboard-section__head--activity">
                  <div>
                    <h2>Recent Activity</h2>
                    <p className="admin-muted dashboard-section__subtext">
                      Latest admin actions and system updates in one stream.
                    </p>
                  </div>
                  <div className="dashboard-activity__controls">
                    <div className="dashboard-activity__view-toggle" role="tablist" aria-label="Activity view mode">
                      {activityViewOptions.map((option) => (
                        <button
                          key={option.id}
                          type="button"
                          className={`dashboard-activity__toggle-chip ${activityView === option.id ? "is-active" : ""}`}
                          onClick={() => setActivityView(option.id)}
                          aria-pressed={activityView === option.id}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                    <button
                      type="button"
                      className="admin-button"
                      onClick={() => navigateTo("audit")}
                    >
                      View All
                    </button>
                  </div>
                </div>
                <div className={`admin-activity-grid admin-activity-grid--${activityView}`}>
                  {recentActivity.map((activity, index) => (
                    (() => {
                      const metadata = parseActivityMetadata(activity.detail);
                      const actionLabel = formatActionLabel(activity.action);
                      const adminLabel = activity.adminName
                        || metadata.actorName
                        || metadata.adminName
                        || "Admin User";
                      const roleFromArray = Array.isArray(activity.adminRoles) && activity.adminRoles.length > 0
                        ? activity.adminRoles[0]
                        : null;
                      const adminRoleTitle = toDisplayRole(
                        activity.adminRole
                        || roleFromArray
                        || metadata.actorRole
                        || metadata.role
                        || "Admin",
                      );
                      const detailText = getActionDescription({
                        action: activity.action,
                        metadata,
                        entityId: activity.entityId,
                        ticketId: metadata.ticketId ?? null,
                        reason: metadata.reason ?? null,
                        description: metadata.description ?? null,
                      }) || activity.subject || "No additional details.";

                      return (
                        <article
                          key={`${activity.action}-${activity.createdAt ?? index}`}
                          className={`admin-card dashboard-activity-card ${activity.tone ? `dashboard-activity-card--${activity.tone}` : ""}`}
                        >
                          <div className="dashboard-activity-card__header">
                            <strong className="dashboard-activity-card__title">
                              {adminRoleTitle}
                            </strong>
                            <span
                              className={`${badgeClassByTone[activity.tone] || "admin-chip"} dashboard-activity-card__time`}
                            >
                              {computeTime(activity.createdAt, activity.time)}
                            </span>
                          </div>

                          <div className="dashboard-activity-card__fields">
                            <div className="dashboard-activity-card__field">
                              <span className="dashboard-activity-card__label">Admin</span>
                              <span className="dashboard-activity-card__value">{adminLabel}</span>
                            </div>

                            <div className="dashboard-activity-card__field">
                              <span className="dashboard-activity-card__label">Action</span>
                              <span className="dashboard-activity-card__value">{actionLabel}</span>
                            </div>

                            <div className="dashboard-activity-card__field dashboard-activity-card__field--detail">
                              <span className="dashboard-activity-card__label">Detail</span>
                              <p className="dashboard-activity-card__desc">{detailText}</p>
                            </div>
                          </div>
                        </article>
                      );
                    })()
                  ))}
                  {!loading && recentActivity.length === 0 ? (
                    <div className="admin-card-grid__placeholder admin-empty-state">
                      No recent activity yet.
                    </div>
                  ) : null}
                </div>
              </section>

            </>
            ) : null}

        {activeTab === "membership" ? (
            <>
              <div className="reports-export-action">
                <button
                  type="button"
                  className="admin-button is-primary"
                  onClick={handleOpenMembershipExportPreview}
                  disabled={isExportingMembership}
                >
                  <Download size={16} /> {isExportingMembership ? 'Preparing...' : 'Export report'}
                </button>
              </div>

              <div className="reports-filters reports-filters-wrapper">
                {/* Primary Filters */}
                <div className="reports-filters-grid-primary">
                  <div className="reports-filters__field">
                    <label>Date Range</label>
                    <select value={timeRange} onChange={handleTimeRangeChange}>
                      {Object.entries(rangeShortLabels).map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="reports-filters__field">
                    <label>Affiliation</label>
                    <select value={selectedAffiliation} onChange={(event) => setSelectedAffiliation(event.target.value)}>
                      <option value="all">All affiliations</option>
                      {advancedFilterOptions.affiliations.map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>
                  <div className="reports-filters__field">
                    <label>Company</label>
                    <select value={selectedCompany} onChange={handleCompanyChange}>
                      <option value="all">All companies</option>
                      {companyOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="reports-filters__field">
                    <label>Region</label>
                    <select value={selectedRegion} onChange={(event) => setSelectedRegion(event.target.value)}>
                      <option value="all">All regions</option>
                      {advancedFilterOptions.regions.map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>
                  <div className="reports-filters__field">
                    <label>Status</label>
                    <select
                      value={selectedStatus}
                      onChange={(event) => {
                        const nextStatus = event.target.value;
                        if (nextStatus === 'all' || MEMBERSHIP_STATUS_FILTER_SET.has(nextStatus)) {
                          setSelectedStatus(nextStatus);
                        }
                      }}
                    >
                      {MEMBERSHIP_STATUS_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="reports-filters__field">
                    <label>Export Format</label>
                    <select
                      value={membershipExportFormat}
                      onChange={(event) => setMembershipExportFormat(event.target.value)}
                    >
                      {MEMBERSHIP_EXPORT_FORMAT_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Actions Row */}
                {!showAdvancedMembershipFilters && (
                  <div className="reports-filters-actions-row">
                    <div className="reports-filters__actions">
                      <button
                        type="button"
                        className="admin-button is-ghost"
                        onClick={handleClearMembershipFilters}
                      >
                        Clear Filters
                      </button>
                      <button
                        type="button"
                        className="admin-button is-outline reports-btn-outline-primary"
                        onClick={handleToggleAdvancedFilters}
                      >
                        <SlidersHorizontal size={15} /> {`Advanced Filters${activeAdvancedMembershipFilterCount ? ` (${activeAdvancedMembershipFilterCount})` : ''}`}
                      </button>
                    </div>
                  </div>
                )}

                {/* Advanced Filters */}
                {showAdvancedMembershipFilters && (
                  <>
                    <div className="reports-filters-divider" />
                    <div className="reports-filters-grid-secondary">
                      <div className="reports-filters__field">
                        <label>Union Name</label>
                        <select value={draftUnionName} onChange={(event) => setDraftUnionName(event.target.value)}>
                          <option value="all">All union names</option>
                          {advancedFilterOptions.unionNames.map((option) => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </select>
                      </div>

                      <div className="reports-filters__field">
                        <label>Union Position</label>
                        <select value={draftUnionPosition} onChange={(event) => setDraftUnionPosition(event.target.value)}>
                          <option value="all">All union positions</option>
                          {advancedFilterOptions.unionPositions.map((option) => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </select>
                      </div>

                      <div className="reports-filters__field">
                        <label>Gender</label>
                        <select value={draftGender} onChange={(event) => setDraftGender(event.target.value)}>
                          <option value="all">All genders</option>
                          {advancedFilterOptions.genders.map((option) => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </select>
                      </div>

                      <div className="reports-filters__field">
                        <label>Age Group</label>
                        <select value={draftAgeGroup} onChange={(event) => setDraftAgeGroup(event.target.value)}>
                          <option value="all">All age groups</option>
                          {advancedFilterOptions.ageGroups.map((option) => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="reports-filters__advanced-actions">
                      <div className="reports-filters__actions">
                        <button
                          type="button"
                          className="admin-button is-ghost"
                          onClick={handleClearMembershipFilters}
                        >
                          Clear Filters
                        </button>
                        <button
                          type="button"
                          className="admin-button is-outline reports-btn-outline-primary"
                          onClick={handleToggleAdvancedFilters}
                        >
                          <SlidersHorizontal size={15} /> Hide Advanced Filters
                        </button>
                        <button
                          type="button"
                          className="admin-button is-primary"
                          onClick={handleApplyAdvancedFilters}
                        >
                          Apply Filters
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
              {membershipExportError ? (
                <p className="admin-text-error reports-builder-feedback">{membershipExportError}</p>
              ) : membershipExportNotice ? (
                <p className="admin-muted reports-builder-feedback">{membershipExportNotice}</p>
              ) : null}

              <section className="reports-section-mb-24">
                {(() => {
                  const totalAllStatuses = scopedMembers?.totalAllStatuses ?? summary?.members?.totalAllStatuses;
                  const activeTotal = scopedMembers?.total ?? summary?.members?.total;
                  const explicitInactive = scopedMembers?.inactive
                    ?? scopedMembers?.inactiveMembers
                    ?? summary?.members?.inactive
                    ?? summary?.members?.inactiveMembers;
                  const computedInactive = (() => {
                    if (explicitInactive != null) {
                      const numeric = Number(explicitInactive);
                      return Number.isFinite(numeric) ? Math.max(numeric, 0) : null;
                    }
                    if (totalAllStatuses == null || activeTotal == null) {
                      return null;
                    }
                    const allNumeric = Number(totalAllStatuses);
                    const activeNumeric = Number(activeTotal);
                    if (!Number.isFinite(allNumeric) || !Number.isFinite(activeNumeric)) {
                      return null;
                    }
                    return Math.max(allNumeric - activeNumeric, 0);
                  })();

                  const retentionRateValue = scopedMembers?.retentionRate ?? summary?.members?.retentionRate;
                  const retentionMeta = selectedCompany !== 'all'
                    ? `${selectedCompany} retention`
                    : 'Trailing 12 months';

                  const rangeCodeMap = {
                    '7d': '7d',
                    '30d': '30d',
                    '90d': '90d',
                    '12m': '12m',
                  };
                  const rangeCode = rangeCodeMap[timeRange] ?? timeRange;

                  const inactiveValue = toFiniteNumber(computedInactive);
                  const pendingApprovalsValue = toFiniteNumber(membershipSummarySource?.members?.pending) ?? 0;
                  const totalForInactive = pickFirstFinite(
                    totalAllStatuses,
                    activeTotal != null && inactiveValue != null ? Number(activeTotal) + Number(inactiveValue) : null,
                  );
                  const inactivePercentOfTotal = (() => {
                    if (inactiveValue == null || totalForInactive == null || totalForInactive <= 0) {
                      return null;
                    }
                    return (inactiveValue / totalForInactive) * 100;
                  })();

                  const cards = [
                    {
                      title: `New Registrations (${rangeCode})`,
                      value: newJoinersValue != null ? formatCount(newJoinersValue) : '—',
                      accent: 'primary',
                      tone: null,
                      meta: null,
                    },
                    {
                      title: 'Active Members',
                      value: activeTotal != null ? formatCount(activeTotal) : '—',
                      accent: 'info',
                      tone: null,
                      meta: null,
                    },
                    {
                      title: 'Inactive Members',
                      value: inactiveValue != null ? formatCount(inactiveValue) : '—',
                      accent: 'danger',
                      tone: inactivePercentOfTotal != null && inactivePercentOfTotal > 0 ? 'negative' : null,
                      meta: null,
                    },
                    {
                      title: 'Pending Approvals',
                      value: formatCount(pendingApprovalsValue),
                      accent: 'warning',
                      tone: null,
                      meta: 'Awaiting verification',
                    },
                  ];

                  return (
                    <div className="admin-card-grid admin-card-grid--kpi reports-membership-kpi-grid">
                      {cards.map((card) => {
                        const toneClass = card.accent === 'danger'
                          ? 'is-red'
                          : card.accent === 'warning'
                            ? 'is-orange'
                          : card.accent === 'primary'
                            ? 'is-purple'
                            : card.accent === 'info'
                              ? 'is-blue'
                              : 'is-blue';
                        const Icon = card.accent === 'primary'
                          ? Plus
                          : card.accent === 'warning'
                            ? Clock
                            : Users;
                        return (
                          <article key={card.title} className={`admin-card dashboard-kpi-card dashboard-kpi-card--${toneClass}`}>
                            <div className="admin-card__label">{card.title}</div>
                            <div className="admin-row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                              <div className="admin-card__value">{card.value}</div>
                              <span className={`admin-chip ${toneClass}`}><Icon size={16} /></span>
                            </div>
                            <div className="admin-card__meta">
                              {card.meta ? <span>{card.meta}</span> : <span className="admin-muted">As of latest sync</span>}
                            </div>
                          </article>
                        );
                      })}
                    </div>
                  );
                })()}
              </section>

              <section className="reports-demographics reports-dashboard-stack" style={{ marginTop: '24px' }}>
                <header className="admin-card__heading reports-section-header">
                  <BarChart3 size={18} />
                  <div>
                    <h2>Membership Analytics</h2>
                    <p className="admin-muted">Filtered membership trends and distributions for {membershipAnalyticsRangeLabel}.</p>
                  </div>
                </header>

                <div className="admin-grid-one">
                  <article className="admin-card admin-stack-md reports-chart-card reports-chart-card--primary">
                    <header className="admin-card__heading">
                      <LineChart size={18} />
                      <div>
                        <h2>Membership Growth</h2>
                        <p className="admin-muted">Registrations over time for the selected filters.</p>
                      </div>
                    </header>
                    {membershipGrowthChartData ? (
                      <div className="dashboard-chart-card__canvas dashboard-chart-card__canvas--line reports-chart-card__canvas reports-chart-card__canvas--primary">
                        <Line data={membershipGrowthChartData} options={membershipGrowthLineOptions} />
                      </div>
                    ) : (
                      <div className="admin-empty-state is-minimal">
                        {membershipAnalyticsLoading ? "Loading membership growth..." : "No data available for selected filters."}
                      </div>
                    )}
                  </article>
                </div>

                <div className="admin-grid-two reports-section-mt-24">
                  <article className="admin-card admin-stack-md reports-chart-card">
                    <header className="admin-card__heading">
                      <PieChart size={18} />
                      <div>
                        <h2>Members by Status</h2>
                        <p className="admin-muted">Status breakdown for the current member scope.</p>
                      </div>
                    </header>
                    {membershipStatusChartData ? (
                      <div className="dashboard-chart-card__canvas dashboard-chart-card__canvas--pie reports-chart-card__canvas reports-chart-card__canvas--secondary">
                        <Doughnut data={membershipStatusChartData} options={pieOptions} />
                      </div>
                    ) : (
                      <div className="admin-empty-state is-minimal">
                        {membershipAnalyticsLoading ? "Loading status distribution..." : "No data available for selected filters."}
                      </div>
                    )}
                  </article>

                  <article className="admin-card admin-stack-md reports-chart-card">
                    <header className="admin-card__heading">
                      <PieChart size={18} />
                      <div>
                        <h2>Members by Affiliation</h2>
                        <p className="admin-muted">Affiliation distribution for the selected filters.</p>
                      </div>
                    </header>
                    {membershipAffiliationChartData ? (
                      <div className="dashboard-chart-card__canvas dashboard-chart-card__canvas--pie reports-chart-card__canvas reports-chart-card__canvas--secondary">
                        <Doughnut data={membershipAffiliationChartData} options={pieOptions} />
                      </div>
                    ) : (
                      <div className="admin-empty-state is-minimal">
                        {membershipAnalyticsLoading ? "Loading affiliation distribution..." : "No data available for selected filters."}
                      </div>
                    )}
                  </article>
                </div>

                <div className="admin-grid-one reports-section-mt-24">
                  <article className="admin-card admin-stack-md reports-chart-card reports-chart-card--ranking">
                    <header className="admin-card__heading">
                      <Building2 size={18} />
                      <div>
                        <h2>Top Companies</h2>
                        <p className="admin-muted">Top 10 companies by filtered member count.</p>
                      </div>
                    </header>
                    {membershipTopCompaniesChartData ? (
                      <div className="dashboard-chart-card__canvas dashboard-chart-card__canvas--bar reports-chart-card__canvas reports-chart-card__canvas--ranking">
                        <Bar data={membershipTopCompaniesChartData} options={horizontalBarOptions} />
                      </div>
                    ) : (
                      <div className="admin-empty-state is-minimal">
                        {membershipAnalyticsLoading ? "Loading top companies..." : "No data available for selected filters."}
                      </div>
                    )}
                  </article>
                </div>
              </section>

              <section className="reports-demographics" style={{ marginTop: '24px' }}>
                <div className="admin-grid-two">
                  <article className="admin-card admin-stack-md reports-chart-card">
                    <header className="admin-card__heading">
                      <Users size={18} />
                      <div>
                        <h2>By union position</h2>
                        <p className="admin-muted">Active members holding union roles.</p>
                      </div>
                    </header>
                    {unionChartData ? (
                      <div className="dashboard-chart-card__canvas dashboard-chart-card__canvas--pie reports-chart-card__canvas reports-chart-card__canvas--secondary">
                        <Doughnut data={unionChartData} options={pieOptions} />
                      </div>
                    ) : (
                      <div className="admin-empty-state is-minimal">{unionPositionsEmptyMessage}</div>
                    )}
                  </article>

                  <article className="admin-card admin-stack-md reports-chart-card">
                    <header className="admin-card__heading">
                      <BarChart3 size={18} />
                      <div>
                        <h2>By years employed</h2>
                        <p className="admin-muted">Experience distribution for approved members.</p>
                      </div>
                    </header>
                    {tenureChartData ? (
                      <div className="dashboard-chart-card__canvas dashboard-chart-card__canvas--bar reports-chart-card__canvas reports-chart-card__canvas--secondary">
                        <Bar data={tenureChartData} options={barOptions} />
                      </div>
                    ) : (
                      <div className="admin-empty-state is-minimal">{tenureEmptyMessage}</div>
                    )}
                  </article>
                </div>
              </section>
            </>
            ) : null}
        {activeTab === "operations" ? (
            <>
              <section className="reports-section-mb-24">
                <div className="admin-card-grid admin-card-grid--kpi">
                  {operationsPrimaryStats.map((item, index) => {
                    const tones = ['is-blue', 'is-green', 'is-purple', 'is-orange'];
                    const toneClass = tones[index % tones.length];
                    const icons = [Activity, TrendingUp, Users, LineChart];
                    const Icon = icons[index % icons.length];
                    return (
                      <article key={item.id} className={`admin-card dashboard-kpi-card dashboard-kpi-card--${toneClass}`}>
                        <div className="admin-card__label">{item.label}</div>
                        <div className="admin-row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                          <div className="admin-card__value">{formatStatValue(item.value, item.format)}</div>
                          <span className={`admin-chip ${toneClass}`}><Icon size={16} /></span>
                        </div>
                        <div className="admin-card__meta">
                          {item.meta ? <span>{item.meta}</span> : <span className="admin-muted">As of latest sync</span>}
                        </div>
                      </article>
                    );
                  })}
                </div>
              </section>
            </>
            ) : null}

        {activeTab === "lifecycle" ? (
          <div className="reports-lifecycle admin-stack-md">

            {lifecycleError ? (
              <section className="admin-surface">
                <div className="admin-empty-state">{lifecycleError}</div>
              </section>
            ) : null}

            {archiveResult ? (
              <article className="admin-card">
                <p className="admin-muted" style={{ padding: "12px 18px" }}>
                  Archived <strong>{(archiveResult.archivedCount ?? 0).toLocaleString()}</strong> record(s) from{" "}
                  <strong>{archiveResult.table}</strong> (records older than {archiveResult.olderThanDays} days).
                </p>
              </article>
            ) : null}

            {archiveError ? (
              <article className="admin-card">
                <div className="admin-empty-state">{archiveError}</div>
              </article>
            ) : null}

            {/* Account Aging */}
            <article className="admin-card admin-stack-md">
              <header className="admin-card__heading">
                <Users size={18} />
                <div>
                  <h2>Account Aging</h2>
                  <p className="admin-muted">Approved member activity breakdown by last login date.</p>
                </div>
              </header>
              {isLoadingLifecycle && !lifecycleData ? (
                <div className="admin-empty-state">Loading…</div>
              ) : lifecycleData?.accountAging ? (
                <div className="admin-card-grid">
                  {/* Active — matches dashboard definition */}
                  <article className="admin-card">
                    <div className="admin-card__label">Active Members</div>
                    <div className="admin-row" style={{ justifyContent: "space-between", alignItems: "center" }}>
                      <div className="admin-card__value">{(lifecycleData.accountAging.active ?? 0).toLocaleString()}</div>
                      <span className="admin-chip is-green"><TrendingUp size={16} /></span>
                    </div>
                    <div className="admin-card__meta">Logged in within configured inactivity threshold</div>
                  </article>

                  {/* Inactive — matches dashboard definition (all non-active approved) */}
                  <article className="admin-card">
                    <div className="admin-card__label">Inactive Members</div>
                    <div className="admin-row" style={{ justifyContent: "space-between", alignItems: "center" }}>
                      <div className="admin-card__value">{(lifecycleData.accountAging.inactive ?? 0).toLocaleString()}</div>
                      <span className="admin-chip is-orange"><Users size={16} /></span>
                    </div>
                    <div className="dashboard-kpi-substats" style={{ marginTop: 6 }}>
                      <span className="dashboard-kpi-substat">
                        <strong>{(lifecycleData.accountAging.dormant ?? 0).toLocaleString()}</strong> Dormant (12–24 mo)
                      </span>
                      <span className="dashboard-kpi-substat">
                        <strong>{(lifecycleData.accountAging.veryDormant ?? 0).toLocaleString()}</strong> Very Dormant (24+ mo)
                      </span>
                    </div>
                  </article>

                  {/* Disabled */}
                  <article className="admin-card">
                    <div className="admin-card__label">Disabled</div>
                    <div className="admin-row" style={{ justifyContent: "space-between", alignItems: "center" }}>
                      <div className="admin-card__value">{(lifecycleData.accountAging.disabled ?? 0).toLocaleString()}</div>
                      <span className="admin-chip is-red"><LineChart size={16} /></span>
                    </div>
                    <div className="admin-card__meta">Rejected or suspended</div>
                  </article>
                </div>
              ) : (
                <div className="admin-empty-state is-minimal">No account data available.</div>
              )}
            </article>

            {/* Record Age Analytics */}
            <article className="admin-card admin-stack-md">
              <header className="admin-card__heading">
                <BarChart3 size={18} />
                <div>
                  <h2>Record Age Analytics</h2>
                  <p className="admin-muted">Age distribution per table. Archive eligible records to keep the system lean.</p>
                </div>
              </header>
              {isLoadingLifecycle && !lifecycleData ? (
                <div className="admin-empty-state">Loading…</div>
              ) : lifecycleData?.tableStats ? (
                <div className="admin-stack-md">
                  {Object.entries(lifecycleData.tableStats).map(([table, stats]) => (
                    <article key={table} className="admin-card admin-stack-sm">
                      <div className="admin-row" style={{ justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 8 }}>
                        <div>
                          <strong style={{ fontSize: 14 }}>{stats.label}</strong>
                          <p className="admin-muted" style={{ fontSize: 12, marginTop: 2 }}>
                            {stats.total.toLocaleString()} total records
                            {stats.hasArchiveSupport
                              ? ` · ${stats.archivedCount.toLocaleString()} archived`
                              : " · Archive not yet enabled (run archive action to enable)"}
                          </p>
                        </div>
                        <div className="admin-row" style={{ gap: 8, flexWrap: "wrap" }}>
                          {archiveConfirm?.table === table ? (
                            <>
                              <span className="admin-muted" style={{ fontSize: 12, alignSelf: "center" }}>
                                Archive records older than {archiveConfirm.days} days?
                              </span>
                              <button
                                type="button"
                                className="admin-button is-danger"
                                onClick={() => handleArchive(table, archiveConfirm.days)}
                                disabled={archivingTable === table}
                              >
                                {archivingTable === table ? "Archiving…" : "Confirm"}
                              </button>
                              <button
                                type="button"
                                className="admin-button"
                                onClick={() => setArchiveConfirm(null)}
                                disabled={archivingTable === table}
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                type="button"
                                className="admin-button"
                                onClick={() => { setArchiveResult(null); setArchiveError(null); setArchiveConfirm({ table, days: 365 }); }}
                                disabled={!!archivingTable}
                                title="Soft-archive records older than 1 year"
                              >
                                Archive 1 yr+
                              </button>
                              <button
                                type="button"
                                className="admin-button"
                                onClick={() => { setArchiveResult(null); setArchiveError(null); setArchiveConfirm({ table, days: 1095 }); }}
                                disabled={!!archivingTable}
                                title="Soft-archive records older than 3 years"
                              >
                                Archive 3 yr+
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginTop: 4 }}>
                        {[
                          { label: "< 6 months", value: stats.last6mo, color: ARCHIVE_AGING_COLORS[0] },
                          { label: "6 mo – 1 yr", value: stats.btw6moAnd1yr, color: ARCHIVE_AGING_COLORS[1] },
                          { label: "1 – 3 yrs", value: stats.btw1And3yr, color: ARCHIVE_AGING_COLORS[2] },
                          { label: "3+ years", value: stats.olderThan3yr, color: ARCHIVE_AGING_COLORS[3] },
                        ].map((bucket) => {
                          const pct = stats.total > 0 ? Math.round((bucket.value / stats.total) * 100) : 0;
                          return (
                            <div
                              key={bucket.label}
                              style={{
                                textAlign: "center",
                                padding: "10px 8px",
                                background: "var(--admin-surface-bg, var(--admin-card-bg))",
                                borderRadius: "var(--admin-radius-md, 8px)",
                                border: "1px solid var(--admin-card-border)",
                              }}
                            >
                              <div style={{ fontSize: 18, fontWeight: 700, color: bucket.color }}>{bucket.value.toLocaleString()}</div>
                              <div style={{ fontSize: 11, color: "var(--admin-text-muted)", marginTop: 2 }}>{bucket.label}</div>
                              <div style={{ fontSize: 11, color: "var(--admin-text-muted)" }}>{pct}%</div>
                            </div>
                          );
                        })}
                      </div>
                    </article>
                  ))}
                </div>
              ) : (
                <div className="admin-empty-state is-minimal">No record data available.</div>
              )}
            </article>

            {/* Automation Guide */}
            
          </div>
        ) : null}

        {activeTab === "custom" ? (
            <>
              <div className="admin-grid-two">
                <article className="admin-card admin-stack-md">
                  <header className="admin-card__heading">
                    <BarChart3 size={18} />
                    <div>
                      <h2>Report builder</h2>
                      <p className="admin-muted">Choose data sources, filters, and formats.</p>
                    </div>
                  </header>
                  <div className="admin-form-grid">
                    <label className="admin-field">
                      <span>Report type</span>
                      <select
                        name="reportType"
                        value={reportConfig.reportType}
                        onChange={handleReportBuilderSelectChange}
                        disabled={!reportBuilderOptions.reportTypes.length}
                      >
                        {reportBuilderOptions.reportTypes.length
                          ? reportBuilderOptions.reportTypes.map((option) => (
                              <option
                                key={option.value}
                                value={option.value}
                                title={option.description ?? undefined}
                              >
                                {option.label}
                              </option>
                            ))
                          : (
                            <option value="">No report types available</option>
                          )}
                      </select>
                    </label>
                    <label className="admin-field">
                      <span>Date range</span>
                      <select
                        name="dateRange"
                        value={reportConfig.dateRange}
                        onChange={handleReportBuilderSelectChange}
                        disabled={!reportBuilderOptions.dateRanges.length}
                      >
                        {reportBuilderOptions.dateRanges.length
                          ? reportBuilderOptions.dateRanges.map((option) => (
                              <option
                                key={option.value}
                                value={option.value}
                                title={option.description ?? undefined}
                              >
                                {option.label}
                              </option>
                            ))
                          : (
                            <option value="">No ranges available</option>
                          )}
                      </select>
                    </label>
                    <label className="admin-field">
                      <span>Output format</span>
                      <select
                        name="format"
                        value={reportConfig.format}
                        onChange={handleReportBuilderSelectChange}
                        disabled={!reportBuilderOptions.formats.length}
                      >
                        {reportBuilderOptions.formats.length
                          ? reportBuilderOptions.formats.map((option) => (
                              <option
                                key={option.value}
                                value={option.value}
                                title={option.description ?? undefined}
                              >
                                {option.label}
                              </option>
                            ))
                          : (
                            <option value="">No formats available</option>
                          )}
                      </select>
                    </label>
                  </div>
                  {reportConfig.reportType ? (
                  <section className="reports-form-card">
                    <header className="reports-form-card__header reports-form-card__header--split">
                      <div>
                        <h3>Filters</h3>
                        <p>Apply conditions to narrow down results.</p>
                      </div>
                      {hasActiveBuilderFilters ? (
                        <div className="reports-form-card__actions">
                          <button
                            type="button"
                            className="reports-builder-select-all admin-button admin-button--ghost"
                            onClick={handleClearBuilderFilters}
                          >
                            Clear Filters
                          </button>
                        </div>
                      ) : null}
                    </header>
                    <div className="admin-form-grid">
                      {builderFilterDefinitions.map((filterDef) => {
                        const normalizedField = normalizeFilterFieldKey(filterDef?.value);
                        if (!normalizedField) {
                          return null;
                        }

                        const optionList = Array.isArray(filterOptions[normalizedField])
                          ? filterOptions[normalizedField]
                          : [];
                        const filterValue = reportConfig.filters?.[normalizedField] ?? '';
                        return (
                          <label className="admin-field" key={`builder-filter-${normalizedField}`}>
                            <span>{toFilterFieldLabel(normalizedField)}</span>
                            <select
                              name={normalizedField}
                              value={filterValue}
                              onChange={handleCustomReportFilterChange}
                            >
                              <option value="">All</option>
                              {optionList.map((optionValue) => (
                                <option key={`${normalizedField}-${optionValue}`} value={optionValue}>
                                  {String(optionValue).replace(/[_-]+/g, ' ')}
                                </option>
                              ))}
                            </select>
                          </label>
                        );
                      })}
                    </div>
                  </section>
                  ) : null}

                  {reportConfig.reportType ? (
                  <section className="reports-form-card">
                    <header className="reports-form-card__header">
                      <h3>Date Range</h3>
                      <p>Select time period for report.</p>
                    </header>
                    {isCustomDateRangeSelected ? (
                      <div className="admin-form-grid">
                        <label className="admin-field">
                          <span>Date from</span>
                          <input
                            type="date"
                            name="dateFrom"
                            value={reportConfig.dateFrom ?? ''}
                            onChange={handleCustomReportFilterChange}
                          />
                        </label>
                        <label className="admin-field">
                          <span>Date to</span>
                          <input
                            type="date"
                            name="dateTo"
                            value={reportConfig.dateTo ?? ''}
                            onChange={handleCustomReportFilterChange}
                          />
                        </label>
                      </div>
                    ) : (
                      <p className="admin-muted reports-builder-date-mode-note">Choose Custom Range to set Date From and Date To.</p>
                    )}
                    <p className="admin-muted reports-builder-date-mode-note">Custom range overrides selected date range.</p>
                  </section>
                  ) : null}

                  {reportConfig.dateRange && availableColumns.length > 0 ? (
                    <section className="reports-form-card">
                      <div className="reports-builder-section__header">
                        <div>
                          <h3 className="reports-form-card__title">Select Data to Include</h3>
                          <p className="reports-form-card__subtitle">Choose the exact columns for export output.</p>
                        </div>
                        <div className="reports-form-card__actions">
                          {canSelectAllBuilderColumns ? (
                            <button
                              type="button"
                              className="reports-builder-select-all admin-button admin-button--ghost"
                              onClick={() => setReportConfig((previous) => ({
                                ...previous,
                                selectedColumns: availableColumns.map((col) => col.value),
                              }))}
                            >
                              Select All
                            </button>
                          ) : null}
                          {hasSelectedBuilderColumns ? (
                            <button
                              type="button"
                              className="reports-builder-select-all admin-button admin-button--ghost"
                              onClick={() => setReportConfig((previous) => ({ ...previous, selectedColumns: [] }))}
                            >
                              Clear All
                            </button>
                          ) : null}
                        </div>
                      </div>
                      <div className="reports-column-selector">
                        {availableColumns.map((col) => {
                          const isChecked = (reportConfig.selectedColumns ?? []).includes(col.value);
                          return (
                            <label
                              key={col.value}
                              className={`reports-column-item${isChecked ? ' is-checked' : ''}`}
                            >
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => {
                                  setReportConfig((previous) => {
                                    const current = Array.isArray(previous.selectedColumns) ? previous.selectedColumns : [];
                                    return {
                                      ...previous,
                                      selectedColumns: isChecked
                                        ? current.filter((v) => v !== col.value)
                                        : [...current, col.value],
                                    };
                                  });
                                }}
                              />
                              <span className="reports-column-item__label">{col.label}</span>
                            </label>
                          );
                        })}
                      </div>
                    </section>
                  ) : null}

                  {Array.isArray(reportConfig.selectedColumns) && reportConfig.selectedColumns.length > 0 ? (
                  <section className="reports-form-card">
                    <button
                      type="button"
                      className="reports-advanced-toggle"
                      onClick={() => setIsBuilderAdvancedOpen((prev) => !prev)}
                    >
                      Advanced Options {isBuilderAdvancedOpen ? '▲' : '▼'}
                    </button>
                    {isBuilderAdvancedOpen ? (
                      <div className="reports-builder-controls">
                        <div className="reports-builder-controls__field">
                          <label>Grouping</label>
                          <select
                            value={reportConfig.grouping}
                            onChange={(e) => setReportConfig((previous) => ({ ...previous, grouping: e.target.value }))}
                          >
                            {GROUPING_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                        <div className="reports-builder-controls__field">
                          <label>Aggregation</label>
                          <select
                            value={reportConfig.aggregation}
                            onChange={(e) => setReportConfig((previous) => ({ ...previous, aggregation: e.target.value }))}
                          >
                            {AGGREGATION_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    ) : null}
                  </section>
                  ) : null}

                  <div className="reports-builder-action-row">
                    <button
                      type="button"
                      className="admin-button admin-button--ghost"
                      onClick={openCreateDialogFromBuilder}
                      disabled={!canGenerateReport || isSavingSchedule}
                    >
                      Save This Report
                    </button>
                  <button
                    type="button"
                    className="admin-button is-primary admin-report-action"
                    onClick={handleReportBuilderGenerate}
                    disabled={!canGenerateReport || isGeneratingCustomReport}
                    title={reportBuilderButtonHint}
                  >
                    <BarChart3 size={16} />
                    <span>
                      {isGeneratingCustomReport
                        ? 'Preparing report...'
                        : `Generate ${reportBuilderSelectionLabels.reportType ?? 'report'}`}
                    </span>
                  </button>
                  </div>
                  {reportBuilderError ? (
                    <p className="admin-text-error reports-builder-feedback">{reportBuilderError}</p>
                  ) : reportBuilderNotice ? (
                    <p className="admin-muted reports-builder-feedback">{reportBuilderNotice}</p>
                  ) : null}
                </article>

                <article className="admin-card admin-stack-md">
                  <header className="admin-card__heading">
                    <Calendar size={18} />
                    <div>
                      <h2>Scheduled reports</h2>
                      <p className="admin-muted">Create automated report deliveries triggered at the scheduled time.</p>
                    </div>
                    <button
                      type="button"
                      className="admin-button admin-button--ghost reports-heading-action-end"
                      onClick={() => openCreateDialog()}
                    >
                      <Plus size={14} />
                      Add Schedule
                    </button>
                  </header>
                  <div className="admin-stack-sm">
                    {scheduleActionError ? (
                      <p className="admin-text-error">{scheduleActionError}</p>
                    ) : null}
                    {(() => {
                      if (isLoadingSchedules && !reportSchedules.length) {
                        return <p className="admin-muted">Loading scheduled reports...</p>;
                      }

                      if (!reportSchedules.length) {
                        return (
                          <ul className="reports-schedule-list">
                            <li
                              className="reports-schedule-card schedule-slot-card is-empty"
                              onClick={() => openCreateDialog()}
                              onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                  event.preventDefault();
                                  openCreateDialog();
                                }
                              }}
                              role="button"
                              tabIndex={0}
                            >
                              <div className="schedule-slot-card__icon-wrap">
                                <Plus size={32} className="text-muted" />
                              </div>
                              <div className="schedule-slot-card__text-wrap">
                                <strong>Add Scheduled Report</strong>
                                <span>Click to create a new automated report</span>
                              </div>
                            </li>
                          </ul>
                        );
                      }

                      return (
                        <ul className="reports-schedule-list">
                          {reportSchedules.map((schedule) => {
                            const reportTypeLabel = scheduleBuilderLabelMaps.reportType[schedule.reportType] ?? schedule.reportType;
                            const dateRangeLabel = scheduleBuilderLabelMaps.dateRange[schedule.dateRange] ?? schedule.dateRange;
                            const groupingLabel = scheduleBuilderLabelMaps.grouping[schedule.grouping] ?? schedule.grouping ?? 'none';
                            const aggregationLabel = scheduleBuilderLabelMaps.aggregation[schedule.aggregation] ?? schedule.aggregation ?? 'none';
                            const metaItems = [
                              reportTypeLabel,
                              dateRangeLabel,
                              `Grouping: ${groupingLabel}`,
                              `Aggregation: ${aggregationLabel}`,
                              `${Array.isArray(schedule.selectedColumns) ? schedule.selectedColumns.length : 0} columns`,
                              schedule.cadenceLabel,
                              formatScheduleDate(schedule.nextRun, schedule.timezone),
                            ];
                            if (schedule.format) {
                              metaItems.push(formatExportFormatLabel(schedule.format));
                            }
                            const isPending = Boolean(schedulePendingMap[schedule.id]);
                            const buttonLabel = isPending
                              ? 'Updating...'
                              : schedule.active
                                ? 'Pause'
                                : 'Resume';
                            return (
                              <li
                                key={schedule.id}
                                className={`reports-schedule-card${schedule.active ? '' : ' is-paused'}`}
                              >
                                <div className="reports-schedule-card__info">
                                  <div className="reports-schedule-card__header">
                                    <strong>{schedule.name}</strong>
                                    {!schedule.active ? (
                                      <span className="reports-schedule-card__status">Paused</span>
                                    ) : null}
                                  </div>
                                  <div className="reports-schedule-card__meta">
                                    {metaItems.filter(Boolean).map((item, index) => (
                                      <span key={`${schedule.id}-meta-${index}`}>{item}</span>
                                    ))}
                                  </div>
                                </div>
                                <div className="reports-schedule-card__actions">
                                  <button
                                    type="button"
                                    className="admin-button admin-button--ghost"
                                    onClick={() => handleScheduleToggle(schedule)}
                                    disabled={isPending}
                                  >
                                    {buttonLabel}
                                  </button>
                                  <button
                                    type="button"
                                    className="admin-button admin-button--ghost"
                                    onClick={() => openScheduleDialog(schedule)}
                                    disabled={isPending}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    type="button"
                                    className="admin-button admin-button--ghost"
                                    onClick={() => handleScheduleDelete(schedule)}
                                    disabled={isPending}
                                    aria-label={`Delete ${schedule.name}`}
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      );
                    })()}
                    {scheduleTotalPages > 1 ? (
                      <div className="reports-history-pagination">
                        <button
                          type="button"
                          className="admin-button admin-button--ghost"
                          disabled={schedulePage <= 1}
                          onClick={() => setSchedulePage((value) => Math.max(1, value - 1))}
                        >
                          Prev
                        </button>
                        <span className="reports-pagination-label">Page {schedulePage} of {scheduleTotalPages}</span>
                        <button
                          type="button"
                          className="admin-button admin-button--ghost"
                          disabled={schedulePage >= scheduleTotalPages}
                          onClick={() => setSchedulePage((value) => Math.min(scheduleTotalPages, value + 1))}
                        >
                          Next
                        </button>
                        <span className="reports-pagination-label">{scheduleTotal} total schedules</span>
                      </div>
                    ) : null}
                  </div>
                </article>
              </div>

              <article className="admin-card admin-stack-md">
                <header className="admin-card__heading">
                  <Download size={18} />
                  <div>
                    <h2>Export history</h2>
                    <p className="admin-muted">Track completed downloads and formats.</p>
                  </div>
                  {exportHistoryRows.length > 0 ? (
                    <button
                      type="button"
                      className="admin-button admin-button--ghost reports-heading-action-end"
                      onClick={handleDeleteAllHistory}
                      disabled={isDeletingAllHistory}
                      aria-label="Delete all export history"
                      title="Delete all export history"
                    >
                      {isDeletingAllHistory ? 'Clearing...' : <><Trash2 size={14} /> Clear all</>}
                    </button>
                  ) : null}
                </header>
                {historyActionError ? (
                  <p className="admin-text-error reports-inline-error">{historyActionError}</p>
                ) : null}
                {exportHistoryRows.length > 0 ? (
                  <p className="reports-record-indicator">
                    Showing {historyStart + 1}–{historyEnd} of {exportHistoryRows.length} records
                  </p>
                ) : null}
                <div className="admin-table-wrapper">
                  <table className="admin-table admin-table--condensed">
                    <thead>
                      <tr>
                        <th>Report</th>
                        <th>Date</th>
                        <th>Size</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {isLoadingExportHistory ? (
                        <tr>
                          <td colSpan={5} className="admin-muted">Loading export history...</td>
                        </tr>
                      ) : exportHistoryError ? (
                        <tr>
                          <td colSpan={5} className="admin-text-error">{exportHistoryError}</td>
                        </tr>
                      ) : exportHistoryRows.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="admin-muted">No exports recorded yet.</td>
                        </tr>
                      ) : (
                        paginatedHistoryRows.map((row) => (
                          <tr key={row.id}>
                            <td>
                              <div className="admin-table__primary">{row.title}</div>
                              {row.metaText ? (
                                <div className="admin-muted admin-text-sm">{row.metaText}</div>
                              ) : null}
                            </td>
                            <td>{row.dateDisplay}</td>
                            <td>{row.sizeDisplay}</td>
                            <td>
                              <span className="admin-chip is-green">
                                {row.statusLabel}
                              </span>
                            </td>
                            <td>
                              <div className="admin-row-actions">
                                <button
                                  type="button"
                                  className="admin-icon-button"
                                  onClick={(event) => toggleHistoryMenu(row.id, event)}
                                  aria-label="Actions"
                                  aria-haspopup="menu"
                                  aria-expanded={historyMenuOpenId === row.id}
                                >
                                  <MoreHorizontal size={16} />
                                </button>
                                {historyMenuOpenId === row.id ? createPortal(
                                  <div className="admin-menu admin-menu--fixed" style={{ top: `${historyMenuCoords.top}px`, left: `${historyMenuCoords.left}px` }}>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setHistoryMenuOpenId(null);
                                        handleHistoryDownload(row);
                                      }}
                                      disabled={!row.isAvailable}
                                    >
                                      <Download size={14} />
                                      Download
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setHistoryMenuOpenId(null);
                                        handleHistoryDelete(row);
                                      }}
                                      disabled={historyDeleteId === row.id}
                                    >
                                      <Trash2 size={14} />
                                      {historyDeleteId === row.id ? 'Deleting...' : 'Delete'}
                                    </button>
                                  </div>,
                                  document.body
                                ) : null}
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                {historyTotalPages > 1 ? (
                  <div className="reports-history-pagination">
                    <button
                      type="button"
                      className="admin-button admin-button--ghost"
                      onClick={() => setHistoryPage((p) => Math.max(1, p - 1))}
                      disabled={historyPage <= 1}
                    >
                      Previous
                    </button>
                    {historyPageNumbers.map((page, idx) =>
                      page === '...' ? (
                        <span key={`ellipsis-${idx}`} className="reports-page-ellipsis">…</span>
                      ) : (
                        <button
                          key={page}
                          type="button"
                          className={`reports-page-btn${page === historyPage ? ' is-active' : ''}`}
                          onClick={() => setHistoryPage(page)}
                        >
                          {page}
                        </button>
                      )
                    )}
                    <button
                      type="button"
                      className="admin-button admin-button--ghost"
                      onClick={() => setHistoryPage((p) => Math.min(historyTotalPages, p + 1))}
                      disabled={historyPage >= historyTotalPages}
                    >
                      Next
                    </button>
                    <span className="reports-pagination-label">Page {historyPage} of {historyTotalPages}</span>
                  </div>
                ) : null}
              </article>
            </>
            ) : null}
        </div>
      {isMembershipExportPreviewOpen ? (
        <div className="admin-dialog" role="dialog" aria-modal="true" aria-label="Export preview">
          <div className="admin-dialog__backdrop" onClick={handleCloseMembershipExportPreview} />
          <div className="admin-dialog__panel">
            <header className="admin-dialog__header">
              <div>
                <h3>Print Preview</h3>
                <p className="admin-muted">Paper-ready preview before exporting.</p>
              </div>
              <button
                type="button"
                className="admin-icon-button"
                onClick={handleCloseMembershipExportPreview}
                aria-label="Close export preview"
                disabled={isExportingMembership || isPreparingMembershipPaperPreview}
              >
                <X size={16} />
              </button>
            </header>
            <div className="admin-dialog__body admin-stack-md">
              <div style={{ border: '1px solid var(--admin-card-border)', borderRadius: '12px', overflow: 'hidden', minHeight: '420px', background: '#f8fafc' }}>
                {isPreparingMembershipPaperPreview ? (
                  <div className="admin-empty-state is-minimal reports-preview-empty">
                    Preparing preview...
                  </div>
                ) : membershipPreviewMode === 'pdf' && membershipPaperPreviewUrl ? (
                  <iframe
                    ref={membershipPreviewFrameRef}
                    title="Membership print preview"
                    src={membershipPaperPreviewUrl}
                    style={{ width: '100%', height: '420px', border: 0, background: '#ffffff' }}
                  />
                ) : membershipPreviewMode !== 'pdf' && membershipTablePreview.header.length ? (
                  <div style={{ height: '420px', overflow: 'auto', background: '#ffffff' }}>
                    <table className="admin-table admin-table--condensed">
                      <thead>
                        <tr>
                          {membershipTablePreview.header.map((cell, index) => (
                            <th key={`preview-header-${index}`}>{cell}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {membershipTablePreview.rows.map((row, rowIndex) => (
                          <tr key={`preview-row-${rowIndex}`}>
                            {row.map((cell, cellIndex) => (
                              <td key={`preview-cell-${rowIndex}-${cellIndex}`}>{cell}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="admin-empty-state is-minimal reports-preview-empty">
                    Preview is unavailable.
                  </div>
                )}
              </div>
              {membershipPaperPreviewError ? (
                <p className="admin-text-error">{membershipPaperPreviewError}</p>
              ) : null}
              <p className="admin-muted">
                Preview mode: {membershipPreviewMode === 'excel' ? 'Excel-style table' : membershipPreviewMode === 'csv' ? 'CSV table' : 'PDF paper layout'}.
              </p>
            </div>
            <footer className="admin-dialog__footer">
              <button
                type="button"
                className="admin-button admin-button--ghost"
                onClick={handleCloseMembershipExportPreview}
                disabled={isExportingMembership || isPreparingMembershipPaperPreview}
              >
                Cancel
              </button>
              <button
                type="button"
                className="admin-button"
                onClick={handlePrintMembershipPreview}
                disabled={(membershipPreviewMode === 'pdf' && !membershipPaperPreviewUrl) || isPreparingMembershipPaperPreview}
              >
                Print
              </button>
              <button
                type="button"
                className="admin-button is-primary"
                onClick={handleConfirmMembershipExport}
                disabled={isExportingMembership || isPreparingMembershipPaperPreview}
              >
                {isExportingMembership ? 'Preparing...' : `Export ${formatExportFormatLabel(membershipExportFormat)}`}
              </button>
            </footer>
          </div>
        </div>
      ) : null}
      {(editingScheduleId && editingSchedule) || isCreatingSchedule ? (
        <div className="admin-dialog" role="dialog" aria-modal="true">
          <div className="admin-dialog__backdrop" onClick={closeScheduleDialog} />
          <div className="admin-dialog__panel">
            <header className="admin-dialog__header">
              <div>
                <h3>{isCreatingSchedule ? 'Create scheduled report' : 'Edit scheduled report'}</h3>
                <p className="admin-muted">
                  {isCreatingSchedule
                    ? 'Fill in the details to create a new scheduled report.'
                    : editingSchedule?.name ?? ''}
                </p>
              </div>
              <button
                type="button"
                className="admin-icon-button"
                onClick={closeScheduleDialog}
                aria-label="Close scheduled report editor"
              >
                <X size={16} />
              </button>
            </header>
            <div className="admin-dialog__body admin-stack-md">
              <section className="reports-form-card">
                <header className="reports-form-card__header">
                  <h3>Basic Info</h3>
                  <p>Name and output setup for this saved report.</p>
                </header>
                <div className="admin-form-grid">
                  <label className="admin-field">
                    <span>Report name</span>
                    <input
                      type="text"
                      name="name"
                      value={scheduleForm.name}
                      onChange={handleScheduleFormChange}
                      placeholder="Example: Weekly Ticket Health Summary"
                      required
                    />
                  </label>
                  <label className="admin-field">
                    <span>Report type</span>
                    <select
                      name="reportType"
                      value={scheduleReportConfig.reportType}
                      onChange={handleScheduleFormChange}
                    >
                      {(reportBuilderOptions.reportTypes.length
                        ? reportBuilderOptions.reportTypes
                        : DEFAULT_REPORT_BUILDER_OPTIONS.reportTypes).map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                      ))}
                    </select>
                  </label>
                  <label className="admin-field">
                    <span>Format</span>
                    <select
                      name="format"
                      value={scheduleReportConfig.format}
                      onChange={handleScheduleFormChange}
                    >
                      <option value="pdf">PDF report</option>
                      <option value="excel">Excel spreadsheet</option>
                      <option value="csv">CSV export</option>
                    </select>
                  </label>
                </div>
              </section>

              <section className="reports-form-card">
                <header className="reports-form-card__header">
                  <h3>Schedule Settings</h3>
                  <p>Control when this report runs automatically.</p>
                </header>
                <div className="admin-form-grid">
                  <label className="admin-field">
                    <span>Cadence</span>
                    <select
                      name="cadence"
                      value={scheduleForm.cadence}
                      onChange={handleScheduleFormChange}
                    >
                      {SCHEDULE_CADENCE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="admin-field">
                    <span>Next run date</span>
                    <input
                      type="date"
                      name="nextRun"
                      value={scheduleForm.nextRun}
                      onChange={handleScheduleFormChange}
                      required
                    />
                  </label>
                  <label className="admin-field">
                    <span>Activation time</span>
                    <input
                      type="time"
                      name="activationTime"
                      value={scheduleForm.activationTime}
                      onChange={handleScheduleFormChange}
                      required
                    />
                  </label>
                  <label className="admin-field">
                    <span>Timezone</span>
                    <input type="text" value="Asia/Manila (PHT)" disabled />
                  </label>
                </div>
              </section>

              <section className="reports-form-card">
                <header className="reports-form-card__header">
                  <h3>Report Configuration</h3>
                  <p>Match this setup with your builder selection.</p>
                </header>

                <div className="admin-form-grid">
                  <label className="admin-field">
                    <span>Date range</span>
                    <select
                      name="dateRange"
                      value={scheduleReportConfig.dateRange}
                      onChange={handleScheduleFormChange}
                    >
                      {(reportBuilderOptions.dateRanges.length
                        ? reportBuilderOptions.dateRanges
                        : DEFAULT_REPORT_BUILDER_OPTIONS.dateRanges).map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                      ))}
                    </select>
                  </label>
                </div>

                {scheduleIsCustomDateRange ? (
                  <div className="admin-form-grid">
                    <label className="admin-field">
                      <span>Date from</span>
                      <input
                        type="date"
                        name="dateFrom"
                        value={scheduleReportConfig.dateFrom}
                        onChange={handleScheduleFormChange}
                      />
                    </label>
                    <label className="admin-field">
                      <span>Date to</span>
                      <input
                        type="date"
                        name="dateTo"
                        value={scheduleReportConfig.dateTo}
                        onChange={handleScheduleFormChange}
                      />
                    </label>
                  </div>
                ) : null}
                <p className="admin-muted admin-text-sm">Custom range overrides selected date range.</p>

                <div className="reports-builder-section__header reports-builder-section__header--filters">
                  <div>
                    <h4 className="reports-builder-section__title">Filters</h4>
                    <p className="reports-form-card__subtitle">Apply conditions to narrow down results.</p>
                  </div>
                  {hasActiveScheduleFilters ? (
                    <div className="reports-form-card__actions">
                      <button
                        type="button"
                        className="reports-builder-select-all admin-button admin-button--ghost"
                        onClick={handleClearScheduleFilters}
                      >
                        Clear Filters
                      </button>
                    </div>
                  ) : null}
                </div>

                <div className="admin-form-grid">
                  {scheduleFilterDefinitions.map((filterDef) => {
                    const normalizedField = normalizeFilterFieldKey(filterDef?.value);
                    if (!normalizedField) {
                      return null;
                    }

                    const options = Array.isArray(scheduleFilterOptions[normalizedField])
                      ? scheduleFilterOptions[normalizedField]
                      : [];

                    return (
                      <label className="admin-field" key={`schedule-filter-${normalizedField}`}>
                        <span>{toFilterFieldLabel(normalizedField).replace(/^By\s+/i, '')}</span>
                        <select
                          value={scheduleReportConfig.filters?.[normalizedField] ?? ''}
                          onChange={(event) => handleScheduleFilterChange(normalizedField, event.target.value)}
                        >
                          <option value="">All</option>
                          {options.map((value) => (
                            <option key={`schedule-${normalizedField}-${value}`} value={value}>{value.replace(/[_-]+/g, ' ')}</option>
                          ))}
                        </select>
                      </label>
                    );
                  })}
                </div>

                <div className="admin-stack-sm">
                  <div className="reports-builder-section__header">
                    <div>
                      <h4 className="reports-builder-section__title">Select Data to Include</h4>
                      <p className="reports-form-card__subtitle">Set the exact columns sent in exports.</p>
                    </div>
                    <div className="reports-form-card__actions">
                      {canSelectAllScheduleColumns ? (
                        <button
                          type="button"
                          className="reports-builder-select-all admin-button admin-button--ghost"
                          onClick={() => setScheduleForm((previous) => {
                            const allColumns = scheduleAvailableColumns.map((column) => column.value);
                            return {
                              ...previous,
                              selectedColumns: allColumns,
                              reportConfig: {
                                ...(previous.reportConfig ?? {}),
                                selectedColumns: allColumns,
                              },
                            };
                          })}
                        >
                          Select All
                        </button>
                      ) : null}
                      {hasSelectedScheduleColumns ? (
                        <button
                          type="button"
                          className="reports-builder-select-all admin-button admin-button--ghost"
                          onClick={() => setScheduleForm((previous) => ({
                            ...previous,
                            selectedColumns: [],
                            reportConfig: {
                              ...(previous.reportConfig ?? {}),
                              selectedColumns: [],
                            },
                          }))}
                        >
                          Clear All
                        </button>
                      ) : null}
                    </div>
                  </div>
                  <div className="reports-column-selector">
                    {scheduleAvailableColumns.map((column) => (
                      <label
                        key={`schedule-column-${column.value}`}
                        className={`reports-column-item${scheduleReportConfig.selectedColumns?.includes(column.value) ? ' is-checked' : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={Boolean(scheduleReportConfig.selectedColumns?.includes(column.value))}
                          onChange={() => handleScheduleColumnToggle(column.value)}
                        />
                        <span className="reports-column-item__label">{column.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="reports-form-card__advanced">
                  <button
                    type="button"
                    className="reports-advanced-toggle"
                    onClick={() => setIsScheduleAdvancedOpen((prev) => !prev)}
                  >
                    Advanced Options {isScheduleAdvancedOpen ? '▲' : '▼'}
                  </button>
                  {isScheduleAdvancedOpen ? (
                    <div className="reports-builder-controls">
                      <div className="reports-builder-controls__field">
                        <label>Grouping</label>
                        <select
                          name="grouping"
                          value={scheduleReportConfig.grouping}
                          onChange={handleScheduleFormChange}
                        >
                          {GROUPING_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="reports-builder-controls__field">
                        <label>Aggregation</label>
                        <select
                          name="aggregation"
                          value={scheduleReportConfig.aggregation}
                          onChange={handleScheduleFormChange}
                        >
                          {AGGREGATION_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  ) : null}
                </div>
              </section>

              <section className="reports-form-card">
                <header className="reports-form-card__header">
                  <h3>Delivery</h3>
                  <p>Recipients for scheduled report delivery.</p>
                </header>
                <label className="admin-field">
                  <span>Recipients</span>
                  <input
                    type="text"
                    name="recipientsInput"
                    value={scheduleForm.recipientsInput}
                    onChange={handleScheduleFormChange}
                    placeholder="Example: team@company.com, ops@company.com"
                  />
                </label>
                {scheduleRecipients.length ? (
                  <div className="reports-chip-list">
                    {scheduleRecipients.map((email) => {
                      const invalid = !EMAIL_PATTERN.test(email);
                      return (
                        <span key={`recipient-chip-${email}`} className={`reports-chip${invalid ? ' is-invalid' : ''}`}>
                          {email}
                          <button type="button" onClick={() => handleScheduleRecipientRemove(email)} aria-label={`Remove ${email}`}>
                            <X size={12} />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                ) : null}
                {scheduleInvalidRecipients.length ? (
                  <p className="admin-text-error">Invalid recipient email(s): {scheduleInvalidRecipients.join(', ')}</p>
                ) : null}
              </section>

              {scheduleFormError ? (
                <p className="admin-text-error">{scheduleFormError}</p>
              ) : null}
            </div>
            <footer className="admin-dialog__footer">
              <button
                type="button"
                className="admin-button admin-button--ghost"
                onClick={closeScheduleDialog}
                disabled={isSavingSchedule}
              >
                Cancel
              </button>
              <button
                type="button"
                className="admin-button is-primary"
                onClick={isCreatingSchedule ? handleScheduleCreate : handleScheduleSave}
                disabled={isSavingSchedule || scheduleInvalidRecipients.length > 0}
              >
                {isSavingSchedule ? 'Saving...' : isCreatingSchedule ? 'Create Schedule' : 'Save Changes'}
              </button>
            </footer>
          </div>
        </div>
      ) : null}
    </div>
  );
}
